import React, { useEffect, useRef, useState, useMemo, forwardRef, useCallback, useImperativeHandle } from 'react';
import { useGLTF, useTexture, useFBX, useAnimations } from '@react-three/drei';
import { MeshStandardMaterial, MeshPhysicalMaterial, Vector2, AnimationMixer, LoopRepeat } from 'three';
import createAnimation, { getMorphTargets, resolveMorphTargetIndex } from '../../converter';
import blinkData from '../../data/blendDataBlink.json';
import userTalkingData from '../../data/blendDataUserTalking.json';
import * as THREE from 'three';
import listeningData from '../../data/blendDataListening.json';
import thinkingData from '../../data/blendDataThinking.json';
import idleMicroData from '../../data/blendDataIdleMicro.json';
import breathingData from '../../data/blendDataBreathing.json';
import expressionsData from '../../data/blendDataExpressions.json';
import naturalBlinkData from '../../data/blendDataNaturalBlink.json';
import { setupListeningAnimation } from '../../animations/listeningAnimation';
import { setupThinkingAnimation } from '../../animations/thinkingAnimation';
import { setupVoiceNonSpeechAnimation } from '../../animations/voiceNonSpeechAnimation';
import { overlayBreathingOnBlendData } from '../../animations/speechBreathing';
import { sampleProceduralIdleNoise } from '../../animations/enhancedIdleAnimation';
import {
  applyMoodToAvatar,
  applyBlendshapeOverlay,
  stopMoodOverlay,
  tickMoodOverlay,
  getActiveMoodOverlayBlendshapes,
  isMoodSpeechDonorMesh,
  setMoodIntensityScale,
  setOmittedMoodBlendshapes,
  setMoodOverrides,
  setPersonalityMoodScales,
  setPersonalityRestingFace,
  fadeOutActiveSentimentMood,
} from '../../utils/moodAnimator';
import { shouldSkipLegacyConversationClips } from '../../utils/legacyConversationClips';
import {
  getSentimentRuntimeConfig,
  getServerSentimentGates,
  isServerSentimentConsumerActive,
} from '../../utils/sentimentAnalyzer';
import {
  resolvePersonalityKnobs,
  applyPersonalityToAnimationConfig,
  personalityKnobsEqual,
  type ResolvedPersonalityKnobs,
} from '../../animations/personalityPresets';
import { ensureMirroredSmileMorph } from '../../utils/bilateralSmile';
import {
  getGestureController,
  detectGestureFromText,
  isSkeletalGesture,
  GESTURE_BONE_KEYFRAMES,
  resolveBoneNameForRole,
} from '../../animations/gestureAnimation';
import type { GestureType, GestureFrame, BoneRole } from '../../animations/gestureAnimation';
import { setupThreeDView } from '../../animations/ThreeDView';
import { animationController } from '../../animations/UnifiedAnimationController';
import { useFrame } from '@react-three/fiber';
import { animateAvatar } from '../../utils/speechUtils';
import { AnimationStateManager } from '../../animations/AnimationStateManager';
import { AnimationType, AnimationPriority } from '../../utils/animationUtils';
import { SpeechExpressivenessController } from '../../utils/speechExpressiveness';
import { getSpeechText } from '../../utils/spokenText';
import { isAudioActuallyPlaying, monitorAudioElement, getAudioPlaybackState } from '../../utils/audioUtils';
import { SPEECH_OWNED_MORPH_NAMES as SPEECH_OWNED_MORPH_NAMES_IMPORT, zeroSpeechOwnedMorphs } from '../../utils/speechMorphReset';
import {
  applyTrackedOverlay,
  clampMouthFamilyInfluences,
  jawWeightFromBlendshapes,
  smileWeightFromBlendshapes,
  SAFE_NONSPEECH_JAW_MAX,
  SAFE_NONSPEECH_SMILE_MAX,
} from '../../utils/mouthMorphSafety';
import * as _ from 'lodash';
import { applyBoneFaceCustomization, cacheOriginalTransforms } from '../../utils/boneScaling';
import { getModelById, getModelByUrl, resolveModelUrl, resolveAvatarRuntimeAssetUrl, resolveSharedModelLibraryAssetUrl, getModelLibraryBaseUrl, MODEL_REGISTRY } from '../../modelRegistry';
import {
  mountWardrobePiece,
  unmountWardrobePiece,
  resetWardrobeToDefault,
  getEquippedPieces,
  type WardrobeManifestPiece,
  type WardrobeSlot,
} from '../../utils/wardrobeLoader';
import { patchHairMaterial } from '../../utils/hairMaterialPatch';
import { probeGestureIntegrity } from '../../utils/skinningIntegrity';

// Legacy encoding constants for backward compatibility
const sRGBEncoding = 3001;
const LinearEncoding = 3000;

// Slice 10 (May 12 2026) — Mouth/jaw morph names that the speech RAF owns
// during speaking. Used to copy the speech RAF's writes from a non-primary
// mesh (teeth/eyes/tongue) back onto the primary body mesh in useFrame
// AFTER mixer.update has run. Why: idle morph actions (idleMicroAction,
// breathingAction, naturalBlinkAction) are bound exclusively to bodyMeshName
// (e.g. SK_F4_Head_1 on F4 models) and on first prompts after page load
// they sample tracks that pull face morphs back to ~0, overriding the
// speech RAF's writes. The other morph submeshes (SK_F4_Head_3 = teeth,
// etc.) have NO mixer track bindings, so they retain the speech RAF's
// writes intact. We use them as the source of truth and copy mouth/jaw
// morphs back to body mesh per frame. Eye/brow/cheek morphs are NOT
// included so the natural blink and brow expressions keep working.
//
// Includes both ARKit camelCase (jawOpen) and underscore aliases (jaw_L)
// because different rigs name them differently and the speech RAF resolves
// via getMorphTargets() which expands across both.
// Moved to utils/speechMorphReset.ts (Aug 16 2026) so the speech-teardown
// reset is unit-testable; this file re-imports the same list.
const SPEECH_OWNED_MORPH_NAMES = SPEECH_OWNED_MORPH_NAMES_IMPORT;

/** Morphs that read as a hanging-open jaw when leftover viseme/teeth values stick. */
const PREVIEW_JAW_SLAM_MORPHS: ReadonlyArray<string> = [
  'jawOpen', 'mouthOpen', 'tongueOut', 'tongue_out',
];

const TEETH_ENAMEL_HEX = 0xf6f2eb;
// gums22: healthy gum pink for the two-tone teeth material (vertex-colour
// gum band). Tune here if gums read too bright/dull on F4/M4.
const GUM_PINK_HEX = 0xc9868a;
const MOUTH_FLESH_HEX = 0x5d363b;
/** Shift applied so signed texel-redness (typically 0..95) lands in 0..255 for Otsu. */
const GUM_REDNESS_OTSU_SHIFT = 50;
/** Clamp the adaptive redness cut so we never replay gums21 (red>18 → 100% gum). */
const GUM_REDNESS_T_MIN = 32;
const GUM_REDNESS_T_MAX = 72;

/** Tooth materials only — not tongue / gum / cavity / combined "TeethTongue". */
function isTeethMaterialName(name: string | undefined): boolean {
  const n = String(name || '').toLowerCase();
  if (!n.includes('teeth') && !n.includes('tooth')) return false;
  if (/(tongue|gum|mouth|oral|cavity)/.test(n)) return false;
  return true;
}

function meshHasTongueMorphs(node: THREE.Object3D): boolean {
  const dict = (node as THREE.Mesh).morphTargetDictionary;
  if (!dict) return false;
  return Object.keys(dict).some((key) => key.toLowerCase().includes('tongue'));
}

function meshHasVertexColors(mesh: THREE.Mesh): boolean {
  return Boolean(pickToothMaskAttribute(mesh.geometry));
}

function readMaskR01(attr: THREE.BufferAttribute, index: number): number {
  const arr = attr.array as ArrayLike<number>;
  const size = attr.itemSize || 3;
  let r = Number(arr[index * size]);
  if (arr instanceof Uint8Array) r /= 255;
  else if (arr instanceof Uint16Array) r /= 65535;
  return r;
}

function attributeIsVaryingMask(attr: THREE.BufferAttribute | undefined): boolean {
  if (!attr || !attr.count) return false;
  let lo = false;
  let hi = false;
  const step = Math.max(1, Math.floor(attr.count / 4096));
  for (let i = 0; i < attr.count; i += step) {
    const r = readMaskR01(attr, i);
    if (r <= 0.25) lo = true;
    if (r >= 0.75) hi = true;
    if (lo && hi) return true;
  }
  return false;
}

/**
 * Sam CC upper teeth (`HG_TeethUpper` / `M_Teeth`) store a constant-1
 * COLOR_0 and the real tooth/cavity mask on COLOR_1. Lower `Mesh.002`
 * stores the tooth/tongue mask on COLOR_0. Prefer the first colour set
 * that actually varies.
 */
function pickToothMaskAttribute(geometry: THREE.BufferGeometry | undefined): THREE.BufferAttribute | null {
  if (!geometry?.getAttribute) return null;
  const names = ['color', 'color_1', 'color_2', 'COLOR_0', 'COLOR_1', 'COLOR_2'];
  for (const name of names) {
    const attr = geometry.getAttribute(name) as THREE.BufferAttribute | undefined;
    if (attributeIsVaryingMask(attr)) return attr;
  }
  return null;
}

function isSamCombinedTeethTongueName(nodeName: string): boolean {
  const n = nodeName.toLowerCase();
  return n === 'mesh.002' || n === 'mesh002' || n.includes('teethlower') || n.includes('teeth_lower');
}

/** Sam CC teeth meshes whose COLOR_* is a real tooth/flesh mask (v13). */
function isSamVertexMaskedTeethMesh(nodeName: string): boolean {
  const n = nodeName.toLowerCase();
  return (
    isSamCombinedTeethTongueName(nodeName) ||
    n === 'hg_teethupper' ||
    n.includes('teethupper') ||
    n.includes('teeth_upper')
  );
}

/**
 * X3D F4/M4 heads ship `M_Fluid` / `M_Fluid.001` saliva cards with
 * baseColorFactor [0,0,0,0] and alphaMode MASK. They still write depth,
 * which reads as a dark band over the upper teeth on a wide smile.
 * Hide the primitive when it is its own mesh; neutralize the material
 * when it shares a multi-material head.
 */
function hideStrayMouthFluidOverlay(node: THREE.Object3D): void {
  const mesh = node as THREE.Mesh;
  if (!mesh.material) return;
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const isFluidMat = (mat: THREE.Material | undefined) =>
    /fluid|saliva|occlusion|mouthbag|wetness/i.test(String((mat as { name?: string } | undefined)?.name || ''));
  if (/fluid|saliva|occlusion|mouthbag|wetness/i.test(node.name)) {
    mesh.visible = false;
    console.log('🦷 enamel20 hid fluid mesh', node.name);
    return;
  }
  if (!mats.some(isFluidMat)) return;
  // X3D saliva cards: Nicola ships MASK+[0,0,0,0] (depth-only), Arc ships
  // BLEND with a dark map. Both read as spots on the upper teeth on a wide
  // smile. Hide the primitive when it is its own mesh; neutralize the
  // material when it shares a multi-material head.
  if (mats.every(isFluidMat) || mats.length === 1) {
    mesh.visible = false;
    console.log('🦷 enamel20 hid stray mouth overlay on', node.name);
    return;
  }
  for (const mat of mats) {
    if (!isFluidMat(mat)) continue;
    const std = mat as THREE.MeshStandardMaterial & { colorWrite?: boolean };
    std.opacity = 0;
    std.transparent = true;
    std.depthWrite = false;
    std.colorWrite = false;
    std.map = null;
    std.needsUpdate = true;
  }
}

/** Tooth materials only — see sanitizeTeethMaterialsOnNode. The F4/M4
 *  mouth-interior machinery that used to live here (cavity masks, vertex
 *  tucking, teeth rescaling, atlas repainting — mouthfix1-8, gums21/22,
 *  teethscale1-3, cavityshade1) was removed on Aug 15 2026: the authored
 *  UE5 atlas was correct all along and every layer of runtime correction
 *  made the mouth worse. Asset defects go to the Blender/UE5 pipeline. */

/** Mouth-region layer matchers for the `avatar:debug:mouthLayer` event.
 *  MATERIAL-name first (GLTFLoader splits multi-primitive heads into
 *  SK_F4_Head_1/_2/… so node names carry no layer semantics), node-name
 *  fallback for models like Sam whose teeth ARE named meshes. `teeth`
 *  includes `enamel` because sanitizeTeethMaterialsOnNode renames the
 *  material to CamlinFlatEnamelV20 / CamlinTeethMaskedEnamel at load. */
const MOUTH_LAYER_PATTERNS: Record<string, RegExp> = {
  skin: /skin_face/i,
  teeth: /teeth|tooth|enamel/i,
  fluid: /fluid|saliva|wetness|mouthbag/i,
  lashes: /lash/i,
  occlusion: /occlusion/i,
};

/**
 * Sam CC teeth meshes store a binary colour mask (1 = tooth, 0 = tongue /
 * gum / cavity). Multiplying enamel by that mask turns the interior black;
 * disabling vertex colours paints the tongue enamel. Mix the two colours
 * in the shader instead, using the varying mask — not a constant COLOR_0.
 */
function applyVertexMaskedEnamelMaterial(
  mesh: THREE.Mesh,
  teethNormalTexture: THREE.Texture | null,
): boolean {
  const geom = mesh.geometry;
  const mask = pickToothMaskAttribute(geom);
  if (!mask) return false;
  // Do not use Three's COLOR_0 as vColor — Sam's upper teeth COLOR_0 is
  // constant-1, which paints cavity/tongue enamel. Bake a 0/1 float attr.
  const tooth = new Float32Array(mask.count);
  let toothCount = 0;
  for (let i = 0; i < mask.count; i++) {
    const v = readMaskR01(mask, i) > 0.5 ? 1 : 0;
    tooth[i] = v;
    toothCount += v;
  }
  geom.setAttribute('toothMask', new THREE.BufferAttribute(tooth, 1));
  // Shared Mesh.002 is teeth+tongue. v11 looked unchanged / too bright.
  // 50% darker albedo; unlit only on diffuse so HDR spec stays.
  const mat = new MeshStandardMaterial({
    name: 'CamlinTeethMaskedEnamel',
    color: 0xffffff,
    roughness: 0.22,
    metalness: 0,
    vertexColors: false,
    side: THREE.DoubleSide,
    normalMap: teethNormalTexture,
    envMapIntensity: 0.45,
  });
  mat.customProgramCacheKey = () => 'camlin-tooth-mask-v13';
  mat.onBeforeCompile = (shader) => {
    // Three 0.182 calls onBeforeCompile BEFORE ShaderChunk includes expand.
    // Patch unexpanded includes + the inline outgoingLight line.
    shader.vertexShader =
      'attribute float toothMask;\nvarying float vToothMask;\n' +
      shader.vertexShader.replace(
        'void main() {',
        'void main() {\n  vToothMask = toothMask;',
      );
    shader.fragmentShader =
      'varying float vToothMask;\n' +
      shader.fragmentShader
        .replace(
          'vec4 diffuseColor = vec4( diffuse, opacity );',
          `vec4 diffuseColor = vec4( diffuse, opacity );
           float camlinTooth = step(0.5, vToothMask);
           vec3 enamel = vec3(0.965, 0.949, 0.922);
           vec3 flesh = vec3(0.30, 0.09, 0.07);
           diffuseColor.rgb = mix(flesh, enamel, camlinTooth);`,
        )
        .replace(
          '#include <roughnessmap_fragment>',
          `#include <roughnessmap_fragment>
           roughnessFactor = mix(0.55, roughnessFactor, camlinTooth);`,
        )
        .replace(
          '#include <normal_fragment_maps>',
          `#include <normal_fragment_maps>
           normal = normalize(mix(nonPerturbedNormal, normal, camlinTooth));`,
        )
        .replace(
          'vec3 outgoingLight = totalDiffuse + totalSpecular + totalEmissiveRadiance;',
          `vec3 camlinPbr = totalDiffuse + totalSpecular + totalEmissiveRadiance;
           vec3 fleshLight = mix(totalDiffuse, diffuseColor.rgb, 0.20) + totalSpecular + totalEmissiveRadiance;
           vec3 outgoingLight = mix(fleshLight, camlinPbr, camlinTooth);`,
        );
    console.log('🦷 tooth-mask-v13 patches', {
      albedo: shader.fragmentShader.includes('vec3 flesh = vec3(0.30'),
      roughness: shader.fragmentShader.includes('mix(0.55, roughnessFactor, camlinTooth)'),
      normals: shader.fragmentShader.includes('mix(nonPerturbedNormal, normal, camlinTooth)'),
      outgoing: shader.fragmentShader.includes('fleshLight = mix(totalDiffuse, diffuseColor.rgb, 0.20)'),
    });
  };
  mesh.material = mat;
  console.log(
    '🦷 Tooth-mask enamel/flesh',
    mesh.name,
    'toothVerts',
    toothCount,
    'fleshVerts',
    mask.count - toothCount,
  );
  return true;
}

/**
 * Shared enamel look for tooth-only surfaces without a vertex-colour mask.
 * Do not use this on combined teeth+tongue meshes.
 */
function sanitizeTeethMaterial(mat: THREE.Material, nodeName?: string): void {
  const std = mat as THREE.MeshStandardMaterial;
  if (!std || typeof std.color?.setHex !== 'function') return;
  std.color.setHex(TEETH_ENAMEL_HEX);
  if (std.emissive) std.emissive.setHex(0x000000);
  std.metalness = 0;
  std.roughness = 0.22;
  if ('specularIntensity' in std) {
    (std as THREE.MeshStandardMaterial & { specularIntensity?: number }).specularIntensity = 0.2;
  }
  // Replace — do not mutate a shared GLTF atlas. Nicola/Arc celebration
  // kept dark gum texels through every onBeforeCompile lift.
  std.map = null;
  std.vertexColors = false;
  std.customProgramCacheKey = () => 'camlin-flat-enamel-v20';
  if ('aoMap' in std) std.aoMap = null;
  if ('emissiveMap' in std) std.emissiveMap = null;
  const extra = std as THREE.MeshStandardMaterial & {
    specularMap?: THREE.Texture | null;
    specularColorMap?: THREE.Texture | null;
    specularIntensityMap?: THREE.Texture | null;
  };
  extra.specularMap = null;
  extra.specularColorMap = null;
  extra.specularIntensityMap = null;
  if (std.normalScale) std.normalScale.set(0.28, 0.28);
  std.needsUpdate = true;
  console.log('🦷 enamel20 sanitized teeth material', (mat as { name?: string }).name || '(unnamed)', 'on', nodeName || '(node)', '(flat enamel)');
}

function sanitizeTeethMaterialsOnNode(node: THREE.Object3D): void {
  const mesh = node as THREE.Mesh;
  if (!mesh.material) return;
  hideStrayMouthFluidOverlay(node);
  const n = node.name.toLowerCase();
  if (n.includes('tongue') && !n.includes('teeth')) return;
  // Sam CC teeth: COLOR_* is a real tooth/flesh mask (signed-off v13).
  // Do not treat incidental COLOR_0 on F4/M4 teeth as that mask — it paints
  // Sam's dark flesh albedo onto gum-line tooth faces (celebration dark band).
  if (isSamVertexMaskedTeethMesh(node.name) && pickToothMaskAttribute(mesh.geometry)) {
    const existingNormal = (mesh.material as THREE.MeshStandardMaterial).normalMap ?? null;
    if (applyVertexMaskedEnamelMaterial(mesh, existingNormal)) {
      console.log('🦷 Vertex-masked enamel/flesh on embedded teeth:', node.name);
      return;
    }
  }
  const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  const nodeLooksLikeTeeth = /teeth|tooth|m_teeth/i.test(node.name);
  const isTeeth = mats.some((mat) => isTeethMaterialName((mat as { name?: string } | undefined)?.name)) || nodeLooksLikeTeeth;
  if (!isTeeth) return;
  const prev = mats[0] as THREE.MeshStandardMaterial | undefined;
  // basics1 (Aug 15 2026) — KEEP THE AUTHORED M_Teeth MATERIAL.
  //
  // The UE5 F4/M4 `M_Teeth_BaseColor` atlas already paints both arches
  // correctly: warm-white crowns on a scalloped pink gum line, plus the
  // oral-cavity bag. Verified by decoding the GLB texture offline
  // (/tmp/camlin-glb/out/f4-shay-teeth-tex-crop.png). Nothing about that
  // art needed replacing.
  //
  // The original bug (Aug 13 2026) was a DARK BAND over Nicola/Arc's
  // upper teeth on celebration. Its real causes were the M_Fluid saliva
  // cards (hidden above) and the AO/specular maps blotching the crowns.
  // Every later "fix" escalated instead of stopping there: enamel18/20
  // dropped the albedo entirely (white gums), gums21/22 re-derived a gum
  // line from vertex colours (faceted pink slabs), teethscale1-3 shrank
  // the arch (Nicola's teeth went tiny), and mouthfix2-8 displaced face
  // verts and repainted the face atlas (collapsed lips, punch-through).
  // All of that is gone. If a defect survives with the authored material,
  // it is an ASSET bug and belongs in the Blender/UE5 pipeline
  // (see .cursor/rules/avatar-asset-pipeline.mdc), not in this renderer.
  if (prev?.map) {
    prev.metalness = 0;
    prev.roughness = Math.max(prev.roughness ?? 0.5, 0.35);
    if (prev.emissive) prev.emissive.setHex(0x000000);
    // The blotches: baked AO and specular darkening on the crowns.
    if ('aoMap' in prev) prev.aoMap = null;
    if ('emissiveMap' in prev) prev.emissiveMap = null;
    const extra = prev as THREE.MeshStandardMaterial & {
      specularMap?: THREE.Texture | null;
      specularColorMap?: THREE.Texture | null;
      specularIntensityMap?: THREE.Texture | null;
    };
    extra.specularMap = null;
    extra.specularColorMap = null;
    extra.specularIntensityMap = null;
    if (prev.normalScale) prev.normalScale.set(0.4, 0.4);
    prev.vertexColors = false;
    prev.needsUpdate = true;
    console.log('🦷 basics1 kept authored teeth atlas on', node.name, '(AO/specular neutralised)');
    return;
  }
  // No authored atlas (legacy Shenya-class teeth): flat enamel as before.
  for (const mat of mats) sanitizeTeethMaterial(mat, node.name);
}

/** CC exports sometimes include a `Basis.001` target. Driving it >0 double-applies rest pose and bunches the lips. */
function zeroCcBasisMorphs(node: THREE.Object3D): void {
  const mesh = node as THREE.Mesh;
  const dict = mesh.morphTargetDictionary;
  const inf = mesh.morphTargetInfluences;
  if (!dict || !inf) return;
  for (const name of Object.keys(dict)) {
    if (!/^basis(\.|$)/i.test(name)) continue;
    const idx = dict[name];
    if (typeof idx === 'number' && idx < inf.length) inf[idx] = 0;
  }
}

function moodOwnsJawOpen(blendshapes: Record<string, number> | null): boolean {
  if (!blendshapes) return false;
  return (Number(blendshapes.jawOpen) || 0) > 0.001 || (Number(blendshapes.mouthOpen) || 0) > 0.001;
}

/**
 * During a mood/expression hold: keep viseme donors (Sam Mesh002 teeth) from
 * showing a leftover open jaw, and force jawOpen closed on the face unless
 * the active mood actually includes it (surprised).
 */
function clampPreviewJawSlam(
  scene: THREE.Object3D,
  bodyMeshName: string | undefined,
  moodBlendshapes: Record<string, number> | null,
): void {
  const keepFaceJaw = moodOwnsJawOpen(moodBlendshapes);
  scene.traverse((node: any) => {
    const influences = node?.morphTargetInfluences;
    const dict = node?.morphTargetDictionary;
    if (!influences || !dict) return;
    const isDonor = isMoodSpeechDonorMesh(node, bodyMeshName);
    if (isDonor) {
      for (const morphName of SPEECH_OWNED_MORPH_NAMES) {
        const idx = resolveMorphTargetIndex(dict, morphName);
        if (idx !== undefined && idx < influences.length) {
          influences[idx] = 0;
        }
      }
      return;
    }
    if (keepFaceJaw) return;
    for (const morphName of PREVIEW_JAW_SLAM_MORPHS) {
      const idx = resolveMorphTargetIndex(dict, morphName);
      if (idx !== undefined && idx < influences.length) {
        influences[idx] = 0;
      }
    }
  });
}

/**
 * Whether a model uses the UE5 MetaHuman skeleton family (Xandra3D F4/M4 and
 * the renamed default Arc, plus their skeleton-merged variants). UE5 rigs need
 * MetaHuman-specific morph corrections and MUST skip the legacy FBX skeletal
 * idle clip (incompatible rest poses).
 *
 * Resolution order:
 *   1. Registry lookup — if the model declares `skeletonType`, trust it. This
 *      is rename-proof: a GLB renamed away from the `f4-`/`m4-`/`arc`
 *      convention still classifies correctly as long as its registry entry
 *      carries `skeletonType: 'ue5'`. The May 17 2026 Arc regression
 *      (`m4-male.glb` → `arc.glb` slipping the substring guard) is exactly
 *      why this lookup comes first.
 *   2. URL-substring fallback — defensive default for URLs NOT in the registry
 *      (ad-hoc test models, externally-supplied GLBs). Kept identical to the
 *      historical heuristic so undeclared models behave exactly as before.
 */
function isXandraUE5AvatarUrl(url: string): boolean {
  const declared = getModelByUrl(url)?.skeletonType;
  if (declared) return declared === 'ue5';
  const u = url.toLowerCase();
  return (
    u.includes('m4-') ||
    u.includes('f4-') ||
    u.includes('arc.glb') ||
    u.includes('arc-lowres.glb')
  );
}

interface AvatarProps {
  position?: number[];
  isMobileCall?: boolean;
  isWebrtcCall?: boolean;
  isInVoiceCall?: boolean;
  isLiveMode?: boolean;
  avatar_url?: string;
  speak: boolean;
  setSpeak: (value: boolean) => void;
  animateCallSpeak: any;
  setAnimateCallSpeak: (value: any) => void;
  setRecord: (value: boolean) => void;
  text: string;
  setAudioSource: (source: string | null) => void;
  playing: boolean;
  setPlaying: (value: boolean) => void;
  userTalking: boolean;
  deviceOrientation: { alpha: number; beta: number; gamma: number };
  orientationPermission: string;
  animateHangupButton: () => void;
  resetHangupButton: () => void;
  /** Skip teeth animation creation for performance (Visual IVR doesn't need teeth morphs) */
  skipTeethAnimation?: boolean;
  /** Apply Default avatar-style materials to non-default models (legacy Shenya workaround) */
  useDefaultMaterials?: boolean;
  /** Use embedded textures from GLB (skip external texture loading). For models with baked-in textures. */
  useEmbeddedTextures?: boolean;
  /** Appearance to apply as part of initial model setup, merged over model defaults. */
  initialAppearance?: Record<string, string | undefined>;
  avatarInstanceId?: string;
  /** Fired after model default + initial appearance colours have been applied. */
  onAppearanceApplied?: () => void;
}

interface AvatarRef {
  stopAllAnimations: () => void;
  forceResetSkeletalAnimations: () => void;
  setIsThinking: (value: boolean) => void;
  setIsListening: (value: boolean) => void;
  getMixer: () => AnimationMixer | null;
  getMorphTargetDictionary: () => { body: any; teeth: any } | null;
  playIdleAnimation: () => void;
  stopVisemeAnimation: () => void;
  isReady: () => boolean;
}

/**
 * Map a hex eye colour to the nearest pre-baked iris texture variant.
 * Converts hex to HSL and picks the closest natural eye colour file.
 */
function mapEyeColorToVariant(hex: string): string {
  const raw = hex.replace('#', '');
  const r = parseInt(raw.substring(0, 2), 16) / 255;
  const g = parseInt(raw.substring(2, 4), 16) / 255;
  const b = parseInt(raw.substring(4, 6), 16) / 255;

  const cmax = Math.max(r, g, b);
  const cmin = Math.min(r, g, b);
  const delta = cmax - cmin;
  const l = (cmax + cmin) / 2;

  let s = 0;
  if (delta > 0) {
    s = l <= 0.5 ? delta / (cmax + cmin) : delta / (2 - cmax - cmin);
  }

  let h = 0;
  if (delta > 0) {
    if (cmax === r) h = 60 * (((g - b) / delta) % 6);
    else if (cmax === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
    if (h < 0) h += 360;
  }

  if (s < 0.15) return 'gray';
  if (h >= 180 && h < 260) return 'blue';
  if (h >= 80 && h < 170) return 'green';
  if (h >= 38 && h < 55) return 'hazel';
  if (h >= 10 && h < 55) return 'brown';
  return 'brown';
}

const Avatar = forwardRef<AvatarRef, AvatarProps>((
  {
    position = [0, 0, 0],
    isMobileCall = false,
    isWebrtcCall = false,
    isInVoiceCall = false,
    isLiveMode = false,
    avatar_url = '/models/arc.glb',
    speak,
    setSpeak,
    animateCallSpeak,
    setAnimateCallSpeak,
    setRecord,
    text,
    setAudioSource,
    playing,
    setPlaying,
    userTalking,
    deviceOrientation,
    orientationPermission,
    animateHangupButton,
    resetHangupButton,
    skipTeethAnimation = false,
    useDefaultMaterials = false,
    useEmbeddedTextures = false,
    initialAppearance,
    avatarInstanceId,
    onAppearanceApplied
  },
  ref
) => {
  const verboseAvatarDebug = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return new URLSearchParams(window.location.search).get('debugAvatar') === 'true';
    } catch {
      return false;
    }
  }, []);
  const resolvedAvatarUrl = resolveModelUrl(avatar_url);
  const resolvedDracoDecoderPath = resolveAvatarRuntimeAssetUrl('/draco/');
  const isLegacyModel = (() => {
    const file = avatar_url.split('/').pop()?.split('?')[0] || '';
    return file === 'model.glb' || file === 'default.glb' || file === 'default-lowres.glb';
  })();
  useEffect(() => {
    if (!verboseAvatarDebug) {
      return;
    }

    console.log('[Avatar] Preparing model load', {
      avatarUrl: avatar_url,
      resolvedAvatarUrl,
      resolvedDracoDecoderPath,
      useDefaultMaterials,
      useEmbeddedTextures
    });
  }, [avatar_url, resolvedAvatarUrl, resolvedDracoDecoderPath, useDefaultMaterials, useEmbeddedTextures, verboseAvatarDebug]);
  const gltf = useGLTF(resolvedAvatarUrl, resolvedDracoDecoderPath);
  const [morphTargetDictionaryBody, setMorphTargetDictionaryBody] = useState<any>(null);
  const [bodyMeshName, setBodyMeshName] = useState<string>('HG_Body'); // Store actual mesh name for animation tracks
  const [teethMeshName, setTeethMeshName] = useState<string>('HG_TeethLower'); // Store actual teeth mesh name
  const [morphTargetDictionaryLowerTeeth, setMorphTargetDictionaryLowerTeeth] = useState<any>(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [isVoiceCallAnimationInProgress, setIsVoiceCallAnimationInProgress] = useState(false);
  const isSpeakingRef = useRef<boolean>(false); // Immediate ref to track speaking state (prevents race conditions)
  const facePreviewHoldRef = useRef<boolean>(false);
  const mixerRef = useRef<AnimationMixer | null>(null);
  // Slice 9 v3 (May 12 2026): post-frame teeth diagnostic. Reads
  // morphTargetInfluences[jawOpen] on the teeth + face primitives AT THE END
  // of useFrame (after mixer.update + skeletal gesture + bone sync), so we
  // can compare against what the speech RAF wrote. Divergence proves an
  // overwrite is happening downstream of the speech RAF.
  // Active skeletal gesture state — driven per-frame after mixer.update so
  // offsets stack on top of idle/voice animations. We find ALL bones in the
  // scene that match each role's candidate names (not just the first), so
  // models with duplicated skeletons across body/hair/clothes meshes still
  // animate correctly.
  /** Re-applied each useFrame while a gesture plays (see mixer.update block). */
  const gestureMorphApplyRef = useRef<((frame: GestureFrame) => void) | null>(null);
  const skeletalGestureRef = useRef<{
    gestureType: GestureType;
    startTimeMs: number;
    durationMs: number;
    fadeInMs: number;
    fadeOutMs: number;
    times: number[];
    /**
     * When true, frame `{x,y,z}` values are interpreted as rotations
     * around the WORLD x/y/z axes (and converted to bone-local using
     * the parent's current world quaternion inverse each frame). This
     * makes lean-forward / lean-back stable across rigs whose spine
     * local axes point in different world directions. Defaults to
     * false (bone-local Euler) for the arm gestures.
     */
    worldSpaceAxes: boolean;
    byRole: Array<{
      role: BoneRole;
      bones: THREE.Bone[];
      baseQuats: THREE.Quaternion[];
      // Slice 13 (May 15 2026): for the world-space-axes path, we ALSO
      // snapshot each bone's WORLD quaternion at gesture start. The pre-
      // existing `parentInv * worldOffset * parentWorld` math fails for
      // multi-skeleton models like Arc / Joel / Kiera / Naomi where the
      // duplicate spine_03 bones have DIFFERENT parent chains: the body
      // skeleton's chest_03 is animated by the idle clip, while the hair
      // / clothes skeletons' chest_03 stays at rest, so each duplicate
      // ends up with a different `parentWorldQ`. Targeting the desired
      // WORLD quat directly (`worldBase * worldOffset`) and back-deriving
      // local from the bone's own parent removes that asymmetry.
      worldBaseQuats: THREE.Quaternion[];
      frames: Array<{ x: number; y: number; z: number }>;
    }>;
  } | null>(null);
  const lastAnimationTimeRef = useRef<number>(0); // Track last animation time to prevent rapid duplicates
  const currentAnimationHandoffTokenRef = useRef<string | null>(null); // Track current animation handoffToken to prevent duplicates
  const currentAnimationCleanupRef = useRef<(() => void) | null>(null); // Track cleanup function for current animation
  const currentAnimationIdRef = useRef<number>(0); // Unique ID for each animation instance to prevent old animations from completing
  const lastBlendDataRef = useRef<any>(null); // Track last blendData to detect true duplicates
  const isStoppingAnimationRef = useRef<boolean>(false); // Track if we're in the process of stopping animation
  const listeningAnimationRef = useRef<any>(null);  // Store listening animation instance
  const thinkingAnimationRef = useRef<any>(null);   // Store thinking animation instance
  const voiceNonSpeechAnimationRef = useRef<any>(null); // Unified voice/live non-speech controller
  const speechExpressivenessRef = useRef<any>(null); // Brow/eye modulation during speech
  const animationStateManagerRef = useRef<AnimationStateManager>(new AnimationStateManager()); // Centralized animation state management
  const audioSyncRef = useRef<{
    isPlaying: boolean;
    startTime: number;
    pausedAt: number;
    currentAudioTime: number;
  }>({
    isPlaying: false,
    startTime: 0,
    pausedAt: 0,
    currentAudioTime: 0
  });
  const audioMonitorCleanupRef = useRef<(() => void) | null>(null); // Cleanup for audio monitoring

  // Phase 1: Audio Time Synchronization for Visual IVR
  // Tracks external audio time from parent page (when audio plays in parent, not iframe)
  const externalAudioTimeRef = useRef<{
    currentTime: number;       // Current playback position in seconds
    duration: number;          // Total audio duration
    isPlaying: boolean;        // Is audio currently playing
    lastSyncTime: number;      // performance.now() when last sync was received
    animationStartOffset: number; // Offset between animation start and audio start
  }>({
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    lastSyncTime: 0,
    animationStartOffset: 0
  });

  const faceCustomizationRef = useRef<Record<string, number>>({}); // Persistent face shape values
  const skeletonRef = useRef<THREE.Skeleton | null>(null); // Skeleton for bone-based face customization
  // Tracks the current mood label so sentiment events can be deduped
  // across the same label without permanently latching (see
  // handleSentiment). Kept as a ref so `finishLiveSpeaking` / reset
  // paths can drop it back to 'neutral' and the next sentiment gets
  // through again.
  const lastSentimentMoodRef = useRef<string>('neutral');
  // Mirror of the `userTalking` prop for event handlers registered once
  // (handleSentiment). While the USER is speaking the face should read
  // attentive-neutral: a sentiment mood still held from the previous turn is
  // faded out early, and a freshly arriving one is not applied (Aug 17 2026
  // "smile hanging around while I talk" Visual IVR defect).
  const userTalkingRef = useRef<boolean>(false);
  // M4 model: cache initial (baked) morph values for non-ARKit shapes so we can restore them every frame
  const m4BakedMorphsRef = useRef<Map<number, number> | null>(null);
  // M4 model: cache all morph-target submeshes for cross-mesh sync (lip-sync must affect all submeshes)
  const m4MorphMeshesRef = useRef<any[] | null>(null);
  // Idle-noise overlay accounting. The pre-fix writer did
  // `influences[idx] += max(0, noise)` every frame while not speaking,
  // so smile/jaw ratcheted to 1.0 during idle / listening / thinking /
  // first-bind — the face-breaking grin (Aug 29 2026).
  const idleNoiseOverlayRef = useRef<Map<number, number>>(new Map());
  // M4 model: cache bone pairs for syncing head/neck rotation across skeletons (hair follows head)
  const m4BoneSyncRef = useRef<{ primary: THREE.Bone; copies: THREE.Bone[] }[] | null>(null);
  // Slice 10 (May 12 2026): index into m4MorphMeshesRef.current of the
  // "donor" non-primary mesh used to restore mouth/jaw morphs onto the
  // primary body mesh during speaking (corrects for mixer.update clobbering).
  // See SPEECH_OWNED_MORPH_NAMES at the top of this file for the full list
  // of restored morph names and the rationale.
  const m4SpeechDonorIdxRef = useRef<number | undefined>(undefined);
  // Slice 10 trip-wire: one-shot warn if jawOpen reads "stuck-low" during
  // speech (raw nextValue >= 0.30 but the final influence is below 0.05).
  // This is the exact failure signature Slice 10 fixes — if the warn fires
  // post-fix, the speech-restore step has regressed.
  const stuckJawWarnedRef = useRef(false);

  // For embedded-texture models (GLBs with baked textures), use a 1px transparent placeholder
  // to satisfy the useTexture hook (React hooks can't be called conditionally).
  // The textures won't actually be applied -- the model keeps its own materials.
  const PLACEHOLDER_TEX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='; // 1px transparent GIF
  const texturePaths = useEmbeddedTextures
    ? Array(14).fill(PLACEHOLDER_TEX) as [string, string, string, string, string, string, string, string, string, string, string, string, string, string]
    : [
      resolveAvatarRuntimeAssetUrl('/images/body.webp'),
      resolveAvatarRuntimeAssetUrl('/images/eyes.webp'),
      resolveAvatarRuntimeAssetUrl('/images/teeth_diffuse.webp'),
      resolveAvatarRuntimeAssetUrl('/images/body_specular.webp'),
      resolveAvatarRuntimeAssetUrl('/images/body_roughness.webp'),
      resolveAvatarRuntimeAssetUrl('/images/body_normal.webp'),
      resolveAvatarRuntimeAssetUrl('/images/teeth_normal.webp'),
      resolveAvatarRuntimeAssetUrl('/images/h_color.webp'),
      resolveAvatarRuntimeAssetUrl('/images/tshirt_diffuse.webp'),
      resolveAvatarRuntimeAssetUrl('/images/tshirt_normal.webp'),
      resolveAvatarRuntimeAssetUrl('/images/tshirt_roughness.webp'),
      resolveAvatarRuntimeAssetUrl('/images/h_alpha.webp'),
      resolveAvatarRuntimeAssetUrl('/images/h_normal.webp'),
      resolveAvatarRuntimeAssetUrl('/images/h_roughness.webp'),
    ] as [string, string, string, string, string, string, string, string, string, string, string, string, string, string];

  const [
    bodyTexture,
    eyesTexture,
    teethTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    teethNormalTexture,
    hairTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture,
  ] = useTexture(texturePaths);

  // Match standalone (r142) texture encodings for visual parity
  _.each([
    bodyTexture,
    eyesTexture,
    teethTexture,
    teethNormalTexture,
    bodySpecularTexture,
    bodyRoughnessTexture,
    bodyNormalTexture,
    tshirtDiffuseTexture,
    tshirtNormalTexture,
    tshirtRoughnessTexture,
    hairAlphaTexture,
    hairNormalTexture,
    hairRoughnessTexture
  ], (t: THREE.Texture) => {
    if ('colorSpace' in t) {
      t.colorSpace = THREE.SRGBColorSpace;
    } else {
      (t as any).encoding = sRGBEncoding;
    }
    t.flipY = false;
  });

  // Only normal maps are linear in the reference setup
  const normalTextures = [bodyNormalTexture, tshirtNormalTexture, teethNormalTexture, hairNormalTexture];
  normalTextures.forEach((t: THREE.Texture) => {
    if ('colorSpace' in t) {
      t.colorSpace = THREE.LinearSRGBColorSpace;
    } else {
      (t as any).encoding = LinearEncoding;
    }
  });

  const headRef = useRef<THREE.Object3D>(null);
  const neckRef = useRef<THREE.Object3D>(null);
  const leftEyeRef = useRef<THREE.Object3D>(null);
  const rightEyeRef = useRef<THREE.Object3D>(null);
  const threeDViewRef = useRef<{
    updateOrientation: (deviceOrientation: { alpha: number; beta: number; gamma: number }) => void;
    reset: () => void;
  } | null>(null);

  useEffect(() => {
    threeDViewRef.current = null;
  }, [avatar_url]);

  useFrame((state, delta) => {
    if (orientationPermission === 'granted') {
      if (!threeDViewRef.current && headRef.current && neckRef.current && leftEyeRef.current && rightEyeRef.current) {
        threeDViewRef.current = setupThreeDView(headRef, neckRef, leftEyeRef, rightEyeRef);
      }

      threeDViewRef.current?.updateOrientation(deviceOrientation);
    }

    // Update mixer with audio sync for Visual IVR
    // When external audio position is available, sync animation time to it
    if (mixerRef.current) {
      const audioSync = externalAudioTimeRef.current;
      const now = performance.now();
      const timeSinceSync = (now - audioSync.lastSyncTime) / 1000; // Convert to seconds

      // Check if we have a recent audio sync (within 500ms) and audio is playing
      // This indicates Visual IVR mode where we need to sync with phone audio
      if (audioSync.isPlaying && audioSync.lastSyncTime > 0 && timeSinceSync < 0.5) {
        // Interpolate the current audio time:
        // lastKnownPosition + timeSinceLastSync
        const interpolatedAudioTime = audioSync.currentTime + timeSinceSync;

        // Clamp to duration to prevent overshooting
        const clampedTime = Math.min(interpolatedAudioTime, audioSync.duration);

        // Get all playing actions and sync their time
        // This provides more precise control than mixer.setTime()
        const actions = (mixerRef.current as any)._actions;
        let syncedCount = 0;
        if (actions && actions.length > 0) {
          for (const action of actions) {
            if (action && action.isRunning && action.isRunning()) {
              // Set action time directly for frame-accurate sync
              action.time = clampedTime;
              syncedCount++;
            }
          }
        }

        // Debug logging: log first sync and then periodically (every 60 frames = ~1 second)
        if (!externalAudioTimeRef.current.animationStartOffset) {
          if (verboseAvatarDebug) {
            console.log(`🔄 [Avatar] Audio sync ACTIVE: audioTime=${clampedTime.toFixed(3)}s, syncedActions=${syncedCount}`);
          }
          externalAudioTimeRef.current.animationStartOffset = 1; // Use as flag for "logged once"
        }

        // Still update mixer for blending, but with zero delta since we set time manually
        mixerRef.current.update(0);
      } else if (!isSpeakingRef.current) {
        // No active audio sync - use normal delta-based animation
        // SKIP during speaking: the RAF-based animateFrame directly sets morph values,
        // and mixer.update() can reset them via stale track bindings even after uncaching
        const gestureCtrl = getGestureController();
        if (gestureCtrl.isGesturePlaying() || facePreviewHoldRef.current) {
          muteIdleMorphActionsForGesture();
        } else if (idleMorphWeightRestoreRef.current) {
          restoreIdleMorphActionsAfterGesture();
        }
        mixerRef.current.update(delta);
      }
    }

    // UE5/F4/M4/Arc: idle micro + breathing mixer clips write the same morph
    // indices as gesture playback. Re-apply the latest gesture frame AFTER
    // mixer.update so 🎭 gestures stay visible (May 27 2026 Arc tester fix).
    const gestureCtrl = getGestureController();
    if (gestureCtrl.isGesturePlaying()) {
      const latest = gestureCtrl.getLatestFrame();
      if (latest && gestureMorphApplyRef.current) {
        gestureMorphApplyRef.current(latest);
      }
    }

    // Mood overlay is RAF subtract-then-add. mixer.update() typically runs
    // after that RAF and zeros body morphs, so accounting becomes a no-op
    // while teeth donors keep the overlay (jaw slam + mood at once).
    // Re-apply after the mixer, same as gestures. Skip while speaking so
    // we do not overwrite visemes with absolute mood weights.
    if (!isSpeakingRef.current) {
      tickMoodOverlay();
    }
    if (facePreviewHoldRef.current && !isSpeakingRef.current && gltf?.scene) {
      clampPreviewJawSlam(gltf.scene, bodyMeshName, getActiveMoodOverlayBlendshapes());
    }

    // F4/M4 head + neck sibling sync (restored from Slice 13 v2 — the
    // shipped working version). UE5 MetaHuman models split a single
    // logical avatar across 5–8 skinned meshes (head / torso / legs /
    // hair / clothes), each carrying its own copy of the rig.
    // GLTFLoader renames duplicate bones with `_N` suffixes, so the
    // mixer's `head` track only animates skin 0's bone — the hair /
    // clothes meshes (using `head_1`, `head_2`, …) would freeze.
    //
    // We sync ONLY head / neck_01 / neck_02 here for two reasons:
    //   1) hair / makeup-attached meshes anchor on head and neck, so
    //      synchronising those keeps the hair tracking facial rotation
    //      during idle / lip-sync (the main visible artefact pre-fix).
    //   2) extending the sync to spine / arm bones is unsafe across all
    //      models in the library — Arc and a handful of others have
    //      slightly different bind matrices per skin, and copying skin
    //      0's local quat onto skin 1's bone bakes skin 0's bind into
    //      skin 1's vertex skinning, producing heavy mesh deformation
    //      (the user reported this as "Arc face-down / clothes
    //      detached" when an earlier slice tried the wider sync).
    //
    // This means body-rotation gestures (lean-forward / lean-back /
    // shrug) still only animate the canonical bone — multi-skel rigs
    // see the head + hair tilt but the body mesh stays put. That is
    // the trade-off until we land a proper skeleton-merge step at
    // GLB-load time. Tracked as a known limitation in the session note.
    if (m4BoneSyncRef.current && m4BoneSyncRef.current.length > 0) {
      for (const pair of m4BoneSyncRef.current) {
        for (const copy of pair.copies) {
          copy.quaternion.copy(pair.primary.quaternion);
        }
      }
    }

    // Skeletal gesture application — runs AFTER mixer.update each frame.
    // For each role we walk every matching bone in the entire scene tree
    // (so models that authored hair/clothes as separate skinned meshes,
    // each with their own bone copy, all follow the gesture). We compose
    // bone.quaternion = baseQuatAtGestureStart * gestureOffset, which
    // dominates over idle/voice for bones they share (head/neck/chest)
    // and avoids per-frame compounding for bones idle never touches
    // (arms/shoulders). When the gesture finishes we clear the ref and
    // the next mixer.update restores any shared bones to their idle pose.
    const skg = skeletalGestureRef.current;
    if (skg) {
      const elapsedMs = performance.now() - skg.startTimeMs;
      const totalMs = skg.durationMs + skg.fadeOutMs;
      if (elapsedMs >= totalMs) {
        skeletalGestureRef.current = null;
      } else {
        const elapsedSec = Math.min(
          elapsedMs / 1000,
          skg.times[skg.times.length - 1],
        );
        let idx = 0;
        for (let i = 0; i < skg.times.length - 1; i++) {
          if (skg.times[i + 1] > elapsedSec) { idx = i; break; }
          idx = i;
        }
        const t1 = skg.times[idx];
        const t2 = skg.times[Math.min(idx + 1, skg.times.length - 1)];
        const segDur = t2 - t1;
        const segT = segDur > 0 ? (elapsedSec - t1) / segDur : 0;

        let weight = 1;
        if (elapsedMs < skg.fadeInMs) {
          weight = elapsedMs / skg.fadeInMs;
        } else if (elapsedMs > skg.durationMs) {
          weight = Math.max(0, (totalMs - elapsedMs) / skg.fadeOutMs);
        }

        // Compose: base (snapshot at gesture start) * offset.
        // We OVERWRITE bone.quaternion rather than multiplying onto the
        // current value because:
        //   • arm/shoulder bones are NOT driven by idle/voice animations
        //     in this rig setup, so multiplying onto current would compound
        //     across frames and the arm would spiral.
        //   • for bones the idle DOES drive (head/neck/chest), the gesture
        //     is meant to dominate while it plays — once the gesture ends
        //     and skeletalGestureRef.current is cleared, the mixer's next
        //     update restores the idle's intended quaternion.
        //
        // Two paths:
        //   (a) bone-local Euler (default) — `frame.{x,y,z}` are Euler
        //       angles in the bone's LOCAL frame. Best for arm gestures
        //       where shoulders/elbows have a standardised outward-Z
        //       across rigs.
        //   (b) world-space axes (`skg.worldSpaceAxes` true) — same
        //       numbers but interpreted as world-axis rotations. Best
        //       for lean gestures where rigs disagree on which local
        //       axis points "sideways in world space".
        const offsetEuler = new THREE.Euler();
        const offsetQuat = new THREE.Quaternion();
        const worldX = new THREE.Vector3(1, 0, 0);
        const worldY = new THREE.Vector3(0, 1, 0);
        const worldZ = new THREE.Vector3(0, 0, 1);
        const tmpQ = new THREE.Quaternion();
        const parentWorldQ = new THREE.Quaternion();
        for (const rb of skg.byRole) {
          const f1 = rb.frames[idx];
          const f2 = rb.frames[Math.min(idx + 1, rb.frames.length - 1)];
          const x = (f1.x + (f2.x - f1.x) * segT) * weight;
          const y = (f1.y + (f2.y - f1.y) * segT) * weight;
          const z = (f1.z + (f2.z - f1.z) * segT) * weight;

          if (!skg.worldSpaceAxes) {
            offsetEuler.set(x, y, z);
            offsetQuat.setFromEuler(offsetEuler);
            for (let i = 0; i < rb.bones.length; i++) {
              const bone = rb.bones[i];
              const base = rb.baseQuats[i];
              bone.quaternion.copy(base).multiply(offsetQuat);
            }
          } else {
            // Build the WORLD-space offset (rotate around world X/Y/Z in
            // that order; only X is non-zero for our lean keyframes
            // today, but we keep all three for future gestures).
            const worldOffset = new THREE.Quaternion();
            if (Math.abs(x) > 1e-9) {
              worldOffset.multiply(tmpQ.setFromAxisAngle(worldX, x));
            }
            if (Math.abs(y) > 1e-9) {
              worldOffset.multiply(tmpQ.setFromAxisAngle(worldY, y));
            }
            if (Math.abs(z) > 1e-9) {
              worldOffset.multiply(tmpQ.setFromAxisAngle(worldZ, z));
            }
            // Slice 13 v2 (May 15 2026 PM): for each bone instance, target
            // the desired WORLD quat directly:
            //   desiredWorld = worldOffset · worldBase
            //   bone.quaternion = parentWorld⁻¹ · desiredWorld
            // The PRE-multiplication order matters: writing the offset on
            // the LEFT of the rest pose makes the change-from-rest equal
            // to `worldOffset` exactly (i.e., a pure rotation around the
            // intended world axis). The Slice 13 v1 ordering
            // `worldBase · worldOffset` looked symmetric but actually
            // CONJUGATED the rotation axis by worldBase — for any bone
            // whose rest world rotation is non-identity (head / spine on
            // most rigs), the effective axis swung 90° from the intended
            // axis, producing the "nod moves head left-right" /
            // "lean-forward moves head left-right" regression Keith
            // reported May 15 2026 PM. The PM fix is one line: swap the
            // multiplication order so the offset pre-multiplies the rest.
            // worldBase was snapshotted at gesture start (per-instance)
            // so multi-skeleton models still get hair / clothes /
            // body in lockstep with the body skeleton's idle drift.
            const tmpDesired = new THREE.Quaternion();
            for (let i = 0; i < rb.bones.length; i++) {
              const bone = rb.bones[i];
              const worldBase = rb.worldBaseQuats[i];
              tmpDesired.copy(worldOffset).multiply(worldBase);
              if (bone.parent) {
                bone.parent.updateWorldMatrix(true, false);
                bone.parent.getWorldQuaternion(parentWorldQ);
              } else {
                parentWorldQ.identity();
              }
              bone.quaternion.copy(parentWorldQ).invert().multiply(tmpDesired);
            }
          }
        }
      }
    }

    // Apply persistent face shape customization AFTER animations
    // This ensures face shape keys are not overwritten by animation updates
    const faceConfig = faceCustomizationRef.current;
    if (Object.keys(faceConfig).length > 0 && gltf?.scene) {
      gltf.scene.traverse((node: any) => {
        if (node instanceof THREE.SkinnedMesh && (node.name.includes('Body') || node.name.includes('HG_Body'))) {
          const dict = node.morphTargetDictionary;
          const influences = node.morphTargetInfluences;
          if (dict && influences) {
            // Helper to set morph target
            const setMorph = (name: string, value: number) => {
              const index = dict[name];
              if (typeof index === 'number' && index < influences.length) {
                influences[index] = Math.max(0, Math.min(1, value));
              }
            };

            Object.entries(faceConfig).forEach(([key, value]) => {
              if (typeof value !== 'number') return;
              if (facePreviewHoldRef.current && (
                key === 'jawOpen' || key === 'mouthSmile' || key === 'mouthPucker' ||
                key === 'browInnerUp' || key === 'browDown' || key === 'cheekPuff'
              )) {
                return;
              }

              // Face shape controls (-1 to 1 range, map to 0-1)
              if (key === 'faceWidth') {
                const morphValue = (value + 1) / 2;
                setMorph('faceWidth', morphValue);
                return;
              }
              if (key === 'faceHeight') {
                // faceHeight is bone-based only, skip
                return;
              }

              // Jaw position controls (0-1 range, apply directly)
              if (key === 'jawForward') {
                setMorph('jawForward', value);
                return;
              }
              if (key === 'jawLeft') {
                setMorph('jaw_L', value);
                return;
              }
              if (key === 'jawRight') {
                setMorph('jaw_R', value);
                return;
              }

              // Expression controls (0-1 range, apply directly)
              // Some need to be combined (L+R)
              if (key === 'cheekPuff') {
                setMorph('cheekPuff', value);
                return;
              }
              if (key === 'browInnerUp') {
                setMorph('browInnerUp', value);
                return;
              }
              if (key === 'browDown') {
                setMorph('browDown_L', value);
                setMorph('browDown_R', value);
                return;
              }
              if (key === 'jawOpen') {
                setMorph('jawOpen', value);
                return;
              }
              if (key === 'mouthSmile') {
                setMorph('mouthSmile_L', value);
                setMorph('mouthSmile_R', value);
                return;
              }
              if (key === 'mouthPucker') {
                setMorph('mouthPucker', value);
                return;
              }
              if (key === 'eyeWide') {
                setMorph('eyeWide_L', value);
                setMorph('eyeWide_R', value);
                return;
              }
              if (key === 'eyeSquint') {
                setMorph('eyeSquint_L', value);
                setMorph('eyeSquint_R', value);
                return;
              }
              if (key === 'noseSneer') {
                setMorph('noseSneer_L', value);
                setMorph('noseSneer_R', value);
                return;
              }

              // Custom shape keys (jawWidth, noseWidth, etc. - when added in Blender)
              // Map -1..1 to 0..1
              const index = dict[key];
              if (index !== undefined) {
                const morphValue = (value + 1) / 2;
                influences[index] = morphValue;
              }
            });
          }
        }
      });
    }

    // Speech expressiveness layer: add subtle brow/cheek modulation while speaking
    if (isSpeakingRef.current && speechExpressivenessRef.current) {
      speechExpressivenessRef.current.update(state.clock.getElapsedTime() * 1000);
    }

    // Procedural idle micro-motion (non-repeating noise) when not speaking.
    // Overlay-accounted (subtract previous, add next) so idle / listening /
    // thinking / startup cannot ratchet smile+jaw to 1.0. Speech visemes
    // own the mouth while speaking — skip entirely then.
    if (!isSpeakingRef.current && !facePreviewHoldRef.current && gltf?.scene && m4MorphMeshesRef.current && m4MorphMeshesRef.current.length > 0) {
      const mood = lastSentimentMoodRef.current || 'neutral';
      const noise = sampleProceduralIdleNoise(state.clock.getElapsedTime(), mood);
      const primary = m4MorphMeshesRef.current[0];
      const dict = primary?.morphTargetDictionary;
      const inf = primary?.morphTargetInfluences;
      if (dict && inf) {
        const applied = idleNoiseOverlayRef.current;
        for (const [name, delta] of Object.entries(noise)) {
          const idx = resolveMorphTargetIndex(dict, name);
          if (idx !== undefined && idx < inf.length) {
            applyTrackedOverlay(inf, idx, delta, applied);
          }
        }
      }
    } else if (isSpeakingRef.current) {
      idleNoiseOverlayRef.current.clear();
    }

    // Morph-submesh collection + speech-mouth restore run for EVERY model.
    // Slice 10 originally gated this on UE5 only, so Sam's legacy FBX mesh
    // (`/model.glb`) kept the "lips don't part" mixer-clobber: idle morph
    // tracks bind to the body mesh and reset jawOpen every frame while
    // teeth keep moving. Collecting meshes here lets the donor-copy restore
    // run on Sam and any other multi-mesh morph rig.
    //
    // UE5-only steps (baked non-ARKit restore, idle submesh sync, bone
    // sibling sync) stay gated — running baked restore on Sam would
    // clobber face-customization morphs applied earlier in this frame.
    const isUE5Model = isXandraUE5AvatarUrl(avatar_url);
    if (gltf?.scene) {

      // --- Collect all morph submeshes on first frame ---
      // Ensure the PRIMARY mesh (the head mesh with the most morph targets, targeted by animation clips) is meshes[0]
      if (!m4MorphMeshesRef.current) {
        let primary: any = null;
        let primaryCount = 0;
        const others: any[] = [];
        gltf.scene.traverse((node: any) => {
          if (node.isMesh && node.morphTargetDictionary && node.morphTargetInfluences) {
            const count = node.morphTargetInfluences.length;
            // Primary = mesh with the MOST morph targets (the head mesh with 107)
            // Also match by bodyMeshName if available
            if (node.name === bodyMeshName || (count > primaryCount && !primary)) {
              if (primary) others.push(primary); // Demote previous primary
              primary = node;
              primaryCount = count;
            } else if (count > primaryCount) {
              // Found a mesh with even more morphs — promote it
              if (primary) others.push(primary);
              primary = node;
              primaryCount = count;
            } else {
              others.push(node);
            }
          }
        });
        const meshes = primary ? [primary, ...others] : others;
        m4MorphMeshesRef.current = meshes;
        console.log('[M4] Found', meshes.length, 'morph submeshes, primary:', primary?.name, `(${primaryCount} morphs)`);
      }

      const meshes = m4MorphMeshesRef.current;
      if (meshes && meshes.length > 0) {
        const primaryMesh = meshes[0]; // SK_M4_Head_1 — the one targeted by animation clips
        const primaryDict = primaryMesh.morphTargetDictionary;
        const primaryInf = primaryMesh.morphTargetInfluences;

        // --- (1) Cache baked morph values on first encounter ---
        // Only cache while NOT speaking — otherwise we'd capture speech-driven
        // values as the "baked" rest pose.
        // UE5-only: Sam's face-customization morphs are applied earlier in
        // this frame; treating them as "baked rest" would freeze them.
        if (isUE5Model && !m4BakedMorphsRef.current && !isSpeakingRef.current) {
          // Standard ARKit blendshape names — only these should be animated
          const ARKIT_NAMES = new Set([
            'eyeBlinkLeft', 'eyeBlinkRight', 'eyeLookDownLeft', 'eyeLookDownRight',
            'eyeLookInLeft', 'eyeLookInRight', 'eyeLookOutLeft', 'eyeLookOutRight',
            'eyeLookUpLeft', 'eyeLookUpRight', 'eyeSquintLeft', 'eyeSquintRight',
            'eyeWideLeft', 'eyeWideRight',
            'jawForward', 'jawLeft', 'jawRight', 'jawOpen',
            'mouthClose', 'mouthFunnel', 'mouthPucker', 'mouthRight', 'mouthLeft',
            'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft', 'mouthFrownRight',
            'mouthDimpleLeft', 'mouthDimpleRight', 'mouthStretchLeft', 'mouthStretchRight',
            'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
            'mouthPressLeft', 'mouthPressRight', 'mouthLowerDownLeft', 'mouthLowerDownRight',
            'mouthUpperUpLeft', 'mouthUpperUpRight',
            'browDownLeft', 'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight',
            'cheekPuff', 'cheekSquintLeft', 'cheekSquintRight',
            'noseSneerLeft', 'noseSneerRight', 'tongueOut',
            // Also include underscore variants
            'eyeBlink_L', 'eyeBlink_R', 'eyeSquint_L', 'eyeSquint_R', 'eyeWide_L', 'eyeWide_R',
            'jaw_L', 'jaw_R', 'mouth_L', 'mouth_R',
            'mouthSmile_L', 'mouthSmile_R', 'mouthFrown_L', 'mouthFrown_R',
            'mouthDimple_L', 'mouthDimple_R', 'mouthStretch_L', 'mouthStretch_R',
            'mouthPress_L', 'mouthPress_R', 'mouthLowerDown_L', 'mouthLowerDown_R',
            'mouthUpperUp_L', 'mouthUpperUp_R',
            'browDown_L', 'browDown_R', 'browOuterUp_L', 'browOuterUp_R',
            'cheekSquint_L', 'cheekSquint_R', 'noseSneer_L', 'noseSneer_R', 'tongue_out',
          ]);

          const bakedMorphs = new Map<number, number>();
          for (const [name, idx] of Object.entries(primaryDict) as [string, number][]) {
            if (!ARKIT_NAMES.has(name)) {
              bakedMorphs.set(idx, primaryInf[idx] ?? 0);
            }
          }
          if (bakedMorphs.size > 0) {
            m4BakedMorphsRef.current = bakedMorphs;
            console.log('[M4] Cached', bakedMorphs.size, 'non-ARKit morph baked values');
          }
        }

        // --- (2) Restore non-ARKit morphs — ONLY when NOT speaking ---
        // During speaking, the RAF-based animateFrame sets morph values on ALL meshes directly.
        // Running restoration here would overwrite animation values with stale data, causing
        // the "second animation stops working" bug.
        if (isUE5Model && !isSpeakingRef.current && m4BakedMorphsRef.current) {
          m4BakedMorphsRef.current.forEach((bakedValue, idx) => {
            primaryInf[idx] = bakedValue;
          });
        }

        // Do not globally clamp browDown on UE5/F4/M4 exports. The mood,
        // thinking, concern, negative, and confused states all rely on those
        // ARKit shapes; zeroing them here made newer faces like Nicola look
        // emotionally flat even though the GLB exposes the correct morphs.

        // --- (3) Sync morph targets — ONLY when NOT speaking ---
        // During speaking, the RAF animateFrame already sets values on ALL 12 submeshes.
        // Syncing here would overwrite those with stale primary mesh data.
        // During idle, the mixer-based morph animations only target bodyMeshName,
        // so we need to sync to keep all submeshes in sync.
        if (isUE5Model && !isSpeakingRef.current) {
          for (let m = 1; m < meshes.length; m++) {
            const otherMesh = meshes[m];
            const otherInf = otherMesh.morphTargetInfluences;
            if (otherInf && otherInf.length === primaryInf.length) {
              for (let i = 0; i < primaryInf.length; i++) {
                otherInf[i] = primaryInf[i];
              }
            }
          }
        }

        // One mouth family per frame while not speaking. Listening / thinking /
        // idle / first-bind all share this path — speech visemes skip it.
        if (!isSpeakingRef.current) {
          const overlay = getActiveMoodOverlayBlendshapes();
          const limits = {
            maxSmile: Math.max(
              SAFE_NONSPEECH_SMILE_MAX,
              overlay ? smileWeightFromBlendshapes(overlay) : 0,
            ),
            maxJaw: Math.max(
              SAFE_NONSPEECH_JAW_MAX,
              overlay ? jawWeightFromBlendshapes(overlay) : 0,
            ),
          };
          for (const mesh of meshes) {
            clampMouthFamilyInfluences(
              mesh.morphTargetInfluences,
              mesh.morphTargetDictionary,
              limits,
            );
          }
        }

        // --- (4) Speech restore: copy mouth/jaw morphs from a non-primary mesh
        //          back onto primary — ONLY when speaking ---
        //
        // Why this exists: the speech RAF (renderLiveSpeechAnimation, ~line 3258)
        // writes its computed jawOpen/mouth-* values to ALL morph submeshes
        // uniformly each frame. After it runs, three.js's AnimationMixer
        // (running idle actions naturalBlinkAction / idleMicroAction /
        // breathingAction with non-zero effective weight) calls mixer.update()
        // in this useFrame and clobbers the BODY mesh's morph influences
        // because those idle clips' tracks bind to bodyMeshName.morphTargetInfluences[idx]
        // exclusively. Non-primary submeshes (teeth = SK_F4_Head_3, eyes,
        // tongue) are NOT bound by any idle action, so they retain the
        // speech RAF's writes intact.
        //
        // The bug surface: the user sees "lips don't part open" because the
        // body/face mesh's jawOpen is reset to ~0 every frame, hiding the
        // teeth (which ARE moving correctly). It's intermittent because the
        // idle actions' track sampling depends on action.time, which lands
        // on different keyframe values each playback cycle. Captured for the
        // first time on May 12 2026 with [Avatar][teeth-postframe] diagnostics
        // showing teeth=0.156 / face=0.0005 on a "stuck-teeth" first burst.
        //
        // The restore: pick any non-primary mesh that has a jawOpen morph
        // (the "donor"), then copy its values for the speech-owned morph
        // names back to primary. This corrects body mesh's lip line WITHOUT
        // touching eye blinks / brow expressions / cheek puffs that the idle
        // actions are intentionally driving.
        if (isSpeakingRef.current && !facePreviewHoldRef.current && meshes.length > 1) {
          // Find the donor mesh once and cache its index across frames in a
          // ref so we don't re-traverse on every frame. We only need a mesh
          // that exposes jawOpen via either name; teeth/eyes meshes always
          // do on F4/M4 rigs.
          let donorIdx = m4SpeechDonorIdxRef.current;
          if (donorIdx === undefined) {
            for (let m = 1; m < meshes.length; m++) {
              const candidate = meshes[m];
              if (candidate.morphTargetDictionary &&
                  resolveMorphTargetIndex(candidate.morphTargetDictionary, 'jawOpen') !== undefined) {
                donorIdx = m;
                m4SpeechDonorIdxRef.current = m;
                console.log('[Avatar][speech-restore] donor mesh selected:', candidate.name);
                break;
              }
            }
          }
          if (donorIdx !== undefined && donorIdx < meshes.length) {
            const donor = meshes[donorIdx];
            const donorDict = donor.morphTargetDictionary;
            const donorInf = donor.morphTargetInfluences;
            if (donorDict && donorInf) {
              for (const morphName of SPEECH_OWNED_MORPH_NAMES) {
                const dIdx = resolveMorphTargetIndex(donorDict, morphName);
                const pIdx = resolveMorphTargetIndex(primaryDict, morphName);
                if (dIdx !== undefined && pIdx !== undefined &&
                    dIdx < donorInf.length && pIdx < primaryInf.length) {
                  primaryInf[pIdx] = donorInf[dIdx];
                }
              }
            }
          }
        }
      }

      // --- (4) Initialize the sibling-bone map (Slice 16, May 16 2026) ---
      //
      // F4/M4 GLBs have ONE armature per skin (5 for M4 / Joel; 8 for F4 /
      // Naomi / Kiera). THREE.js's GLTFLoader renames duplicate bones with
      // `_1`, `_2`, … `_N` suffixes (e.g. `spine_02`, `spine_02_1`, …
      // `spine_02_7`). The AnimationMixer's PropertyBinding.findNode
      // resolves track names by walking the scene tree top-down and
      // returning the FIRST match — which is always the canonical-named
      // bone (no suffix). So idle / breathing / micro animations only
      // update skin 0's bones; skins 1..N are stuck at rest, which made
      // multi-skel rigs look "head-only moves, body/clothes stay" before
      // this fix.
      //
      // URL-gated: F4/M4 share a single MetaHuman bind pose across all
      // armatures, so copying skin 0's local quaternion to siblings keeps
      // them in lockstep without distortion. Arc has 5 armatures with
      // *different* bind matrices per skin (verified locally — applying
      // sibling-sync to Arc destroys the silhouette completely). The Arc
      // fix is the legacy head/neck-only sync that lived here pre-Slice-16,
      // restored below for `arc.glb` only.
      // F4/M4 head + neck sibling sync setup (Slice 13 v2 — restored
      // May 16 2026 after Slice 16 full-body sync proved unsafe for
      // models with diverging armature bind poses).
      //
      // We collect ONLY head / neck_01 / neck_02 bone groups. The
      // narrower scope keeps hair / makeup tracking the head, which is
      // the visually dominant artefact pre-fix; it deliberately does
      // NOT extend to spine / arm bones because the wider sync caused
      // mesh distortion on Arc (different bind matrix per armature)
      // and a backwards-facing rotation on Naomi during the May 16
      // verification pass. Multi-skel BODY gestures remain a known
      // limitation pending a real skeleton-merge implementation.
      if (isUE5Model && !m4BoneSyncRef.current) {
        const boneGroups: Record<string, THREE.Bone[]> = {};
        gltf.scene.traverse((node: any) => {
          if (!node?.isBone) return;
          const baseName = (node.name as string).replace(/_\d+$/, '');
          if (
            baseName === 'head' ||
            baseName === 'neck_01' ||
            baseName === 'neck_02'
          ) {
            if (!boneGroups[baseName]) boneGroups[baseName] = [];
            boneGroups[baseName].push(node);
          }
        });

        const syncPairs: { primary: THREE.Bone; copies: THREE.Bone[] }[] = [];
        for (const [baseName, bones] of Object.entries(boneGroups)) {
          if (bones.length <= 1) continue;
          const canonical = bones.find((b) => b.name === baseName) ?? bones[0];
          const copies = bones.filter((b) => b !== canonical);
          syncPairs.push({ primary: canonical, copies });
        }

        m4BoneSyncRef.current = syncPairs;
        if (syncPairs.length > 0) {
          console.log(
            `[M4] Head/neck sibling sync initialized: ${syncPairs.length} canonical bones with ${syncPairs.reduce((n, p) => n + p.copies.length, 0)} sibling copies`
          );
        }
      }
    }

  });

  useEffect(() => {
    console.log('Starting model initialization with gltf:', gltf);
    if (gltf.scene) {
      try {
        // Shift F4/M4 models up to align face with camera
        // Scale 0.01 is already baked into the GLB root node -- do NOT apply again
        // Position Y uses base offset + model-specific adjustment from registry
        if (isXandraUE5AvatarUrl(avatar_url)) {
          const modelConfig = getModelByUrl(avatar_url) ?? MODEL_REGISTRY.find(m => avatar_url.includes(m.url) || avatar_url.endsWith(m.url.split('/').pop() || ''));
          const baseY = 0.14;
          const modelPosY = modelConfig?.positionY ?? 0;
          gltf.scene.position.y = baseY + modelPosY;
        }

        // Count nodes for summary (full list available via debug flag)
        let nodeCount = 0;
        gltf.scene.traverse(() => { nodeCount++; });
        console.log(`[Avatar] Model loaded: ${nodeCount} nodes`);

        // Apply per-model hideMaterials (May 12 2026, Arc clothing fix).
        // Some GLBs ship duplicate / stray sub-mesh primitives whose material
        // names match a known unused outfit. arc.glb ships a stray
        // MI_Ryan_Top primitive on SK_M4_Torso_Modern_Levi that overlaps
        // the visible Levi shirt and produces a torn-collar artefact. The
        // model registry's `hideMaterials` array lists case-insensitive
        // material-name substrings to hide. We set `mesh.visible = false`
        // (NOT remove from the scene graph) so the underlying skeleton +
        // morph targets remain available for any face primitives sharing
        // a morph dictionary on the same parent mesh.
        const hideMaterialsConfig = (() => {
          const cfg = getModelByUrl(avatar_url);
          if (!cfg?.hideMaterials || cfg.hideMaterials.length === 0) return null;
          return cfg.hideMaterials.map(s => s.toLowerCase());
        })();
        if (hideMaterialsConfig) {
          let hiddenCount = 0;
          gltf.scene.traverse((node: any) => {
            if (!node || !node.material) return;
            // Three.js can collapse multi-primitive meshes into either a
            // single mesh with a Material[] or one mesh per primitive. Handle
            // both cases.
            const materials: any[] = Array.isArray(node.material) ? node.material : [node.material];
            const matchedMaterialNames = materials
              .map(m => (m?.name ? String(m.name).toLowerCase() : ''))
              .filter(name => name && hideMaterialsConfig.some(needle => name.includes(needle)));
            if (matchedMaterialNames.length > 0) {
              node.visible = false;
              hiddenCount++;
              console.log(`👕 [hideMaterials] Hid mesh "${node.name}" (material(s): ${matchedMaterialNames.join(', ')})`);
            }
          });
          if (hiddenCount === 0) {
            console.warn(`⚠️ [hideMaterials] hideMaterials configured (${hideMaterialsConfig.join(', ')}) but no matching mesh found on this GLB`);
          }
        }

        // Track best body mesh candidate across the traverse (for embedded texture models)
        // Using 'any' for node because TS can't narrow through traverse() closure
        let bestEmbeddedBodyMesh: { node: any; count: number } | null = null as { node: any; count: number } | null;
        // Track best teeth mesh candidate across the traverse. The previous
        // implementation called setMorphTargetDictionaryLowerTeeth on every
        // matching mesh — React state doesn't update mid-traverse so the
        // LAST teeth mesh in the scene-graph order accidentally won. On
        // models that ship a tongue mesh after the teeth (e.g. Nicola =
        // f4-shay.glb) the tongue dictionary became the lip-sync target;
        // jaw blendshapes hit nothing and the visemes silently froze.
        // Mirror the body-mesh "best by morph count" logic with a tie-
        // breaker that prefers a mesh whose name contains "lower" (the
        // lower-jaw teeth carry the active jaw morphs in every model
        // we've shipped — upper teeth are typically static or follow
        // the upper-jaw rest pose). Slice 2, May 11 2026.
        let bestEmbeddedTeethMesh: { node: any; count: number; nameLower: string; isLower: boolean; isTongueOnly: boolean } | null = null as
          | { node: any; count: number; nameLower: string; isLower: boolean; isTongueOnly: boolean }
          | null;
        gltf.scene.traverse(node => {
          const isMeshLikeNode = Boolean((node as any).isMesh || (node as any).isSkinnedMesh || (node as any).isLineSegments);
          if (isMeshLikeNode) {
            node.castShadow = true;
            node.receiveShadow = true;
            node.frustumCulled = false;

            // === EMBEDDED TEXTURE MODE ===
            // For models with baked-in textures (e.g. Annika, GymGirls, Tia, M4):
            // Keep ALL original materials untouched, just find morph targets for animation
            if (useEmbeddedTextures) {
              // Track morph target meshes -- actual selection happens after traverse
              if (((node as any).isSkinnedMesh || (node as any).isMesh) && node.morphTargetDictionary) {
                const morphCount = Object.keys(node.morphTargetDictionary).length;
                const nameLower = node.name.toLowerCase();

                // Teeth/tongue mesh: identified by name. We treat tongue
                // and teeth together because some GLBs use one combined
                // mesh; the tie-breaker below de-prioritises tongue-only
                // meshes when a real teeth mesh exists.
                const isTeethMesh = nameLower.includes('teeth') || nameLower.includes('tongue');
                if (isTeethMesh) {
                  const isLower = nameLower.includes('lower') || nameLower.endsWith('_l') || nameLower.endsWith('_lo');
                  const isTongueOnly = nameLower.includes('tongue') && !nameLower.includes('teeth');
                  // Replace the candidate when:
                  //   1. no candidate yet, OR
                  //   2. current candidate is tongue-only and this one isn't, OR
                  //   3. same tongue-vs-teeth class but this one has more morphs, OR
                  //   4. counts within ~5%, current isn't lower-jaw and this one is.
                  const shouldReplace = (() => {
                    if (!bestEmbeddedTeethMesh) return true;
                    if (bestEmbeddedTeethMesh.isTongueOnly && !isTongueOnly) return true;
                    if (!bestEmbeddedTeethMesh.isTongueOnly && isTongueOnly) return false;
                    if (morphCount > bestEmbeddedTeethMesh.count) return true;
                    if (morphCount >= bestEmbeddedTeethMesh.count * 0.95
                        && !bestEmbeddedTeethMesh.isLower
                        && isLower) {
                      return true;
                    }
                    return false;
                  })();
                  if (shouldReplace) {
                    bestEmbeddedTeethMesh = { node, count: morphCount, nameLower, isLower, isTongueOnly };
                  }
                }

                // Body/face mesh candidate: prefer mesh with MOST morph targets (>30)
                // This ensures we pick the head mesh (with all ARKit blendshapes)
                // over a body mesh that might have fewer morphs
                if (!isTeethMesh && morphCount > 30) {
                  if (!bestEmbeddedBodyMesh || morphCount > bestEmbeddedBodyMesh.count) {
                    bestEmbeddedBodyMesh = { node, count: morphCount };
                  }
                }
              }

              // Log material state for diagnostics (detect white mannequin issue)
              if (node.material && ((node as any).isMesh || (node as any).isSkinnedMesh)) {
                const mat = node.material as any;
                const hasMap = !!mat.map;
                const hasColor = !!mat.color;
                const isWhite = hasColor && mat.color.r > 0.9 && mat.color.g > 0.9 && mat.color.b > 0.9;
                const meshNameLower = node.name.toLowerCase();
                if (!hasMap && isWhite && !meshNameLower.includes('teeth') && !meshNameLower.includes('eye')) {
                  console.warn(`⚠️ [Embedded] Mesh "${node.name}" has no texture map and white color -- may appear as mannequin. Material: ${mat.name || 'unnamed'}`);
                }
              }

              // Slice 22 — Patch hair materials in-place. F4 hair ships
              // with GLTF alphaMode=BLEND + baseColorFactor[3] > 1 from
              // the Blender export pipeline, which produces wispy /
              // strandy / blocky cards. The helper flips them to
              // alpha-test and clamps the alpha factor. No-op for M4
              // hair (already alphaMode=MASK) and for non-hair
              // translucent layers (eyelashes, eye fluid, occlusion).
              patchHairMaterial(node);
              sanitizeTeethMaterialsOnNode(node);
              zeroCcBasisMorphs(node);
              if ((node as { isMesh?: boolean }).isMesh || (node as { isSkinnedMesh?: boolean }).isSkinnedMesh) {
                ensureMirroredSmileMorph(node as THREE.Mesh);
              }

              // Skip all material replacement -- keep model's own baked textures
              // Continue to next node (don't fall through to Default/Shenya material code)
              return;
            }

            // === LEGACY: Apply Default avatar's textures to non-default models (e.g., Shenya) ===
            // Shenya material mapping:
            // - SK_Shenya_Fullbody → MI_Shenya_Fullbody (BODY)
            // - SK_Shenya_Fullbody_1 → MI_Shenya_Eyes (EYES)
            // - SK_Shenya_Fullbody_2 → MI_Shenya_Eyes_Shadow (keep)
            // - SK_Shenya_Fullbody_3 → MI_Shenya_Eyebrowlashes (keep)
            // - SK_Shenya_Fullbody_4 → MI_TeethTongue (TEETH)
            if (useDefaultMaterials && node instanceof THREE.Mesh) {
              const materialName = (node.material as any)?.name || '';
              const nodeName = node.name || '';

              // Apply solid skin material to MI_Shenya_Fullbody (UVs don't match Default avatar)
              if (materialName === 'MI_Shenya_Fullbody') {
                console.log('🎨 Applying solid skin material to:', nodeName, '(material:', materialName, ')');
                // Use solid skin color since Shenya's UVs don't match the Default avatar texture
                node.material = new MeshPhysicalMaterial({
                  color: new THREE.Color(0.88, 0.68, 0.55), // Natural skin tone matching Default avatar
                  roughness: 0.5,
                  metalness: 0.0,
                  envMapIntensity: 0.55,
                  clearcoat: 0.1,
                  clearcoatRoughness: 0.4,
                });

                // Capture morph targets for blendshapes
                if (node.morphTargetDictionary && Object.keys(node.morphTargetDictionary).length > 10) {
                  console.log('Found Shenya body mesh with', Object.keys(node.morphTargetDictionary).length, 'morph targets, mesh name:', node.name);
                  console.log('🎯 Shenya morph target names (first 20):', Object.keys(node.morphTargetDictionary).slice(0, 20));
                  console.log('🎯 Shenya morph target names (all):', Object.keys(node.morphTargetDictionary));
                  setMorphTargetDictionaryBody(node.morphTargetDictionary);
                  setBodyMeshName(node.name); // Store actual mesh name for animation tracks
                }
              }

              // Apply eyes texture to MI_Shenya_Eyes material
              else if (materialName === 'MI_Shenya_Eyes') {
                console.log('🎨 Applying Default avatar EYES texture to:', nodeName, '(material:', materialName, ')');
                node.material = new MeshStandardMaterial();
                node.material.map = eyesTexture;
                node.material.roughness = 0.1;
                node.material.envMapIntensity = 0.5;
              }

              // Keep Shenya's original teeth/tongue texture (UVs don't match Default avatar)
              else if (materialName === 'MI_TeethTongue') {
                console.log('🎨 Keeping Shenya original TEETH texture for:', nodeName, '(material:', materialName, ')');
                // Don't replace - Shenya's teeth texture works correctly with its own UVs
                if (node.morphTargetDictionary) {
                  setMorphTargetDictionaryLowerTeeth(node.morphTargetDictionary);
                }
              }

              // Keep eye shadow and eyebrows/lashes as-is (they look okay)
              else if (materialName === 'MI_Shenya_Eyes_Shadow' || materialName === 'MI_Shenya_Eyebrowlashes') {
                console.log('🎨 Keeping original material for:', nodeName, '(material:', materialName, ')');
                // Don't modify - keep original
              }

              // Log any other materials
              else if (nodeName.startsWith('SK_Shenya')) {
                console.log('🎨 Unknown Shenya material:', materialName, 'on', nodeName);
              }
            }

            // Detect body mesh by name OR by being a SkinnedMesh with morph targets (for Blender-edited models)
            const isBodyMesh = !useDefaultMaterials && (node.name.includes("Body") ||
              node.name.includes("HG_Body") ||
              node.name === "Female_Body_Default" ||
              (node instanceof THREE.SkinnedMesh &&
                node.morphTargetDictionary &&
                Object.keys(node.morphTargetDictionary).length > 50 &&
                !node.name.includes("Teeth") &&
                !node.name.includes("Eyes")));

            if (isBodyMesh) {
              node.castShadow = true;
              node.receiveShadow = true;
              node.material = new MeshPhysicalMaterial();
              node.material.map = bodyTexture;
              node.material.roughness = 1.7;
              node.material.roughnessMap = bodyRoughnessTexture;
              node.material.normalMap = bodyNormalTexture;
              node.material.normalScale = new Vector2(0.6, 0.6);
              console.log('Found Body mesh:', node.name, 'with', Object.keys(node.morphTargetDictionary || {}).length, 'morph targets');
              setMorphTargetDictionaryBody(node.morphTargetDictionary);
              setBodyMeshName(node.name); // Store actual mesh name for animation tracks
              node.material.envMapIntensity = 0.55;
              console.log('🎨 Body material created:', {
                type: node.material.type,
                roughness: node.material.roughness,
                envMapIntensity: node.material.envMapIntensity,
                hasMap: !!node.material.map
              });
            }
            if (node.name.includes("HG_Eyes")) {
              node.material = new MeshStandardMaterial();
              node.material.map = eyesTexture;
              node.material.roughness = 0.1;
              node.material.envMapIntensity = 0.5;
            }
            if (node.name.includes("eyeball")) {
              node.material = new MeshStandardMaterial();
              node.material.color.setHex(0x3366ff);
              node.material.roughness = 0.1;
              node.material.envMapIntensity = 0.5;
            }

            const nodeNameLower = node.name.toLowerCase();
            const materialNameLower = String((node.material as any)?.name || '').toLowerCase();
            const morphTargetNamesLower = Object.keys(node.morphTargetDictionary || {}).map((name) => name.toLowerCase());
            const hasTongueMorphs = morphTargetNamesLower.some((name) => name.includes('tongue'));
            const isTeethName =
              nodeNameLower.includes('teeth') ||
              nodeNameLower.includes('tooth') ||
              materialNameLower.includes('teeth') ||
              materialNameLower.includes('tooth');
            // Combined CC lower mesh (HG_TeethLower / Mesh.002) carries teeth
            // AND tongue. Three names it Mesh.002 with DefaultMaterial.002 —
            // neither string contains "teeth", so match by known name too.
            const isCombinedTeethTongueMesh =
              hasTongueMorphs && (isTeethName || isSamCombinedTeethTongueName(node.name));
            const isTeethOnlyMesh = isTeethName && !hasTongueMorphs && !/(tongue|gum|mouth|oral|cavity)/.test(nodeNameLower);
            const isSoftTissueMouthMesh =
              !isTeethOnlyMesh && !isCombinedTeethTongueMesh && (
                nodeNameLower.includes('tongue') ||
                nodeNameLower.includes('gum') ||
                nodeNameLower.includes('mouth') ||
                nodeNameLower.includes('oral') ||
                nodeNameLower.includes('cavity') ||
                materialNameLower.includes('tongue') ||
                materialNameLower.includes('gum') ||
                materialNameLower.includes('mouth') ||
                materialNameLower.includes('oral') ||
                materialNameLower.includes('cavity') ||
                hasTongueMorphs
              );

            if (isSoftTissueMouthMesh) {
              const existingMap = (node.material as any)?.map ?? null;
              const existingNormalMap = (node.material as any)?.normalMap ?? null;
              node.receiveShadow = false;
              node.castShadow = false;
              node.material = new MeshStandardMaterial({
                color: isLegacyModel ? MOUTH_FLESH_HEX : 0xc07c80,
                emissive: isLegacyModel ? 0x14090b : 0x2a1015,
                emissiveIntensity: isLegacyModel ? 0.1 : 0.24,
                roughness: isLegacyModel ? 0.9 : 0.82,
                metalness: 0,
                side: THREE.DoubleSide
              });
              node.material.map = existingMap;
              node.material.normalMap = existingNormalMap;
              node.material.envMapIntensity = isLegacyModel ? 0.04 : 0.12;
            } else if (isCombinedTeethTongueMesh && node instanceof THREE.Mesh) {
              node.receiveShadow = true;
              node.castShadow = true;
              if (applyVertexMaskedEnamelMaterial(node, teethNormalTexture)) {
                console.log('🦷 [Legacy] Vertex-masked enamel/flesh on combined teeth+tongue:', node.name);
              }
            } else if (isTeethOnlyMesh && node instanceof THREE.Mesh && meshHasVertexColors(node)) {
              node.receiveShadow = true;
              node.castShadow = true;
              if (applyVertexMaskedEnamelMaterial(node, teethNormalTexture)) {
                console.log('🦷 [Legacy] Vertex-masked enamel on teeth:', node.name);
              }
            } else if (isTeethOnlyMesh) {
              node.receiveShadow = true;
              node.castShadow = true;
              node.material = new MeshStandardMaterial();
              node.material.name = 'CamlinTeethEnamel';
              node.material.color.setHex(TEETH_ENAMEL_HEX);
              node.material.roughness = 0.22;
              node.material.metalness = 0;
              node.material.normalMap = teethNormalTexture;
              node.material.side = THREE.DoubleSide;
              node.material.envMapIntensity = 0.45;
            }
            if (node.name.includes("Hair")) {
              node.material = new MeshStandardMaterial();
              node.material.map = hairTexture;
              node.material.alphaMap = hairAlphaTexture;
              node.material.normalMap = hairNormalTexture;
              node.material.roughnessMap = hairRoughnessTexture;
              node.material.transparent = true;
              node.material.depthWrite = false;
              node.material.side = 2;
              node.material.color.setHex(0xFFA07A);
              node.material.envMapIntensity = 0.2;
            }
            if (node.name.includes("TSHIRT")) {
              node.material = new MeshStandardMaterial();
              node.material.map = tshirtDiffuseTexture;
              node.material.roughnessMap = tshirtRoughnessTexture;
              node.material.normalMap = tshirtNormalTexture;
              node.material.color.setHex(0x9370DB);
              node.material.envMapIntensity = 0.35;
            }

            // Detect lower teeth mesh - check various naming conventions
            // Can be SkinnedMesh OR regular Mesh with morph targets
            const isTeethLowerMesh = (node instanceof THREE.SkinnedMesh || node instanceof THREE.Mesh) &&
              (node.name.includes("TeethLower") ||
                node.name.includes("Teeth_Lower") ||
                node.name.includes("teeth_lower") ||
                node.name === "HG_TeethLower" ||
                node.name === "Mesh002" ||
                node.name === "Mesh.002");

            if (isTeethLowerMesh && (node as THREE.Mesh).morphTargetDictionary) {
              console.log('Found TeethLower mesh:', node.name, 'with', Object.keys((node as THREE.Mesh).morphTargetDictionary!).length, 'morph targets');
              setMorphTargetDictionaryLowerTeeth((node as THREE.Mesh).morphTargetDictionary);
            } else if (isTeethLowerMesh) {
              console.log('Found TeethLower mesh:', node.name, 'but NO morph targets');
            }

            // Slice 4: combined CC lower mesh is teeth+tongue. Vertex-colour
            // mask (applied above) keeps enamel on tooth verts and flesh on
            // tongue/cavity. Do not flatten the whole mesh to enamel.
          }

          // Eye-bone detection used to miss the UE5 / MetaHuman naming
          // convention (`LeftEye`, `RightEye`, `Eye_L`, `Eye_R`), which
          // silently disabled `setupVoiceNonSpeechAnimation` for every
          // M4/F4/GymGirl avatar in the registry — because the setup
          // effect early-returns when either eye ref is null, and the
          // controller stays null, and `setIsListening`/`setIsThinking`
          // become no-ops on those models. Net effect: no listening or
          // thinking animations on Visual IVR, Live, or NativeLiveAvatar.
          // Match both the legacy Daz3D names and the UE5 names now.
          const lowerName = node.name.toLowerCase();
          const isBoneNode = Boolean((node as any).isBone);
          const currentHeadIsBone = Boolean((headRef.current as any)?.isBone);
          const currentNeckIsBone = Boolean((neckRef.current as any)?.isBone);
          if ((lowerName === "head" || lowerName.endsWith(":head")) && (isBoneNode || !currentHeadIsBone)) {
            headRef.current = node;
          }
          if (
            (lowerName === "neck" || lowerName === "neck_02" || lowerName === "neck_01" || lowerName.endsWith(":neck")) &&
            (isBoneNode || !currentNeckIsBone)
          ) {
            neckRef.current = node;
          }
          const isLeftEye =
            node.name === "eyeballL"
            || lowerName.includes("eyeballl")
            || lowerName === "lefteye"
            || lowerName.endsWith("lefteye")
            || lowerName === "eye_l"
            || lowerName.endsWith("eye_l")
            || lowerName.includes("left_eye");
          // Slice 14 (May 15 2026): reject eye-bone refs whose REST pose
          // would gimbal-lock when listening / thinking / voiceNonSpeech
          // animations reset rotation.z to 0. Those animations capture
          // bone.rotation.x/.y at start (Euler XYZ decomposition), then
          // periodically write rotation.set(x, y, 0) — assuming z=0 in
          // the rest pose. That assumption holds for Mixamo-style rigs
          // (Cara has NO eye bones at all → animation skips silently and
          // the iris stays correctly forward-facing) but FAILS on the
          // GymGirl family (Blake / Sienna / Petra / Kya) whose eye_l
          // bone has rest quaternion (0.4942, 0.5057, 0.5057, 0.4942)
          // → Euler XYZ (-89.83°, 88.52°, -180°). Forcing z=0 produces a
          // quaternion 180° flipped from rest → the avatar's left eye
          // (viewer-right) appears turned-IN with darkness around the
          // eyeball, exactly the symptom reported. We detect this at
          // capture time: if decoded |z| > 5° (well above floating-
          // point precision drift and well below any plausibly-correct
          // bone-local Z component for an eye), refuse the ref and
          // silently fall back to "no eye animation" (Cara behaviour).
          const eyeRefRestSafeForZeroZ = (n: any): boolean => {
            const q = (n && n.quaternion) as THREE.Quaternion | undefined;
            if (!q) return false;
            const e = new THREE.Euler().setFromQuaternion(q, 'XYZ');
            return Math.abs(e.z) < (5 * Math.PI) / 180;
          };
          if (isLeftEye) {
            if (eyeRefRestSafeForZeroZ(node)) {
              leftEyeRef.current = node;
            } else if (!leftEyeRef.current) {
              console.debug(
                `[Avatar] Skipping eye-bone ref '${node.name}' — rest pose would gimbal-lock when animations reset rotation.z (Slice 14).`
              );
            }
          }
          const isRightEye =
            node.name === "eyeballR"
            || lowerName.includes("eyeballr")
            || lowerName === "righteye"
            || lowerName.endsWith("righteye")
            || lowerName === "eye_r"
            || lowerName.endsWith("eye_r")
            || lowerName.includes("right_eye");
          if (isRightEye) {
            if (eyeRefRestSafeForZeroZ(node)) {
              rightEyeRef.current = node;
            } else if (!rightEyeRef.current) {
              console.debug(
                `[Avatar] Skipping eye-bone ref '${node.name}' — rest pose would gimbal-lock when animations reset rotation.z (Slice 14).`
              );
            }
          }
        });

        mixerRef.current = new AnimationMixer(gltf.scene);
        gltf.scene.traverse((node: any) => zeroCcBasisMorphs(node));

        // Find and cache skeleton for bone-based face customization
        gltf.scene.traverse((node: any) => {
          if (node instanceof THREE.SkinnedMesh && node.skeleton && !skeletonRef.current) {
            skeletonRef.current = node.skeleton;
            cacheOriginalTransforms(node.skeleton);
            console.log(`🦴 Skeleton cached: ${node.skeleton.bones.length} bones`);
          }
        });

        // After traverse: select the best body mesh for embedded texture models
        // This picks the mesh with the MOST morph targets (ensures we get the head/face mesh
        // even if another mesh with some morphs was traversed first)
        if (bestEmbeddedBodyMesh && !morphTargetDictionaryBody) {
          const morphDict = bestEmbeddedBodyMesh.node.morphTargetDictionary;
          const morphNames = Object.keys(morphDict);
          const arkitLike = morphNames.filter((n: string) => n.includes('jaw') || n.includes('mouth') || n.includes('eye') || n.includes('brow'));
          console.log(`🎭 [Embedded] Body mesh: ${bestEmbeddedBodyMesh.node.name} (${bestEmbeddedBodyMesh.count} morphs, ${arkitLike.length} ARKit-like)`);

          setMorphTargetDictionaryBody(morphDict);
          setBodyMeshName(bestEmbeddedBodyMesh.node.name);
        }

        // After traverse: select the best teeth mesh for embedded texture
        // models. See `bestEmbeddedTeethMesh` declaration above for the
        // tie-break rules. Slice 2, May 11 2026.
        if (bestEmbeddedTeethMesh && !morphTargetDictionaryLowerTeeth) {
          const teethMorphDict = bestEmbeddedTeethMesh.node.morphTargetDictionary;
          const jawMorphCount = Object.keys(teethMorphDict).filter(
            (n) => n.toLowerCase().includes('jaw') || n.toLowerCase().includes('mouth')
          ).length;
          console.log(
            `🦷 [Embedded] Teeth mesh: ${bestEmbeddedTeethMesh.node.name} (${bestEmbeddedTeethMesh.count} morphs, ${jawMorphCount} jaw/mouth-related, isLower=${bestEmbeddedTeethMesh.isLower}, isTongueOnly=${bestEmbeddedTeethMesh.isTongueOnly})`
          );
          if (bestEmbeddedTeethMesh.isTongueOnly) {
            console.warn(
              `⚠️ [Embedded] Only a tongue-class teeth mesh was found on this model — jaw blendshapes may not animate. Check the GLB for a separate teeth mesh.`
            );
          }
          setMorphTargetDictionaryLowerTeeth(teethMorphDict);
          setTeethMeshName(bestEmbeddedTeethMesh.node.name);
        }

        setIsModelLoaded(true);
        console.log('Model initialization complete. State:', {
          hasTeethMorph: !!morphTargetDictionaryLowerTeeth,
          hasBodyMorph: !!morphTargetDictionaryBody,
          hasMixer: !!mixerRef.current,
          hasSkeleton: !!skeletonRef.current,
          bodyMeshName: bestEmbeddedBodyMesh?.node?.name || bodyMeshName,
          isModelLoaded: true
        });

        // Dispatch custom event to notify parent components that model is TRULY ready
        // This fires AFTER all state updates, so morphTargetDictionary should be set
        // CamlinAvatar listens for this to trigger the onReady callback
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('avatar:modelReady', {
            detail: {
              hasMixer: !!mixerRef.current,
              hasBodyMorph: true, // We know it's set because we just set it
              hasSkeleton: !!skeletonRef.current
            }
          }));
          console.log('🎬 Dispatched avatar:modelReady event');
        }, 0); // Use setTimeout to ensure state updates have processed
      } catch (error) {
        console.error('Model init error:', error);
      }
    }
    // NOTE: Removed morphTargetDictionaryBody and morphTargetDictionaryLowerTeeth from dependencies
    // because they are SET inside this effect, causing a circular dependency and double initialization
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gltf, bodyNormalTexture, bodyRoughnessTexture, bodyTexture, eyesTexture, hairAlphaTexture, hairNormalTexture, hairRoughnessTexture, hairTexture, teethNormalTexture, teethTexture, tshirtDiffuseTexture, tshirtNormalTexture, tshirtRoughnessTexture, useDefaultMaterials, useEmbeddedTextures]);

  // ---------------------------------------------------------------------------
  // Shared appearance application logic
  // Used by both the Avatar Editor (config:update events) and model defaults on load.
  // ---------------------------------------------------------------------------
  const normalizeAppearance = useCallback((appearance: Record<string, string | undefined>) => {
    const normalized = {
      ...appearance,
    };
    // Backwards-compatible aliases from older editor/runtime shapes.
    if (!normalized.clothingColor) {
      normalized.clothingColor = appearance.shirtColor || appearance.tshirtColor || appearance.tShirtColor;
    }
    if (!normalized.skinColor) {
      normalized.skinColor = appearance.skinTone;
    }
    return normalized;
  }, []);

  const applyAppearanceToScene = useCallback(async (appearance: Record<string, string | undefined>) => {
    const normalizedAppearance = normalizeAppearance(appearance);
    if (!gltf?.scene || Object.keys(normalizedAppearance).length === 0) return;

    console.log('⚙️ Applying appearance changes:', normalizedAppearance);

    const foundMeshes: string[] = [];
    const foundEyeNodes: string[] = [];
    const skippedEyeNodes: string[] = [];
    const allNodes: string[] = [];
    const eyeTextureTargets: Array<{ node: any; material: any }> = [];

    const forEachMaterial = (material: any, fn: (mat: any) => void) => {
      const materials = Array.isArray(material) ? material : [material];
      for (const mat of materials) {
        if (mat) fn(mat);
      }
    };

    const applyTint = (material: any, color: THREE.Color, options?: { strength?: number; emissiveStrength?: number }) => {
      forEachMaterial(material, (mat) => {
        if (!mat.color) mat.color = new THREE.Color(0xffffff);
        // For embedded-texture GLBs, material.color is multiplied by a baked
        // texture. Set the tint strongly and add a tiny emissive lift so dark
        // baked hair/clothing (Arc/Sienna class models) visibly changes while
        // retaining the original texture detail.
        mat.color.set(0xffffff).lerp(color, options?.strength ?? 1);
        if ('emissive' in mat && color && (options?.emissiveStrength ?? 0) > 0) {
          mat.emissive = (mat.emissive || new THREE.Color(0x000000)).copy(color);
          mat.emissiveIntensity = options?.emissiveStrength;
        }
        mat.needsUpdate = true;
      });
    };

    gltf.scene.traverse((node: any) => {
      const nodeLower = node.name.toLowerCase();
      const nodeMatName = node.material?.name?.toLowerCase() || '';

      // Diagnostic: log ALL nodes with materials (helps identify correct mesh/material names)
      if (node.material) {
        allNodes.push(`${node.name} [mat: ${node.material.name || 'unnamed'}]`);
      }

      // --- Eye / Iris color ---
      // Arc: eyeballL / eyeballR are iris-only nodes (sclera is separate HG_Eyes mesh) → direct color
      // Xandra F4/M4: Single eye material covers sclera+iris → use overlay disc on eye bone
      // Exclude: eyelashes, eye occlusion, eye fluid/moisture, eyebrows

      const isArcIris = (
        node.name === 'eyeballL' || node.name === 'eyeballR' ||
        nodeLower.includes('iris') || nodeMatName.includes('iris')
      ) && !nodeLower.includes('hg_eyes') && !nodeLower.includes('lookat');

      if (isArcIris && node.material && normalizedAppearance.eyeColor) {
        foundEyeNodes.push(`${node.name} (mat: ${node.material.name || 'unnamed'})`);
        const colorHex = parseInt(normalizedAppearance.eyeColor.replace('#', ''), 16);
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        for (const mat of materials) {
          if (mat.color) mat.color.setHex(colorHex);
          else mat.color = new THREE.Color(colorHex);
          mat.needsUpdate = true;
        }
        console.log('👁️ Arc iris color set on', node.name, ':', normalizedAppearance.eyeColor);
      } else if (nodeLower.includes('eye') && node.material && normalizedAppearance.eyeColor) {
        skippedEyeNodes.push(`${node.name} [mat: ${node.material.name || 'unnamed'}]`);
      }

      // Handle regular mesh nodes
      if (node.isMesh && node.material) {
        foundMeshes.push(`${node.name} [mat: ${node.material.name || 'unnamed'}]`);

        const meshLower = node.name.toLowerCase();
        const matName = node.material.name?.toLowerCase() || '';
        const matchesMeshOrMat = (pattern: string) =>
          meshLower.includes(pattern) || matName.includes(pattern);

        // --- Clothing color tinting ---
        // Arc: HG_TSHIRT_Female001 | Xandra: Torso/Levi/Ryan/Joel/Jumper/Jacket/Dress etc.
        // F4/M4 UE5 meshes: SK_F4_Torso_*, SK_M4_Torso_*, blouse, top, outfit
        const isClothing = matchesMeshOrMat('tshirt') || matchesMeshOrMat('t_shirt') ||
          matchesMeshOrMat('torso') || matchesMeshOrMat('levi') || matchesMeshOrMat('ryan') ||
          matchesMeshOrMat('joel') || matchesMeshOrMat('jumper') || matchesMeshOrMat('jacket') ||
          matchesMeshOrMat('dress') || matchesMeshOrMat('kiera') || matchesMeshOrMat('naomi') ||
          matchesMeshOrMat('shay') || matchesMeshOrMat('nadia') ||
          matchesMeshOrMat('blouse') || matchesMeshOrMat('top') || matchesMeshOrMat('outfit') ||
          matchesMeshOrMat('shirt') || matchesMeshOrMat('sweater') || matchesMeshOrMat('hoodie') ||
          matchesMeshOrMat('short') || matchesMeshOrMat('pants') || matchesMeshOrMat('legging') ||
          matchesMeshOrMat('sports') || matchesMeshOrMat('tracksuit') || matchesMeshOrMat('cloth') ||
          matchesMeshOrMat('fabric') || matchesMeshOrMat('jersey');
        // Guard: never tint a skin material as clothing. Cara's legacy
        // mesh `F4_Torso` carries the nude body skin material
        // `MI_X3D_Skin_Body_Nude_F4` — the mesh name matches the
        // "torso" clothing pattern, but the material IS body skin.
        // Without this guard, `clothingColor` paints her chest /
        // shoulders charcoal (Keith's "black body" regression,
        // May 17 2026). The material-name check is the authoritative
        // skin signal in our pipeline (every skin material we ship
        // is named with `skin`, `body`, or `nude`).
        const matIsSkin = matName.includes('skin') || matName.includes('body') ||
          matName.includes('nude');
        if (isClothing && !matIsSkin && normalizedAppearance.clothingColor) {
          const colorHex = parseInt(normalizedAppearance.clothingColor.replace('#', ''), 16);
          applyTint(node.material, new THREE.Color(colorHex), { strength: 1, emissiveStrength: 0.03 });
        }

        // Note (May 17 2026): the Arc-specific runtime polygon-offset /
        // FrontSide patches on the layered Levi outfit were attempted
        // here and reverted. They didn't cleanly resolve the jagged
        // collar; this needs an upstream Blender re-export of
        // `SK_M4_Torso_Modern_Levi` (split the two layers into separate
        // sub-meshes with a small geometric gap, OR delete the inner
        // t-shirt primitive if the V-neck is meant to show skin). Until
        // that lands upstream, Arc renders with both shirt layers
        // coplanar — visible at the collar but acceptable while the
        // asset is being re-baked.

        // --- Hair color ---
        // Arc: Hair | Xandra: SK_M4_Hair, SK_F4_Hair, MI_X3D_Hair
        const isHair = (
          matchesMeshOrMat('hair') || matchesMeshOrMat('ponytail') || matchesMeshOrMat('scalp')
        ) && !matchesMeshOrMat('eyelash') && !matchesMeshOrMat('lash') && !matchesMeshOrMat('brow');
        if (isHair && normalizedAppearance.hairColor) {
          const colorHex = parseInt(normalizedAppearance.hairColor.replace('#', ''), 16);
          applyTint(node.material, new THREE.Color(colorHex), { strength: 1, emissiveStrength: 0.06 });
        }

        // --- Skin color ---
        // Arc: Body, HG_Body | Xandra: Head, Skin_Face, Skin_Body, Hands, Legs
        // F4/GymGirl: Face, Skin, Neck, Chest, etc.
        const isSkin = matchesMeshOrMat('body') || matchesMeshOrMat('hg_body') ||
          matchesMeshOrMat('skin_face') || matchesMeshOrMat('skin_body') ||
          matchesMeshOrMat('skin_m4') || matchesMeshOrMat('skin_f4') ||
          matchesMeshOrMat('skin') ||
          matchesMeshOrMat('head') || matchesMeshOrMat('face') ||
          matchesMeshOrMat('neck') || matchesMeshOrMat('chest') ||
          matchesMeshOrMat('hand') || matchesMeshOrMat('arm') || matchesMeshOrMat('leg') ||
          matchesMeshOrMat('feet') || matchesMeshOrMat('finger');
        // Exclude non-skin matches
        const isSkinExcluded = matchesMeshOrMat('hair') || matchesMeshOrMat('teeth') ||
          matchesMeshOrMat('tooth') || matchesMeshOrMat('eyeball') || matchesMeshOrMat('iris') ||
          matchesMeshOrMat('gum') || matchesMeshOrMat('lash') || matchesMeshOrMat('tongue') ||
          matchesMeshOrMat('cornea') || matchesMeshOrMat('sclera') ||
          matchesMeshOrMat('brow') || matchesMeshOrMat('eyelash');
        if (isSkin && !isSkinExcluded && normalizedAppearance.skinColor) {
          const colorHex = parseInt(normalizedAppearance.skinColor.replace('#', ''), 16);
          applyTint(node.material, new THREE.Color(colorHex), { strength: node.material.map ? 0.85 : 1 });
          console.debug('🧑 Skin tint:', node.name);
        }

        // Collect eye-material texture-swap targets for Xandra/M4/F4/GymGirl
        // models. Material names vary by source pack, so match broadly but
        // exclude lashes/brows/occluders/cornea-like transparent surfaces.
        const isEyeTextureTarget = (
          matName.includes('x3d_eye')
          || matName.includes('mi_eyes')
          || matName.includes('eye')
          || meshLower.includes('eye')
        ) && !(
          matName.includes('lash') || meshLower.includes('lash')
          || matName.includes('brow') || meshLower.includes('brow')
          || matName.includes('occlusion') || meshLower.includes('occlusion')
          || matName.includes('lookat') || meshLower.includes('lookat')
          || matName.includes('cornea') || meshLower.includes('cornea')
          || matName.includes('tear') || meshLower.includes('tear')
          || matName.includes('moisture') || meshLower.includes('moisture')
        );
        if (isEyeTextureTarget) {
          // Collect eye targets unconditionally — the eyeColor branches below
          // only consume them when an explicit color is set, but the Slice 1
          // GymGirl cornea-roughness fix (further below) needs the targets
          // regardless of whether the user has chosen an eye colour.
          forEachMaterial(node.material, (mat) => eyeTextureTargets.push({ node, material: mat }));
        }
      }
    });

    console.log(`🎨 Appearance applied: ${foundMeshes.length} meshes, ${foundEyeNodes.length} iris nodes colored`);
    if (allNodes.length > 0) {
      console.debug('📋 Scene meshes:', allNodes);
      window.dispatchEvent(new CustomEvent('avatar:scene:meshes', { detail: { meshes: allNodes } }));
    }


    // --- Eye texture swap for Xandra/M4/F4/GymGirl models ---
    // These models have a single eye material covering sclera+iris in one baked texture.
    // We swap the entire eye texture with a pre-baked variant that has the iris recoloured.
    // Arc models have separate iris nodes and are handled by the direct color set above.
    const isGymGirlModel = avatar_url.toLowerCase().includes('gymgirl');
    if (normalizedAppearance.eyeColor && !isLegacyModel && isGymGirlModel && eyeTextureTargets.length > 0) {
      // GymGirl/Blake-class models already have correctly-sized baked iris
      // and pupil details. Swapping the full Xandra eye texture makes the
      // pupil/iris read about 2x too large on those UVs, so preserve the
      // original texture and tint it instead.
      const colorHex = parseInt(normalizedAppearance.eyeColor.replace('#', ''), 16);
      const seen = new Set<any>();
      for (const target of eyeTextureTargets) {
        if (seen.has(target.material)) continue;
        seen.add(target.material);
        applyTint(target.material, new THREE.Color(colorHex), { strength: 0.45, emissiveStrength: 0.01 });
        console.log('👁️ GymGirl eye tint applied on', target.node.name);
      }
    } else if (normalizedAppearance.eyeColor && !isLegacyModel) {
      const variant = mapEyeColorToVariant(normalizedAppearance.eyeColor);
      const eyeTexUrl = resolveSharedModelLibraryAssetUrl(`/models/eyes/xandra/eye_${variant}.png`);
      try {
        const loader = new THREE.TextureLoader();
        const texture = await loader.loadAsync(eyeTexUrl);
        texture.flipY = false; // GLB textures use non-flipped UVs
        texture.colorSpace = THREE.SRGBColorSpace;
        const seen = new Set<any>();
        for (const target of eyeTextureTargets) {
          if (seen.has(target.material)) continue;
          seen.add(target.material);
          target.material.map = texture;
          if (target.material.color) {
            target.material.color.setHex(0xffffff);
          }
          target.material.needsUpdate = true;
          console.log('👁️ Eye texture swapped to', variant, 'on', target.node.name);
        }
      } catch (err) {
        console.warn('👁️ Failed to load eye texture variant:', eyeTexUrl, err);
      }
    } else if (normalizedAppearance.eyeColor && foundEyeNodes.length === 0 && isLegacyModel) {
      console.warn('👁️ eyeColor requested but no iris nodes found for legacy model');
    }

    // Slice 1 (May 15 2026): Cornea roughness fix for GymGirl-class models.
    //
    // Symptom: Blake (and sister GymGirl models) sometimes show a "dark right
    // eye" — one cornea looks blackened/sunken while the other reads bright.
    // Worse on certain camera angles, "intermittent" from the user's POV.
    //
    // Root cause: The shared GymGirl_Eyes material in gymgirl-*.glb ships with
    // metallic=0, roughness=0.05 — a near-mirror specular surface. Combined
    // with the avatar runtime's default `photo_studio` HDR (which is
    // intentionally asymmetric: bright softbox on one side, dark fill on the
    // other), the cornea acts as a mirror and reflects different parts of the
    // HDR per eye. The eye facing the dark side of the studio reflects
    // near-black, which the user perceives as "the eye is dark/dead". The GLB
    // itself is symmetric (verified May 15 2026 — both eyes share the single
    // GymGirl_Eyes material on Mesh.002 with mirror-symmetric eye_l/eye_r
    // node positions); the asymmetry is purely in the IBL reflection.
    //
    // Fix: Bump the cornea roughness from 0.05 → 0.30 and lower
    // envMapIntensity to 0.55. The eye still has a visible specular highlight
    // (so the avatar's gaze stays "alive") but the reflection is matte enough
    // that the asymmetric HDR no longer projects bright-vs-dark onto the
    // corneas. Both eyes share the same material instance, so this fix is
    // automatically symmetric across L/R.
    //
    // Limited to GymGirl models because (a) Xandra/M4/F4 models route through
    // the eye-texture-swap branch above which already replaces the cornea
    // material with the Xandra atlas (different roughness profile), and (b)
    // bumping roughness on legacy/Arc models would wash out their normal
    // cornea highlight which has been tuned against their own UV/material
    // setup.
    // Slice 1 v2 (May 15 2026): The v1 fix (roughness 0.30, envMapIntensity 0.55)
    // was insufficient — Blake's right eye still appeared significantly darker
    // than the left because at roughness 0.30 the IBL convolution still resolves
    // mid-frequency HDR detail asymmetrically across the two corneas. Push
    // BOTH controls harder so the cornea becomes effectively matte:
    //   - roughness 0.30 → 0.55 (averages most of the HDR sphere into a uniform gray)
    //   - envMapIntensity 0.55 → 0.20 (env contribution is now a small fraction of the lit color)
    //   - emissiveIntensity 0 → 0.05 (small constant uplift so neither eye reads as "dead")
    // Both eyes share the same material instance, so this is automatically
    // symmetric across L/R. The threshold `< 0.50` allows this fix to ratchet
    // from a previous v1 deploy (where roughness was already at 0.30) up to v2.
    if (isGymGirlModel && eyeTextureTargets.length > 0) {
      const seen = new Set<any>();
      for (const target of eyeTextureTargets) {
        if (seen.has(target.material)) continue;
        seen.add(target.material);
        const mat = target.material;
        if (typeof mat.roughness === 'number' && mat.roughness < 0.50) {
          const before = {
            roughness: mat.roughness,
            envMapIntensity: mat.envMapIntensity,
            emissiveIntensity: mat.emissiveIntensity,
          };
          mat.roughness = 0.55;
          if (typeof mat.envMapIntensity === 'number') {
            mat.envMapIntensity = Math.min(mat.envMapIntensity, 0.20);
          } else {
            mat.envMapIntensity = 0.20;
          }
          if ('emissive' in mat && 'emissiveIntensity' in mat && mat.emissive) {
            mat.emissive.setRGB(1, 1, 1);
            mat.emissiveIntensity = Math.max(mat.emissiveIntensity ?? 0, 0.05);
          }
          mat.needsUpdate = true;
          console.log('👁️ GymGirl cornea v2 (matte+emissive) on', target.node.name,
            'before:', JSON.stringify(before),
            'after:', JSON.stringify({
              roughness: mat.roughness,
              envMapIntensity: mat.envMapIntensity,
              emissiveIntensity: mat.emissiveIntensity,
            }));
        }
      }
    }
  }, [gltf?.scene, avatar_url, isLegacyModel, normalizeAppearance]);

  const pendingAppearanceRef = useRef<Record<string, string | undefined> | null>(null);
  const sceneReadyRef = useRef(false);
  // Track which (avatar_url) we've already run the initial-appearance
  // path for. This effect's deps include `applyAppearanceToScene` and
  // `initialAppearance`, both of which can change reference legitimately
  // (e.g. when the host re-renders). Without this URL guard the effect
  // re-applies defaults on every re-run, which would (a) waste GPU
  // upload bandwidth and (b) — combined with the avatar:appearanceApplied
  // dispatch and `onAppearanceApplied?.()` callback below — keep firing
  // appearance-applied callbacks long after first paint, blocking the
  // host's onAppearanceApplied handler from being a one-shot signal.
  const initialAppearanceAppliedForRef = useRef<string | null>(null);
  // Ref-pattern for `onAppearanceApplied` so a host that passes an
  // unstable callback (a fresh inline arrow on every render) cannot
  // turn this effect into a feedback loop. The classic React footgun:
  // host's render → new callback ref → effect re-runs because of dep
  // change → applies appearance → calls callback → host setState →
  // host re-renders → new callback ref → … ad infinitum. By stashing
  // the latest callback in a ref and removing it from the deps array
  // we get the same behaviour (always call the latest impl) without
  // the dep loop. Mirrored from the same pattern in
  // useEvent/useEventCallback proposals.
  const onAppearanceAppliedRef = useRef(onAppearanceApplied);
  useEffect(() => {
    onAppearanceAppliedRef.current = onAppearanceApplied;
  }, [onAppearanceApplied]);

  // When gltf.scene becomes available, mark ready, apply model defaults +
  // initialAppearance, then flush any queued external update on top.
  useEffect(() => {
    if (!gltf?.scene) {
      sceneReadyRef.current = false;
      return;
    }
    sceneReadyRef.current = true;

    // Idempotency guard: if we already ran the initial-appearance path
    // for this avatar_url (i.e. the effect re-ran due to a stable-but-
    // re-rendered consumer), skip the apply + dispatch. The
    // model-switch reset effect below clears this ref so a real model
    // change re-applies cleanly.
    if (initialAppearanceAppliedForRef.current === avatar_url) {
      return;
    }
    initialAppearanceAppliedForRef.current = avatar_url;

    let cancelled = false;
    const applyInitialAppearance = async () => {
      // Apply model-registry defaults first, then overlay explicit
      // initialAppearance. This direct prop path is the canonical display
      // path for all native surfaces: settings modal preview, Visual IVR,
      // Avatar Editor, and the admin portrait generator. The older global
      // event path still exists for live editor updates below, but first
      // paint no longer depends on a delayed window event.
      //
      // ALWAYS apply this merge — even when an external
      // `avatar:config:update` arrived while the model was still loading.
      // The old behaviour skipped the merge entirely in that case (a
      // boolean `externalAppearanceAppliedRef`), which made the host's
      // requested hair/clothes/eyes depend on a timing race: any early
      // event (often carrying only a couple of keys, or only background
      // fields) permanently disabled defaultAppearance + initialAppearance.
      // External updates still win — the queued external appearance is
      // layered ON TOP immediately below.
      const modelConfig = getModelByUrl(avatar_url);
      const mergedInitialAppearance = {
        ...(modelConfig?.defaultAppearance ?? {}),
        ...(initialAppearance ?? {}),
      } as Record<string, string | undefined>;
      if (Object.keys(mergedInitialAppearance).length > 0) {
        console.log(`Applying initial appearance for ${modelConfig?.name ?? avatar_url}`);
        await applyAppearanceToScene(mergedInitialAppearance);
      }

      // Layer any external appearance that arrived while the model was
      // still loading on top of the initial merge (external must win).
      if (pendingAppearanceRef.current) {
        console.log('Flushing queued appearance update');
        await applyAppearanceToScene(pendingAppearanceRef.current);
        pendingAppearanceRef.current = null;
      }

      if (cancelled) return;

      // Notify listeners that the model is ready to receive appearance updates
      window.dispatchEvent(new CustomEvent('avatar:model:ready', {
        detail: { avatarUrl: avatar_url }
      }));
      window.dispatchEvent(new CustomEvent('avatar:appearanceApplied', {
        detail: { avatarUrl: avatar_url }
      }));
      onAppearanceAppliedRef.current?.();
    };

    void applyInitialAppearance();

    return () => {
      cancelled = true;
    };
  }, [gltf?.scene, avatar_url, applyAppearanceToScene, initialAppearance]);

  // Set the per-model mood-intensity multiplier on the mood animator. Sourced
  // from `ModelConfig.moodIntensity` in `modelRegistry`. Default 1 (no change).
  // Sam's legacy default mesh ships with 0.55 because the global MOOD_STATES
  // weights were tuned against the GymGirl-class meshes; the same negative-
  // sentiment frown blendshapes that read as gentle on Cara/Blake/Sienna read
  // as exaggerated on Sam (lower lip rises over the top lip).
  // Slice 6 of the avatar-fixes plan, May 11 2026.
  useEffect(() => {
    if (!avatar_url) return;
    const modelConfig = getModelByUrl(avatar_url);
    // Same module instance as applyMoodToAvatar / tickMoodOverlay — do not
    // dynamic-import this, or omitted keys can land on a second copy of
    // moodAnimator while the overlay still plays full MOOD_STATES.
    setMoodIntensityScale(modelConfig?.moodIntensity ?? 1);
    setOmittedMoodBlendshapes(modelConfig?.omitMoodBlendshapes);
    setMoodOverrides(modelConfig?.moodOverrides ?? {});
  }, [avatar_url]);

  // Listen for appearance updates; apply immediately if scene is ready,
  // otherwise queue for when scene loads.
  useEffect(() => {
    const handleConfigUpdate = (event: CustomEvent) => {
      const payload = event.detail || {};
      if (payload.avatarInstanceId && payload.avatarInstanceId !== avatarInstanceId) {
        return;
      }
      const appearance = payload.config?.appearance || payload.appearance || {};
      if (Object.keys(appearance).length === 0) return;

      if (sceneReadyRef.current) {
        void applyAppearanceToScene(appearance).then(() => {
          window.dispatchEvent(new CustomEvent('avatar:appearanceApplied', {
            detail: { avatarUrl: avatar_url }
          }));
          onAppearanceAppliedRef.current?.();
        });
      } else {
        // Queue for the scene-ready effect above, which applies the
        // model-default + initialAppearance merge FIRST and then layers
        // this queued external appearance on top.
        console.log('Scene not ready yet, queuing appearance update');
        pendingAppearanceRef.current = { ...(pendingAppearanceRef.current || {}), ...appearance };
      }
    };

    window.addEventListener('avatar:config:update', handleConfigUpdate as EventListener);

    // Mesh visibility toggle from Mesh Explorer
    const handleMeshVisibility = (e: Event) => {
      const { name, visible } = (e as CustomEvent).detail || {};
      if (!name || !gltf?.scene) return;
      gltf.scene.traverse((node: any) => {
        if (node.isMesh && node.name === name) {
          node.visible = visible;
        }
      });
    };
    window.addEventListener('avatar:mesh:visibility', handleMeshVisibility as EventListener);

    // mouthfix1 — Face Inspect debug events (Avatar Animation Tester).
    // `avatar:mesh:visibility` above matches by NODE name, which is useless
    // for multi-primitive heads (GLTFLoader splits SK_F4_Head into
    // SK_F4_Head_1/_2/… with no layer semantics in the node name). These
    // match by MATERIAL name via MOUTH_LAYER_PATTERNS.
    const handleMouthLayer = (e: Event) => {
      const { layer, visible } = (e as CustomEvent).detail || {};
      const pattern = MOUTH_LAYER_PATTERNS[String(layer)];
      if (!pattern || !gltf?.scene) return;
      let count = 0;
      gltf.scene.traverse((node: any) => {
        if (!node.isMesh && !node.isSkinnedMesh) return;
        const mats = Array.isArray(node.material) ? node.material : [node.material];
        const matched =
          mats.some((m: any) => pattern.test(String(m?.name || ''))) || pattern.test(node.name);
        if (matched) {
          node.visible = visible !== false;
          count++;
        }
      });
      console.log(`👄 mouthfix1 layer '${layer}' → visible=${visible !== false} (${count} mesh(es))`);
    };
    window.addEventListener('avatar:debug:mouthLayer', handleMouthLayer as EventListener);

    const handleDumpMouthMeshes = () => {
      if (!gltf?.scene) return;
      const rows: Array<Record<string, unknown>> = [];
      const interesting = /teeth|tooth|tongue|gum|fluid|saliva|occlusion|lash|skin_face|mouth|enamel/i;
      gltf.scene.traverse((node: any) => {
        if (!node.isMesh && !node.isSkinnedMesh) return;
        const mats = Array.isArray(node.material) ? node.material : [node.material];
        for (const m of mats) {
          const matName = String(m?.name || '');
          if (!interesting.test(matName) && !interesting.test(String(node.name || ''))) continue;
          rows.push({
            node: node.name,
            material: matName,
            visible: node.visible,
            color: m?.color?.getHexString ? `#${m.color.getHexString()}` : '',
            hasMap: !!m?.map,
            transparent: !!m?.transparent,
            opacity: m?.opacity,
            alphaTest: m?.alphaTest ?? 0,
            depthWrite: m?.depthWrite,
            renderOrder: node.renderOrder,
            side: m?.side === THREE.DoubleSide ? 'double' : m?.side === THREE.BackSide ? 'back' : 'front',
            mouthMaskVerts: node.userData?.camlinMouthMaskCount ?? 0,
          });
        }
      });
      console.log(`👄 mouthfix1 mouth-mesh inventory — ${rows.length} row(s); also on window.__camlinMouthDump`);
      console.table(rows);
      (window as unknown as { __camlinMouthDump?: unknown }).__camlinMouthDump = rows;
    };
    window.addEventListener('avatar:debug:dumpMouthMeshes', handleDumpMouthMeshes as EventListener);

    // mouthfix3 — `avatar:debug:cavityHighlight` { enabled }. Face Inspect
    // "Show cavity mask": swap every face mesh that carries the baked
    // camlinMouthMask attribute onto an UNLIT vertex-coloured material —
    // magenta = masked (what a future fix would edit), light grey = rest.
    // Deliberately a whole-material swap with exact restore on disable:
    // NO onBeforeCompile / shader string patching (mouthfix1 leaked that
    // way). MeshBasicMaterial picks up skinning + morph targets
    // automatically, so the mask can be inspected mid-smile (celebration).
    const handleCavityHighlight = (e: Event) => {
      const { enabled } = (e as CustomEvent).detail || {};
      if (!gltf?.scene) return;
      let count = 0;
      gltf.scene.traverse((node: any) => {
        if (!node.isMesh && !node.isSkinnedMesh) return;
        const mask = node.geometry?.getAttribute?.('camlinMouthMask');
        if (!mask || Array.isArray(node.material)) return;
        if (enabled) {
          if (node.userData.camlinCavityHighlightOriginal) return; // already on
          const colors = new Float32Array(mask.count * 3);
          for (let i = 0; i < mask.count; i++) {
            const m = mask.getX(i) > 0.5;
            colors[i * 3] = m ? 1 : 0.78;
            colors[i * 3 + 1] = m ? 0 : 0.78;
            colors[i * 3 + 2] = m ? 1 : 0.78;
          }
          node.userData.camlinCavityHighlightPrevColor =
            node.geometry.getAttribute('color') ?? null;
          node.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
          node.userData.camlinCavityHighlightOriginal = node.material;
          node.material = new THREE.MeshBasicMaterial({
            name: 'CamlinCavityHighlight',
            vertexColors: true,
          });
          count++;
        } else {
          const original = node.userData.camlinCavityHighlightOriginal;
          if (!original) return;
          node.material.dispose();
          node.material = original;
          delete node.userData.camlinCavityHighlightOriginal;
          const prevColor = node.userData.camlinCavityHighlightPrevColor;
          if (prevColor) node.geometry.setAttribute('color', prevColor);
          else node.geometry.deleteAttribute('color');
          delete node.userData.camlinCavityHighlightPrevColor;
          count++;
        }
      });
      console.log(
        `👄 mouthfix3 cavity highlight → ${enabled ? 'ON (magenta = camlinMouthMask verts)' : 'off (original material restored)'} on ${count} mesh(es)`,
      );
    };
    window.addEventListener('avatar:debug:cavityHighlight', handleCavityHighlight as EventListener);

    // Slice 20A — Wardrobe equip/unequip/reset listeners.
    // The Wardrobe BETA panel dispatches:
    //   `avatar:wardrobe:equip`   { piece: WardrobeManifestPiece }
    //   `avatar:wardrobe:unequip` { slot: WardrobeSlot }
    //   `avatar:wardrobe:reset`
    // We respond by mounting/unmounting against the loaded GLB scene and
    // emitting `avatar:wardrobe:equipped` { equipped: { [slot]: pieceId } }
    // back so the panel can render its current-state badge.
    const dispatchEquippedState = () => {
      if (!gltf?.scene) return;
      window.dispatchEvent(
        new CustomEvent('avatar:wardrobe:equipped', {
          detail: { equipped: getEquippedPieces(gltf.scene) },
        }),
      );
    };
    const handleEquip = async (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const piece = detail.piece as WardrobeManifestPiece | undefined;
      if (!piece || !gltf?.scene) return;
      const baseUrl = getModelLibraryBaseUrl();
      if (!baseUrl) {
        console.warn('[wardrobe] no model-library base URL; skipping equip');
        return;
      }
      try {
        await mountWardrobePiece({
          host: gltf.scene,
          piece,
          baseUrl,
        });
        dispatchEquippedState();
      } catch (err) {
        console.error('[wardrobe] mount failed:', err);
      }
    };
    const handleUnequip = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const slot = detail.slot as WardrobeSlot | undefined;
      if (!slot || !gltf?.scene) return;
      unmountWardrobePiece(gltf.scene, slot);
      dispatchEquippedState();
    };
    const handleReset = () => {
      if (!gltf?.scene) return;
      resetWardrobeToDefault(gltf.scene);
      dispatchEquippedState();
    };
    window.addEventListener('avatar:wardrobe:equip', handleEquip as EventListener);
    window.addEventListener('avatar:wardrobe:unequip', handleUnequip as EventListener);
    window.addEventListener('avatar:wardrobe:reset', handleReset as EventListener);

    return () => {
      window.removeEventListener('avatar:config:update', handleConfigUpdate as EventListener);
      window.removeEventListener('avatar:mesh:visibility', handleMeshVisibility as EventListener);
      window.removeEventListener('avatar:debug:mouthLayer', handleMouthLayer as EventListener);
      window.removeEventListener('avatar:debug:dumpMouthMeshes', handleDumpMouthMeshes as EventListener);
      window.removeEventListener('avatar:debug:cavityHighlight', handleCavityHighlight as EventListener);
      window.removeEventListener('avatar:wardrobe:equip', handleEquip as EventListener);
      window.removeEventListener('avatar:wardrobe:unequip', handleUnequip as EventListener);
      window.removeEventListener('avatar:wardrobe:reset', handleReset as EventListener);
    };
    // onAppearanceApplied intentionally read via the ref below (see
    // the long-form comment on `onAppearanceAppliedRef` above) so a
    // host with an unstable callback cannot retrigger the listener
    // wiring on every render.
  }, [applyAppearanceToScene, avatar_url, avatarInstanceId]);

  // Reset flags when switching models (skip initial mount so we don't
  // undo the sceneReadyRef=true that the gltf.scene effect just set).
  const prevAvatarUrlRef = useRef(avatar_url);
  useEffect(() => {
    if (prevAvatarUrlRef.current === avatar_url) return;
    prevAvatarUrlRef.current = avatar_url;
    pendingAppearanceRef.current = null;
    sceneReadyRef.current = false;
    initialAppearanceAppliedForRef.current = null;
    // Slice 16-followup (May 16 2026): clear per-model caches so the next
    // GLB's bone tree / morph submeshes are rediscovered. Without this,
    // switching from Arc → Naomi would keep Arc's bone refs in
    // `m4BoneSyncRef`, leaving Naomi unsynced and her clothes / hair
    // stuck at rest.
    m4BoneSyncRef.current = null;
    m4MorphMeshesRef.current = null;
    idleNoiseOverlayRef.current.clear();
    m4BakedMorphsRef.current = null;
    m4SpeechDonorIdxRef.current = undefined;
  }, [avatar_url]);

  const stopAllAnimations = useCallback(() => {
    if (mixerRef.current) {
      // Stop all animations EXCEPT mood animations (they should overlay continuously)
      // Access _actions as private property (type assertion needed)
      const actions = (mixerRef.current as any)._actions;
      if (actions && actions.length > 0) {
        actions.forEach((action: any) => {
          // Don't stop mood animations - they need to persist as overlay
          if (!action._clip.name.startsWith('mood_')) {
            action.stop();
          }
        });
      } else {
        // Fallback if _actions is not available
        mixerRef.current.stopAllAction();
      }
    }
  }, []);

  const [isListening, setIsListening] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const isVoiceLikeMode = isLiveMode || isMobileCall || isWebrtcCall || isInVoiceCall;

  const applyVoiceNonSpeechMode = useCallback((overrides?: {
    speaking?: boolean;
    listening?: boolean;
    thinking?: boolean;
  }) => {
    if (!isVoiceLikeMode || !voiceNonSpeechAnimationRef.current) {
      return;
    }

    const speaking = overrides?.speaking ?? (isSpeakingRef.current || isVoiceCallAnimationInProgress);
    const listening = overrides?.listening ?? isListening;
    const thinking = overrides?.thinking ?? isThinking;
    const nextMode = speaking ? 'speaking' : thinking ? 'thinking' : listening ? 'listening' : 'idle';
    voiceNonSpeechAnimationRef.current.setMode(nextMode);
  }, [isListening, isThinking, isVoiceCallAnimationInProgress, isVoiceLikeMode]);

  const idleFbx = useFBX(resolveAvatarRuntimeAssetUrl('/idle.fbx'));
  const { clips: rawIdleClips } = useAnimations(idleFbx.animations);

  // Model-aware idle clip processing: detect actual bone names from the loaded model
  // and remap idle FBX tracks to match. This works across different skeletons:
  //   Arc/GymGirl: head, neck, spine2
  //   M4 (UE5 MetaHuman): head, neck_02, spine_05
  const idleClips = useMemo(() => {
    if (!rawIdleClips || !rawIdleClips[0] || !gltf?.scene) return null;

    // --- Detect model bone names ---
    // Find the first matching bone for each role (head, neck, spine)
    // Priority order: exact match first, then common variants
    const headCandidates = ['head'];
    const neckCandidates = ['neck', 'neck_02', 'neck_01'];
    const spineCandidates = ['spine2', 'spine_05', 'spine_04', 'spine_03', 'spine_02'];

    const findBone = (candidates: string[]): string | null => {
      let found: string | null = null;
      gltf.scene.traverse((node: any) => {
        if (found) return; // Already found
        if (node.isBone) {
          for (const name of candidates) {
            if (node.name === name) {
              found = name;
              return;
            }
          }
        }
      });
      return found;
    };

    const headBone = findBone(headCandidates);
    const neckBone = findBone(neckCandidates);
    const spineBone = findBone(spineCandidates);

    console.debug('[Idle] Bone mapping:', { head: headBone, neck: neckBone, spine: spineBone });

    let processedClips = rawIdleClips.map(clip => {
      const newClip = clip.clone();
      newClip.tracks = newClip.tracks.filter(track => {
        return track.name.includes("Head") || track.name.includes("Neck") || track.name.includes("Spine2");
      });
      newClip.tracks = newClip.tracks.map(track => {
        let targetBone: string | null = null;
        let dampenFactor = 0.4;

        if (track.name.includes("Head") && headBone) {
          targetBone = headBone;
          dampenFactor = 0.4; // 60% dampening towards identity
        } else if (track.name.includes("Neck") && neckBone) {
          targetBone = neckBone;
          dampenFactor = 0.3; // 70% dampening
        } else if (track.name.includes("Spine") && spineBone) {
          targetBone = spineBone;
          dampenFactor = 0.5; // 50% dampening
        }

        if (targetBone) {
          track.name = `${targetBone}.quaternion`;
          // Re-base the clip so frame 0 is identity (head/neck/spine at the
          // rig's rest pose). Without this step, the raw Mixamo idle starts
          // at a non-rest pose (~up-left head tilt for the Slice-12 Modern
          // idle clip) and the body's head bone rotates instantly on load.
          //
          // On single-skeleton rigs this is just visually startling. On
          // multi-skeleton rigs (Arc, Joel, Jason, Kiera, Naomi — hair /
          // clothes on separate duplicate skeletons) the body's head bone
          // rotates but the duplicate head bone on the shirt/hair skeleton
          // doesn't, producing the "shirt looks torn off the collar" visual
          // the user reported on Arc (May 17 2026).
          //
          // Re-basing: track_i := track_i * track_0⁻¹. Algebraically
          //   t=0:   track_0 * track_0⁻¹ = identity (rest pose, all rigs aligned)
          //   t=t_n: track_n * track_0⁻¹ = rotation from rest at frame n
          // The subsequent slerp-toward-identity by dampenFactor stays —
          // it caps the maximum offset away from rest.
          if (track.values.length >= 4) {
            const baseQ = new THREE.Quaternion(
              track.values[0],
              track.values[1],
              track.values[2],
              track.values[3],
            );
            const baseQInv = baseQ.clone().invert();
            const identity = new THREE.Quaternion();
            for (let i = 0; i < track.values.length; i += 4) {
              const quat = new THREE.Quaternion(
                track.values[i],
                track.values[i + 1],
                track.values[i + 2],
                track.values[i + 3]
              );
              quat.multiply(baseQInv);
              quat.slerp(identity, dampenFactor);
              track.values[i] = quat.x;
              track.values[i + 1] = quat.y;
              track.values[i + 2] = quat.z;
              track.values[i + 3] = quat.w;
            }
          }
        }
        return track;
      });
      // Remove tracks that didn't match any bone
      newClip.tracks = newClip.tracks.filter(track => track.name.includes('.quaternion'));
      return newClip;
    });
    return processedClips;
  }, [rawIdleClips, gltf?.scene]);

  const blinkTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Cache the natural blink clip + action across blinks so we don't
  // leak new actions into the mixer on every blink (May 17 2026 fix).
  // Pre-fix: performBlink() called `createAnimation(...)` + `mixer.clipAction(freshClip)`
  // every blink, producing a unique AnimationAction every ~1.5s. With
  // `clampWhenFinished: true` those actions sit forever in the mixer
  // and `mixer.update(delta)` walks all of them every frame. The user-
  // visible symptom is "blinks taper off and stop": as the action
  // count grows the per-frame cost spikes, frames drop, the 0.15s
  // blink curve no longer renders any frames, and worse, the
  // accumulated clamped actions write 0 to eyeBlinkLeft/Right on the
  // tail of their 10s held-flat region, summing into the same morph
  // every frame. Reusing one action eliminates both.
  const naturalBlinkClipRef = useRef<THREE.AnimationClip | null>(null);
  const naturalBlinkActionRef = useRef<THREE.AnimationAction | null>(null);
  const idleMicroActionRef = useRef<THREE.AnimationAction | null>(null);
  const breathingActionRef = useRef<THREE.AnimationAction | null>(null);
  const idleMorphWeightRestoreRef = useRef<{
    micro: number;
    breath: number;
    blink: number;
  } | null>(null);

  const muteIdleMorphActionsForGesture = useCallback(() => {
    if (!idleMorphWeightRestoreRef.current) {
      idleMorphWeightRestoreRef.current = {
        micro: idleMicroActionRef.current?.getEffectiveWeight() ?? 0,
        breath: breathingActionRef.current?.getEffectiveWeight() ?? 0,
        blink: naturalBlinkActionRef.current?.getEffectiveWeight() ?? 0,
      };
    }
    idleMicroActionRef.current?.setEffectiveWeight(0);
    breathingActionRef.current?.setEffectiveWeight(0);
    naturalBlinkActionRef.current?.setEffectiveWeight(0);
  }, []);

  const restoreIdleMorphActionsAfterGesture = useCallback(() => {
    const restore = idleMorphWeightRestoreRef.current;
    if (!restore) return;
    idleMicroActionRef.current?.setEffectiveWeight(restore.micro);
    breathingActionRef.current?.setEffectiveWeight(restore.breath);
    naturalBlinkActionRef.current?.setEffectiveWeight(restore.blink);
    idleMorphWeightRestoreRef.current = null;
  }, []);

  // Unmount cleanup for the blink timer + cached clip/action.
  // Prevents a stale setTimeout callback from firing after unmount and
  // (cosmetic) drops the cached clip from the mixer cache.
  useEffect(() => {
    return () => {
      if (blinkTimerRef.current) {
        clearTimeout(blinkTimerRef.current);
        blinkTimerRef.current = null;
      }
      if (naturalBlinkActionRef.current) {
        try { naturalBlinkActionRef.current.stop(); } catch {/* ignore */}
        naturalBlinkActionRef.current = null;
      }
      if (mixerRef.current && naturalBlinkClipRef.current) {
        try { mixerRef.current.uncacheClip(naturalBlinkClipRef.current); } catch {/* ignore */}
      }
      naturalBlinkClipRef.current = null;
    };
  }, []);

  // Initialize speech expressiveness controller when morph dictionary is ready
  useEffect(() => {
    if (!morphTargetDictionaryBody || !gltf?.scene) {
      speechExpressivenessRef.current = null;
      return;
    }
    const ctrl = new SpeechExpressivenessController(morphTargetDictionaryBody);
    // Find the primary body mesh for the expressiveness overlay. Use
    // Three's structural flags instead of instanceof because host apps can
    // bundle multiple Three copies.
    gltf.scene.traverse((node: any) => {
      if ((node.isSkinnedMesh || node.isMesh) && node.morphTargetInfluences &&
          node.morphTargetInfluences.length > 10 && !ctrl['mesh']) {
        ctrl.setMesh(node);
      }
    });
    speechExpressivenessRef.current = ctrl;
  }, [morphTargetDictionaryBody, gltf?.scene]);

  // Sync speaking state to expressiveness controller
  useEffect(() => {
    speechExpressivenessRef.current?.setSpeaking(isSpeakingRef.current);
  });

  // Animation config from editor (configurable via avatar:config:update with section='animations')
  const animationConfigRef = useRef({
    blink: { enabled: true, frequency: 5, duration: 150 },
    breathing: { enabled: true, rate: 0.25, intensity: 0.4 },
    idle: { enabled: true, intensity: 0.3, speed: 0.2 },
    listening: { enabled: true, nodFrequency: 3, tiltAmount: 0.045 },
    thinking: { enabled: true, eyeSearchIntensity: 0.5 },
  });
  // Personality (Aug 14 2026): the UNSCALED editor values live here; the
  // effective config in `animationConfigRef` is always recomputed as
  // base × personality scales, so re-applying a personality (or editor
  // update) can never compound multipliers.
  const baseAnimationConfigRef = useRef({
    blink: { enabled: true, frequency: 5, duration: 150 },
    breathing: { enabled: true, rate: 0.25, intensity: 0.4 },
    idle: { enabled: true, intensity: 0.3, speed: 0.2 },
    listening: { enabled: true, nodFrequency: 3, tiltAmount: 0.045 },
    thinking: { enabled: true, eyeSearchIntensity: 0.5 },
  });
  // Resolved personality knobs (null = no personality active). Read by
  // the gesture handler for the allow-list / frequency / amplitude policy.
  const personalityKnobsRef = useRef<ResolvedPersonalityKnobs | null>(null);

  useEffect(() => {
    if (!isVoiceLikeMode) {
      if (voiceNonSpeechAnimationRef.current) {
        voiceNonSpeechAnimationRef.current.cleanup();
        voiceNonSpeechAnimationRef.current = null;
      }
      return;
    }

    if (!isModelLoaded || !headRef.current || !neckRef.current) {
      return;
    }

    // Find the primary face/body mesh for morph overlay support. Use
    // structural flags instead of instanceof because embedded hosts can
    // bundle a different Three copy than the SDK.
    let primaryMorphMesh: any = null;
    let primaryMorphCount = 0;
    if (gltf?.scene) {
      gltf.scene.traverse((node: any) => {
        if (!((node.isSkinnedMesh || node.isMesh) && node.morphTargetInfluences && node.morphTargetDictionary)) {
          return;
        }

        const morphKeys = Object.keys(node.morphTargetDictionary);
        const hasNamedFacialTargets = morphKeys.some((morphKey) => {
          const key = morphKey.toLowerCase();
          return key.includes('jaw') ||
            key.includes('mouth') ||
            key.includes('tongue') ||
            key.includes('cheek') ||
            key.includes('brow') ||
            key.includes('eye') ||
            key.includes('nose');
        });
        const isPrimaryMesh = node.name === bodyMeshName;
        if ((isPrimaryMesh || hasNamedFacialTargets) && morphKeys.length > primaryMorphCount) {
          primaryMorphMesh = node;
          primaryMorphCount = morphKeys.length;
        }
      });
    }

    const controller = setupVoiceNonSpeechAnimation(
      headRef as any,
      neckRef as any,
      leftEyeRef.current ? leftEyeRef as any : undefined,
      rightEyeRef.current ? rightEyeRef as any : undefined,
      primaryMorphMesh,
      morphTargetDictionaryBody,
    );
    voiceNonSpeechAnimationRef.current = controller;
    controller?.setMode('idle');

    return () => {
      if (voiceNonSpeechAnimationRef.current === controller) {
        controller?.cleanup();
        voiceNonSpeechAnimationRef.current = null;
      }
    };
  }, [bodyMeshName, gltf?.scene, isModelLoaded, isVoiceLikeMode, morphTargetDictionaryBody]);

  useEffect(() => {
    applyVoiceNonSpeechMode();
  }, [applyVoiceNonSpeechMode]);

  const setupNaturalBlinking = useCallback(() => {
    if (!morphTargetDictionaryBody || !mixerRef.current) {
      console.warn('Cannot setup natural blinking - missing dependencies');
      return;
    }

    if (blinkTimerRef.current) {
      clearTimeout(blinkTimerRef.current);
    }

    // The morph dictionary may have changed (model swap, wardrobe equip
    // that rebound the body mesh) — drop any previously-cached clip and
    // action so we don't sample stale morph indices.
    if (naturalBlinkActionRef.current) {
      try {
        naturalBlinkActionRef.current.stop();
      } catch {/* mixer may be torn down */}
    }
    if (naturalBlinkClipRef.current && mixerRef.current) {
      try {
        mixerRef.current.uncacheClip(naturalBlinkClipRef.current);
      } catch {/* mixer may be torn down */}
    }
    naturalBlinkActionRef.current = null;
    naturalBlinkClipRef.current = null;

    const performBlink = () => {
      const mixer = mixerRef.current;
      if (!mixer || !morphTargetDictionaryBody) return;

      const blinkConfig = animationConfigRef.current.blink;
      if (!blinkConfig.enabled) return;

      // Build the clip + action once, then reuse on every blink.
      // safeMode=false: blink data only has eyeBlinkLeft/eyeBlinkRight which safeMode filters out
      if (!naturalBlinkClipRef.current) {
        naturalBlinkClipRef.current = createAnimation(
          naturalBlinkData, morphTargetDictionaryBody, bodyMeshName,
          30, 0, THREE.InterpolateLinear, false, false,
        );
        if (!naturalBlinkClipRef.current) {
          // Could not build clip (missing morphs); abort the schedule
          // chain so we don't busy-loop trying to rebuild on every tick.
          return;
        }
      }
      // Staleness guard (6 Aug 2026, REPORT.md §c-1): if anything uncached the
      // blink clip since the last blink (mixer sweep, model swap), the cached
      // action's property bindings are freed and touching it corrupts the mixer
      // (`_cacheIndex` TypeError → frozen avatar). `existingAction()` returning
      // null means our cached action is no longer known to the mixer — rebuild.
      if (naturalBlinkActionRef.current) {
        const known = mixer.existingAction(naturalBlinkClipRef.current);
        if (!known || known !== naturalBlinkActionRef.current) {
          naturalBlinkActionRef.current = null;
        }
      }
      if (!naturalBlinkActionRef.current) {
        naturalBlinkActionRef.current = mixer.clipAction(naturalBlinkClipRef.current);
      }

      const action = naturalBlinkActionRef.current;
      try {
        // stop() before reset() so a previous in-flight blink (action
        // still in its 0.15s open→shut→open curve) snaps cleanly back
        // to t=0 instead of accumulating residual time.
        action.stop();
        action.reset();
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
        // Duration: lower = faster blink. Config duration is in ms (150 default), map to timeScale
        action.timeScale = Math.max(0.5, 300 / Math.max(50, blinkConfig.duration));
        action.play();
      } catch (err) {
        // A corrupted action must never take the mixer (and the face) down with
        // it. Drop the caches; the next scheduled blink rebuilds cleanly.
        console.warn('[blink] cached action invalid, rebuilding next blink:', err);
        naturalBlinkActionRef.current = null;
        naturalBlinkClipRef.current = null;
      }

      // Schedule next blink: frequency is blinks per ~10s. Convert to ms delay with randomness.
      const baseDelay = Math.max(1000, 10000 / Math.max(1, blinkConfig.frequency));
      const nextBlinkDelay = baseDelay * 0.6 + Math.random() * baseDelay * 0.8;
      blinkTimerRef.current = setTimeout(performBlink, nextBlinkDelay);
    };

    // Start first blink after 2-4 seconds
    blinkTimerRef.current = setTimeout(performBlink, 2000 + Math.random() * 2000);
    console.debug('Natural blinking initialized (freq:', animationConfigRef.current.blink.frequency, ')');
  }, [bodyMeshName, morphTargetDictionaryBody]);

  const playIdleAnimation = useCallback(() => {
    console.log('Playing idle animation', {
      hasIdleClips: !!idleClips,
      hasMixer: !!mixerRef.current,
      isVoiceLikeMode,
      isVoiceCallAnimationInProgress,
      isSpeakingRef: isSpeakingRef.current,
      hasActiveCleanup: currentAnimationCleanupRef.current !== null
    });
    // Check both state and ref to prevent race conditions
    // Also check if cleanup function exists (animation still running)
    const hasActiveAnimation = isVoiceCallAnimationInProgress ||
      isSpeakingRef.current ||
      currentAnimationCleanupRef.current !== null;
    if (hasActiveAnimation) {
      console.log('Voice call animation in progress (state, ref, or cleanup exists), skipping idle animation');
      return;
    }
    if (!morphTargetDictionaryBody || !mixerRef.current) {
      console.warn('Cannot play idle animation - missing dependencies');
      return;
    }
    if (idleClips && idleClips[0]) {
      const existingAction = mixerRef.current?.existingAction(idleClips[0]);
      if (existingAction && existingAction.isRunning()) {
        console.log('Idle animation already playing, skipping');
        return;
      }
    }
    stopAllAnimations();  // This now preserves mood animations (mood_* clips)
    // Idle FBX bone animation: only for models whose skeleton matches the FBX (GymGirls).
    // M4/UE5 MetaHuman skeletons have different bone rest poses — the same rotation values
    // produce wrong results (head tilt/lean back, and on multi-skel rigs the body's
    // head/neck rotates while the duplicate clothing skeletons stay at rest, producing the
    // "torn shirt collar" Keith saw on Arc, May 17 2026).
    //
    // Arc was originally `m4-male.glb` (renamed to `arc.glb` on May 11 2026 to remove the
    // Sam confusion — see modelRegistry.ts line 350). The rename slipped through this
    // guard, so Arc started getting the FBX idle clip applied to his UE5 skeleton, which
    // is what put him in the wrong initial pose. Explicitly include 'arc.glb' so the
    // guard tracks the model identity, not the historical filename.
    const isUE5Idle = isXandraUE5AvatarUrl(avatar_url);
    const shouldSkipBaseIdleClip = isVoiceLikeMode;
    const idleConfig = animationConfigRef.current.idle;
    const breathConfig = animationConfigRef.current.breathing;

    if (shouldSkipBaseIdleClip) {
      console.log('Live/voice mode idle uses ambient morph layers without the skeletal idle clip');
    } else if (!isUE5Idle && idleClips && idleClips[0] && idleConfig.enabled) {
      let idleAction = mixerRef.current?.clipAction(idleClips[0]);
      if (idleAction) {
        // speed: 0-1, maps to timeScale 0.3-1.2
        idleAction.timeScale = 0.3 + (idleConfig.speed * 0.9);
      }
      idleAction?.setEffectiveWeight(0.9);
      idleAction?.setLoop(LoopRepeat, Infinity);
      idleAction?.reset();
      idleAction?.play();
      console.log('Base idle skeletal animation started (speed:', idleConfig.speed, ')');
    } else if (isUE5Idle) {
      console.log('[UE5] Skipping idle bone animation (FBX rest poses incompatible with UE5 skeleton)');
    }
    if (idleConfig.enabled) {
      let idleMicroClip = createAnimation(idleMicroData, morphTargetDictionaryBody, bodyMeshName);
      if (idleMicroClip) {
        let idleMicroAction = mixerRef.current?.clipAction(idleMicroClip);
        if (idleMicroAction) {
          idleMicroActionRef.current = idleMicroAction;
          // speed: 0-1, maps to timeScale 0.1-0.5
          idleMicroAction.timeScale = 0.1 + (idleConfig.speed * 0.4);
        }
        // intensity: 0-1, maps to weight 0.1-0.6
        idleMicroAction?.setEffectiveWeight(0.1 + (idleConfig.intensity * 0.5));
        idleMicroAction?.setLoop(LoopRepeat, Infinity);
        idleMicroAction?.play();
        console.debug('Idle micro-movements (speed:', idleConfig.speed, 'intensity:', idleConfig.intensity, ')');
      }
    }
    if (breathConfig.enabled) {
      let breathingClip = createAnimation(breathingData, morphTargetDictionaryBody, bodyMeshName);
      if (breathingClip) {
        let breathingAction = mixerRef.current?.clipAction(breathingClip);
        if (breathingAction) {
          breathingActionRef.current = breathingAction;
          // rate: 0-1, maps to timeScale 0.1-0.5
          breathingAction.timeScale = 0.1 + (breathConfig.rate * 0.4);
        }
        // intensity: 0-1, maps to weight 0.15-0.7
        breathingAction?.setEffectiveWeight(0.15 + (breathConfig.intensity * 0.55));
        breathingAction?.setLoop(LoopRepeat, Infinity);
        breathingAction?.play();
        console.debug('Breathing animation (rate:', breathConfig.rate, 'intensity:', breathConfig.intensity, ')');
      }
    }
    // ---------------------------------------------------------------
    // Slice 7 (May 12 2026): the idle "expressions" loop is DISABLED.
    //
    // Why: this clip looped `mouthSmileLeft / mouthSmileRight` keyframes
    // (peaks ~0.07) at 0.2 effective weight whenever speech wasn't
    // active. Between sentences, `isSpeakingRef.current` flips false
    // briefly, the mixer wakes up, and the clip advances through a
    // smile keyframe. When the next sentence starts, the speech
    // viseme RAF only writes morphs that are in the current frame's
    // blendshape data — so the residual smile value sticks on
    // `mouthSmileLeft/Right` until something else writes it. The
    // visible symptom: the avatar appears to be slightly smiling
    // through the next utterance, and the half-open mouth combined
    // with raised corners makes it look like the teeth aren't moving
    // (Keith's "teeth still staying closed for Nicola and others"
    // observation, May 12 2026).
    //
    // The clean fix is "expressions overlay co-exist with speech",
    // but that needs a real additive-blend architecture in the
    // overlay system (mood is RAF + accounting; expressions are
    // mixer + clip — they can't trivially share the same indices).
    // For now, kill the leak source. The avatar still feels alive
    // via natural blinking + idle micro-movements + breathing +
    // sentiment-driven mood overlay (which DOES handle this
    // correctly via subtract-then-add accounting in moodAnimator).
    //
    // If you re-enable this, you MUST handle the inter-sentence
    // smile-leak bug — either by gating on "no speech in last 30s"
    // OR by zeroing the smile morph indices at every speech-start
    // AND resetting moodAnimator's `appliedForMesh` accounting for
    // those indices.
    // ---------------------------------------------------------------
    const IDLE_EXPRESSIONS_LOOP_ENABLED = false;
    if (IDLE_EXPRESSIONS_LOOP_ENABLED) {
      let expressionsClip = createAnimation(expressionsData, morphTargetDictionaryBody, bodyMeshName);
      if (expressionsClip) {
        let expressionsAction = mixerRef.current?.clipAction(expressionsClip);
        if (expressionsAction) {
          expressionsAction.timeScale = 0.15;  // Slower for occasional
        }
        expressionsAction?.setEffectiveWeight(0.2);  // Very low weight
        expressionsAction?.setLoop(LoopRepeat, Infinity);
        expressionsAction?.play();
        console.debug('Facial expressions layer added');
      }
    }
    setupNaturalBlinking();
    console.log('[Avatar] Idle animation system ready');
    // NOTE: Do NOT add isVoiceCallAnimationInProgress to deps - it causes excessive re-renders
    // The callback already checks isSpeakingRef.current which is always up-to-date
  }, [idleClips, isVoiceLikeMode, morphTargetDictionaryBody, stopAllAnimations, setupNaturalBlinking]);
  const triggerIdleAnimation = playIdleAnimation;

  useImperativeHandle(ref, () => ({
    stopAllAnimations() {
      console.log('Stopping all animations (except moods and thinking)');
      // Don't stop animations if speaking is in progress (prevents interrupting lip-sync)
      if (isVoiceCallAnimationInProgress || isSpeakingRef.current) {
        console.log('Voice call animation in progress, skipping stopAllAnimations via ref');
        return;
      }
      if (mixerRef.current) {
        // Stop all animations EXCEPT mood animations and thinking animations
        // Access _actions as private property (type assertion needed)
        const actions = (mixerRef.current as any)._actions;
        if (actions && actions.length > 0) {
          actions.forEach((action: any) => {
            // Keep mood animations and don't stop if thinking is active
            if (!action._clip.name.startsWith('mood_') && !isThinking) {
              action.stop();
            }
          });
        } else {
          mixerRef.current.stopAllAction();
        }
      }
    },
    forceResetSkeletalAnimations() {
      if (isVoiceLikeMode) {
        console.log('🔄 Voice/live mode - unified non-speech controller switching to speaking');
        voiceNonSpeechAnimationRef.current?.setMode('speaking');
        return;
      }

      // Stop listening/thinking RAF loops before lip-sync starts, but keep
      // the current head pose so speech doesn't snap back to center first.
      console.log('🔄 Cancelling skeletal animation loops while preserving current pose');
      if (listeningAnimationRef.current?.cancelAndHoldPose) {
        listeningAnimationRef.current.cancelAndHoldPose();
      } else if (listeningAnimationRef.current?.forceImmediateReset) {
        listeningAnimationRef.current.forceImmediateReset();
      }
      if (thinkingAnimationRef.current?.cancelAndHoldPose) {
        thinkingAnimationRef.current.cancelAndHoldPose();
      } else if (thinkingAnimationRef.current?.forceImmediateReset) {
        thinkingAnimationRef.current.forceImmediateReset();
      }
    },
    setIsThinking(value: boolean) {
      if (verboseAvatarDebug) {
        console.log('Setting thinking state to:', value);
      }
      setIsThinking(value);
      if (isVoiceLikeMode) {
        applyVoiceNonSpeechMode({ thinking: value });
        return;
      }
      if (!value && !(isMobileCall || isWebrtcCall)) {
        if (verboseAvatarDebug) {
          console.log('Thinking state ended, stopping animations (not during voice call)');
        }
        stopAllAnimations();
      } else if (!value && verboseAvatarDebug) {
        console.log('Thinking state ended during voice call, preserving animations');
      }
    },
    setIsListening(value: boolean) {
      if (verboseAvatarDebug) {
        console.log('Setting listening state to:', value);
      }
      setIsListening(value);
      if (isVoiceLikeMode) {
        applyVoiceNonSpeechMode({ listening: value });
        return;
      }
      if (!value && !(isMobileCall || isWebrtcCall)) {
        if (verboseAvatarDebug) {
          console.log('Listening state ended, stopping animations (not during voice call)');
        }
        stopAllAnimations();
      } else if (!value && verboseAvatarDebug) {
        console.log('Listening state ended during voice call, preserving animations');
      }
    },
    getMixer: () => mixerRef.current,
    getMorphTargetDictionary: () => {
      if (!isModelLoaded) return null;
      return {
        body: morphTargetDictionaryBody,
        teeth: morphTargetDictionaryLowerTeeth
      };
    },
    playIdleAnimation() {
      console.log('Playing idle animation via ref');
      // Check both state and ref to prevent race conditions
      if (isVoiceCallAnimationInProgress || isSpeakingRef.current) {
        console.log('Voice call animation in progress (state or ref), skipping idle animation');
        return;
      }
      triggerIdleAnimation();
    },
    stopVisemeAnimation() {
      console.log('Stopping viseme animation via ref (audio ended or failed)');

      // Check if audio has actually ended (not just paused)
      const audioState = getAudioPlaybackState();
      const audioActuallyEnded = audioState.isEnded || !isAudioActuallyPlaying();

      // Only stop if audio has actually ended OR if not protected
      const isProtected = animationStateManagerRef.current.isProtected();
      if (isProtected && !audioActuallyEnded) {
        console.log('Viseme animation stop ignored - protected and audio still playing');
        return;
      }

      // Set flag to prevent re-triggering
      isStoppingAnimationRef.current = true;

      // Call cleanup if it exists
      if (currentAnimationCleanupRef.current) {
        currentAnimationCleanupRef.current();
        currentAnimationCleanupRef.current = null;
      }
      // Clear speaking state
      isSpeakingRef.current = false;
      setIsVoiceCallAnimationInProgress(false);
      currentAnimationHandoffTokenRef.current = null;
      lastBlendDataRef.current = null;
      audioSyncRef.current.isPlaying = false;

      // Clear protection and stop speaking animation state
      animationStateManagerRef.current.clearProtection();
      animationStateManagerRef.current.stopAnimation(AnimationType.SPEAKING, true);
      voiceNonSpeechAnimationRef.current?.setMode('idle');

      // Stop any running viseme/morph animations (keep mood animations)
      if (mixerRef.current) {
        const actions = (mixerRef.current as any)._actions;
        if (actions && actions.length > 0) {
          actions.forEach((action: any) => {
            // Stop all non-mood animations (viseme tracks, idle morphs, etc.)
            const clipName = action._clip?.name || '';
            if (!clipName.startsWith('mood_')) {
              action.stop();
              action.reset();
            }
          });
        }
      }
      setAnimateCallSpeak(false);

      // Reset flag after a short delay to allow state to clear
      setTimeout(() => {
        isStoppingAnimationRef.current = false;
      }, 100);
    },
    isReady: () => {
      // Only body morph dictionary is required - teeth is optional
      const ready = !!(isModelLoaded && mixerRef.current && morphTargetDictionaryBody);
      console.log('isReady called with state:', {
        isModelLoaded,
        hasMixer: !!mixerRef.current,
        hasMorphBody: !!morphTargetDictionaryBody,
        hasMorphTeeth: !!morphTargetDictionaryLowerTeeth,
        ready
      });
      return ready;
    }
  }), [applyVoiceNonSpeechMode, morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth, isModelLoaded, idleClips, isListening, isMobileCall, isThinking, isVoiceLikeMode, isWebrtcCall, isInVoiceCall, stopAllAnimations, isVoiceCallAnimationInProgress, triggerIdleAnimation]);

  useEffect(() => {
    // Only start idle animation if not speaking
    if (morphTargetDictionaryBody && mixerRef.current) {
      const hasActiveAnimation = isVoiceCallAnimationInProgress ||
        isSpeakingRef.current ||
        currentAnimationCleanupRef.current !== null;
      if (!hasActiveAnimation) {
        console.log(isVoiceLikeMode ? 'Setting up live/voice ambient idle layers' : 'Setting up initial idle animation');
        playIdleAnimation();
      } else {
        console.log('Skipping initial idle animation - speaking in progress', {
          isVoiceCallAnimationInProgress,
          isSpeakingRef: isSpeakingRef.current,
          hasActiveCleanup: currentAnimationCleanupRef.current !== null
        });
      }
    }
  }, [isVoiceLikeMode, morphTargetDictionaryBody, idleClips, playIdleAnimation, isVoiceCallAnimationInProgress]);

  useEffect(() => {
    return () => {
      console.log('Cleaning up avatar animations');
      if (mixerRef.current) {
        mixerRef.current.stopAllAction();
      }
    };
  }, []);

  useEffect(() => {
    // Log morph target counts and names on init (for debugging Azure blendshape mapping)
    if (morphTargetDictionaryBody) {
      console.log(`🎭 Morph targets ready: ${Object.keys(morphTargetDictionaryBody).length} shapes`);
    }
  }, [morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth]);

  const userTalkingAnimation = useMemo(() => {
    // Body morph is required, teeth is optional
    if (!morphTargetDictionaryBody) return null;
    const bodyAnimation = createAnimation(userTalkingData, morphTargetDictionaryBody, bodyMeshName);
    const teethAnimation = morphTargetDictionaryLowerTeeth
      ? createAnimation(userTalkingData, morphTargetDictionaryLowerTeeth, teethMeshName)
      : null;
    if (!bodyAnimation) return null;
    return teethAnimation ? [bodyAnimation, teethAnimation] : [bodyAnimation];
  }, [morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth]);

  const listeningAnimation = useMemo(() => {
    // Body morph is required, teeth is optional
    if (!morphTargetDictionaryBody) return null;
    try {
      const bodyAnimation = createAnimation(listeningData, morphTargetDictionaryBody, bodyMeshName);
      const teethAnimation = morphTargetDictionaryLowerTeeth
        ? createAnimation(listeningData, morphTargetDictionaryLowerTeeth, teethMeshName)
        : null;
      if (!bodyAnimation) return null;
      return teethAnimation ? [bodyAnimation, teethAnimation] : [bodyAnimation];
    } catch (error) {
      console.error('Error creating listening animation:', error);
      return null;
    }
  }, [morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth]);

  const thinkingAnimation = useMemo(() => {
    // Body morph is required, teeth is optional
    if (!morphTargetDictionaryBody) return null;
    try {
      const bodyAnimation = createAnimation(thinkingData, morphTargetDictionaryBody, bodyMeshName);
      const teethAnimation = morphTargetDictionaryLowerTeeth
        ? createAnimation(thinkingData, morphTargetDictionaryLowerTeeth, teethMeshName)
        : null;
      if (!bodyAnimation) return null;
      return teethAnimation ? [bodyAnimation, teethAnimation] : [bodyAnimation];
    } catch (error) {
      console.error('Error creating thinking animation:', error);
      return null;
    }
  }, [morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth]);

  useEffect(() => {
    console.log('userTalking:', userTalking, 'isListening:', isListening);
    if (!userTalkingAnimation || !listeningAnimation) {
      console.log('Animations not ready');
      return;
    }

    // Legacy morph-target listening/talking clips should not run during
    // voice/live turns. The newer state-managed listening/thinking overlays
    // plus viseme playback own those transitions, and this older mixer path
    // can still fire in the same window and interfere with speech handoff.
    //
    // isLiveMode matters here (Aug 17 2026): Visual IVR sessions run with
    // isLiveMode true and NONE of the three call flags, so this skip never
    // applied there — and blendDataUserTalking.json (a constant mouthSmile
    // clip) grinned at the caller the whole time they spoke. See
    // utils/legacyConversationClips.ts for the full story.
    if (shouldSkipLegacyConversationClips({ isLiveMode, isMobileCall, isWebrtcCall, isInVoiceCall })) {
      [...listeningAnimation, ...userTalkingAnimation].forEach(clip => {
        const clipAction = mixerRef.current?.clipAction(clip);
        clipAction?.stop();
      });
      console.log('Skipping legacy listening/talking mixer clips during voice/live mode');
      return;
    }

    if (userTalking || isListening) {
      console.log('Starting animations');
      stopAllAnimations();
      listeningAnimation.forEach(clip => {
        let clipAction = mixerRef.current?.clipAction(clip);
        clipAction?.setLoop(LoopRepeat, Infinity);
        clipAction?.fadeIn(0.2);
        clipAction?.play();
      });
      if (userTalking) {
        userTalkingAnimation.forEach(clip => {
          let clipAction = mixerRef.current?.clipAction(clip);
          clipAction?.setLoop(LoopRepeat, Infinity);
          clipAction?.fadeIn(0.2);
          clipAction?.play();
        });
      }
    } else {
      console.log('Stopping animations');
      [...listeningAnimation, ...userTalkingAnimation].forEach(clip => {
        let clipAction = mixerRef.current?.clipAction(clip);
        clipAction?.fadeOut(0.5);
      });
    }
  }, [isLiveMode, isInVoiceCall, isMobileCall, isWebrtcCall, userTalking, isListening, userTalkingAnimation, listeningAnimation, stopAllAnimations]);

  // When the user STARTS talking, ease any held sentiment mood back to
  // baseline. The decay hold (default 8s) deliberately outlives a turn
  // boundary, so a "positive" from the previous answer was still pinning a
  // smile while the caller spoke their next sentence — listening should read
  // attentive-neutral. Pinned editor previews are unaffected (the fade only
  // applies to overlays that carry decay options, i.e. sentiment moods).
  useEffect(() => {
    const wasTalking = userTalkingRef.current;
    userTalkingRef.current = userTalking;
    if (userTalking && !wasTalking) {
      const faded = fadeOutActiveSentimentMood(400);
      if (faded) {
        console.log('🎭 User started talking — fading held sentiment mood to attentive-neutral');
        lastSentimentMoodRef.current = 'neutral';
      }
    }
  }, [userTalking]);

  // DISABLED: Blend data thinking animation (causes mouth wobble)
  // Use enhanced eye/head thinking animation instead (setupThinkingAnimation in useEffect below)
  // The blend data version with morph targets is too jerky for thinking state
  // Keeping this code commented for reference - can be re-enabled if blend data is refined
  /*
  useEffect(() => {
    if (!thinkingAnimation || !mixerRef.current) return;

    // Skip blend data thinking animation during voice calls
    if (isVoiceCallAnimationInProgress || isMobileCall || isWebrtcCall) {
      console.log('Skipping blend data thinking animation during voice call');
      return;
    }

    console.log('Thinking animation effect triggered:', {
      isThinking,
      hasThinkingAnimation: !!thinkingAnimation,
      hasMixer: !!mixerRef.current
    });
    if (isThinking) {
      console.log('Playing thinking animation');
      thinkingAnimation.forEach(clip => {
        let clipAction = mixerRef.current?.clipAction(clip);
        clipAction?.setLoop(LoopRepeat, Infinity);
        if (clipAction) {
          clipAction.timeScale = 0.6;
        }
        clipAction?.play();
      });
    } else {
      console.log('Fading out thinking animation');
      thinkingAnimation.forEach(clip => {
        let clipAction = mixerRef.current?.clipAction(clip);
        clipAction?.fadeOut(0.5);
      });
      // Only start idle if not in voice call
      if (!isVoiceCallAnimationInProgress && !isMobileCall && !isWebrtcCall) {
        if (idleClips && idleClips[0]) {
          console.log('Starting idle animation after thinking');
          let idleAction = mixerRef.current?.clipAction(idleClips[0]);
          idleAction?.reset();
          idleAction?.fadeIn(0.5);
          idleAction?.play();
        }
      } else {
        console.log('Skipping idle animation - in voice call mode');
      }
    }
    return () => {
      if (thinkingAnimation) {
        thinkingAnimation.forEach(clip => {
          let clipAction = mixerRef.current?.clipAction(clip);
          clipAction?.stop();
        });
      }
    };
  }, [isThinking, thinkingAnimation, idleClips, isVoiceCallAnimationInProgress, isMobileCall, isWebrtcCall]);
  */

  // Setup listening and thinking animations in useEffect to avoid calling on every render
  useEffect(() => {
    if (isVoiceLikeMode) {
      if (listeningAnimationRef.current?.cleanup) {
        listeningAnimationRef.current.cleanup();
      }
      listeningAnimationRef.current = null;
      return;
    }

    if (!isModelLoaded || !headRef.current || !neckRef.current) return;

    // Pass ALL refs including eyes for eye compensation and focus, plus config from editor
    const listeningConfig = animationConfigRef.current.listening;
    const listeningAnim = setupListeningAnimation(headRef as any, neckRef as any, leftEyeRef as any, rightEyeRef as any, {
      nodFrequency: listeningConfig.nodFrequency,
      tiltAmount: listeningConfig.tiltAmount,
      suppressHeadMovement: isMobileCall || isWebrtcCall || isInVoiceCall,
    });
    listeningAnimationRef.current = listeningAnim;  // Store for force reset access

    if (isListening && listeningAnim) {
      // Check if we can start listening animation (respects priority system)
      const canStart = animationStateManagerRef.current.canStart(AnimationType.LISTENING);

      if (canStart) {
        console.log('Starting listening animation overlay with eye compensation', {
          suppressHeadMovement: isMobileCall || isWebrtcCall || isInVoiceCall,
        });

        // Start animation state (listening can run alongside speaking - different body parts)
        animationStateManagerRef.current.startAnimation(AnimationType.LISTENING, false, false);

        // Use smooth transition if thinking animation is active
        const currentState = animationStateManagerRef.current.getCurrentState();
        if (currentState.type === AnimationType.THINKING && thinkingAnimationRef.current?.getCurrentPositions) {
          const thinkingPositions = thinkingAnimationRef.current.getCurrentPositions();
          listeningAnim.transitionTo(thinkingPositions, 300);
        } else {
          listeningAnim.start();
        }

        // Track when listening started to enforce minimum duration
        const listeningStartTime = Date.now();
        const minListeningDuration = (isMobileCall || isWebrtcCall || isInVoiceCall) ? 0 : 2000; // Voice turns can transition immediately

        return () => {
          const elapsed = Date.now() - listeningStartTime;
          const currentState = animationStateManagerRef.current.getCurrentState();

          // Listening should ONLY stop when:
          // 1. Thinking animation starts (response being processed)
          // 2. Speaking animation starts (protected) - avatar responding
          // 3. Minimum duration has elapsed AND user is not talking
          const isThinkingStarting = currentState.type === AnimationType.THINKING;
          const isSpeakingStarting = currentState.type === AnimationType.SPEAKING && currentState.isProtected;
          const shouldStopForResponse = isThinkingStarting || isSpeakingStarting;

          // If thinking or speaking is starting, allow immediate stop (response arrived)
          if (shouldStopForResponse) {
            console.log('Listening animation stopping - response arrived (thinking or speaking starting)');
            if (listeningAnim) {
              if (isSpeakingStarting && listeningAnim.cancelAndHoldPose) {
                console.log('Listening animation holding pose for speaking takeover');
                listeningAnim.cancelAndHoldPose();
              } else {
                listeningAnim.stop();
                listeningAnim.cleanup();
              }
            }
            animationStateManagerRef.current.stopAnimation(AnimationType.LISTENING, false);
            return;
          }

          // Otherwise, enforce minimum duration unless user stopped talking
          if (elapsed < minListeningDuration) {
            if (verboseAvatarDebug) {
              console.log(`Listening animation stopped too early (${elapsed}ms < ${minListeningDuration}ms) - extending duration`);
            }
            // Extend listening animation by scheduling stop after minimum duration
            setTimeout(() => {
              if (listeningAnim && !isListening) {
                // Double-check state hasn't changed back to listening or thinking/speaking started
                const finalState = animationStateManagerRef.current.getCurrentState();
                const shouldStillStop = finalState.type !== AnimationType.LISTENING &&
                  finalState.type !== AnimationType.THINKING &&
                  !(finalState.type === AnimationType.SPEAKING && finalState.isProtected);
                if (shouldStillStop) {
                  if (verboseAvatarDebug) {
                    console.log('Stopping listening animation after minimum duration');
                  }
                  listeningAnim.stop();
                  listeningAnim.cleanup();
                  animationStateManagerRef.current.stopAnimation(AnimationType.LISTENING, false);
                } else {
                  console.log('Listening animation extended - thinking/speaking started, keeping it running');
                }
              }
            }, minListeningDuration - elapsed);
            return; // Don't stop immediately
          }

          // Minimum duration elapsed, safe to stop
          if (listeningAnim) {
            listeningAnim.stop();
            listeningAnim.cleanup();
          }
          // Stop animation state
          animationStateManagerRef.current.stopAnimation(AnimationType.LISTENING, false);
        };
      } else {
        console.log('Cannot start listening animation - higher priority animation active');
      }
    } else if (listeningAnim) {
      // Stop listening animation when isListening becomes false
      // But check if we should wait for minimum duration
      const currentState = animationStateManagerRef.current.getCurrentState();
      if (currentState.type === AnimationType.LISTENING) {
        const startTime = currentState.startTime;
        if (startTime) {
          const elapsed = Date.now() - startTime;
          const minListeningDuration = (isMobileCall || isWebrtcCall || isInVoiceCall) ? 0 : 2000; // Voice turns can transition immediately

          if (elapsed < minListeningDuration) {
            console.log(`Listening animation active for only ${elapsed}ms - waiting for minimum duration`);
            // Schedule stop after minimum duration
            setTimeout(() => {
              if (!isListening && listeningAnim) {
                const finalState = animationStateManagerRef.current.getCurrentState();
                if (finalState.type === AnimationType.LISTENING) {
                  // Use smooth transition if thinking animation is active
                  const transitionState = animationStateManagerRef.current.getCurrentState();
                  if (transitionState.type === AnimationType.THINKING && thinkingAnimationRef.current?.getCurrentPositions) {
                    const thinkingPositions = thinkingAnimationRef.current.getCurrentPositions();
                    listeningAnim.transitionTo(thinkingPositions, 300);
                  } else {
                    listeningAnim.stop();
                    listeningAnim.cleanup();
                  }
                  animationStateManagerRef.current.stopAnimation(AnimationType.LISTENING, false);
                }
              }
            }, minListeningDuration - elapsed);
            return; // Don't stop immediately
          }
        }
      }

      // Use smooth transition if thinking animation is active
      if (currentState.type === AnimationType.THINKING && thinkingAnimationRef.current?.getCurrentPositions) {
        const thinkingPositions = thinkingAnimationRef.current.getCurrentPositions();
        listeningAnim.transitionTo(thinkingPositions, 300);
      } else if (currentState.type === AnimationType.SPEAKING && currentState.isProtected && listeningAnim.cancelAndHoldPose) {
        console.log('Listening animation preserving pose because speaking is active');
        listeningAnim.cancelAndHoldPose();
      } else {
        listeningAnim.stop();
        listeningAnim.cleanup();
      }
      animationStateManagerRef.current.stopAnimation(AnimationType.LISTENING, false);
    }
  }, [isInVoiceCall, isListening, isMobileCall, isModelLoaded, isVoiceLikeMode, isWebrtcCall]);

  useEffect(() => {
    if (isVoiceLikeMode) {
      if (thinkingAnimationRef.current?.cleanup) {
        thinkingAnimationRef.current.cleanup();
      }
      thinkingAnimationRef.current = null;
      return;
    }

    if (!isModelLoaded || !headRef.current) return;

    const thinkingConfig = animationConfigRef.current.thinking;
    const thinkingAnim = setupThinkingAnimation(
      leftEyeRef.current ? leftEyeRef as any : undefined,
      rightEyeRef.current ? rightEyeRef as any : undefined,
      headRef as any,
      {
      eyeSearchIntensity: thinkingConfig.eyeSearchIntensity,
      suppressHeadMovement: isMobileCall || isWebrtcCall || isInVoiceCall,
    });
    thinkingAnimationRef.current = thinkingAnim;  // Store for force reset access

    if (isThinking && thinkingAnim) {
      // Check if we can start thinking animation (respects priority system)
      const canStart = animationStateManagerRef.current.canStart(AnimationType.THINKING);

      if (canStart) {
        console.log('Starting enhanced thinking animation', {
          isMobileCall,
          isWebrtcCall,
          isInVoiceCall,
          isVoiceCallAnimationInProgress,
          suppressHeadMovement: isMobileCall || isWebrtcCall || isInVoiceCall,
          canStart
        });

        // Start animation state (thinking can run alongside speaking - different body parts)
        animationStateManagerRef.current.startAnimation(AnimationType.THINKING, false, false);

        // Thinking animation works during voice calls too - it's head/eye movement that doesn't conflict with lip-sync
        thinkingAnim.start();

        return () => {
          if (thinkingAnim) {
            const currentState = animationStateManagerRef.current.getCurrentState();
            const isSpeakingStarting = currentState.type === AnimationType.SPEAKING && currentState.isProtected;
            if (isSpeakingStarting && thinkingAnim.cancelAndHoldPose) {
              console.log('Thinking animation holding pose for speaking takeover');
              thinkingAnim.cancelAndHoldPose();
            } else {
              thinkingAnim.stop();
              thinkingAnim.cleanup();
            }
          }
          // Stop animation state
          animationStateManagerRef.current.stopAnimation(AnimationType.THINKING, false);
        };
      } else {
        console.log('Cannot start thinking animation - higher priority animation active');
      }
    } else if (thinkingAnim) {
      // Stop thinking animation when isThinking becomes false
      // Use smooth transition if listening animation is active
      const currentState = animationStateManagerRef.current.getCurrentState();
      if (currentState.type === AnimationType.LISTENING && listeningAnimationRef.current?.getCurrentPositions) {
        const listeningPositions = listeningAnimationRef.current.getCurrentPositions();
        thinkingAnim.transitionTo(listeningPositions, 300);
      } else if (currentState.type === AnimationType.SPEAKING && currentState.isProtected && thinkingAnim.cancelAndHoldPose) {
        console.log('Thinking animation preserving pose because speaking is active');
        thinkingAnim.cancelAndHoldPose();
      } else {
        thinkingAnim.stop();
        thinkingAnim.cleanup();
      }
      animationStateManagerRef.current.stopAnimation(AnimationType.THINKING, false);
    }
  }, [isThinking, isModelLoaded, isMobileCall, isVoiceLikeMode, isWebrtcCall, isInVoiceCall, isVoiceCallAnimationInProgress]);

  // Process animateCallSpeak to animate avatar during voice calls
  useEffect(() => {
    // If we're in the process of stopping, don't start new animations
    if (isStoppingAnimationRef.current) {
      console.log('Animation is being stopped, skipping new animation');
      return;
    }

    if (!animateCallSpeak || typeof animateCallSpeak !== 'object' || !animateCallSpeak.blendData) {
      return;
    }

    // Get handoffToken from animateCallSpeak if available (for duplicate detection)
    const handoffToken = (animateCallSpeak as any).handoffToken || null;

    // Prevent rapid-fire duplicate animations (within 200ms) OR same handoffToken
    // But allow new animations if handoffToken is different (new utterance)
    // Also allow if previous animation has finished (cleanup completed)
    const now = Date.now();
    const timeSinceLast = now - lastAnimationTimeRef.current;

    // Check if previous animation is still actually running
    const previousAnimationStillRunning = currentAnimationCleanupRef.current !== null;

    // Check if blendData is actually the same (true duplicate)
    const blendData = animateCallSpeak.blendData;
    const isSameBlendData = lastBlendDataRef.current &&
      blendData &&
      lastBlendDataRef.current.length === blendData.length &&
      lastBlendDataRef.current.length > 0 &&
      lastBlendDataRef.current[0]?.time === blendData[0]?.time &&
      lastBlendDataRef.current[lastBlendDataRef.current.length - 1]?.time === blendData[blendData.length - 1]?.time;

    // Allow new animation if handoffToken is different (new utterance)
    const isNewUtterance = handoffToken &&
      handoffToken !== currentAnimationHandoffTokenRef.current;

    // Very short time window (< 10ms) with same blendData = almost certainly a duplicate trigger
    const isRapidDuplicate = timeSinceLast < 10 && isSameBlendData;

    // Longer time window (200ms) but animation still running = duplicate
    const isDuplicateByTime = timeSinceLast < 200 && isSpeakingRef.current && previousAnimationStillRunning && !isNewUtterance;

    // Same handoffToken within 1 second = duplicate
    const isDuplicateByToken = handoffToken &&
      handoffToken === currentAnimationHandoffTokenRef.current &&
      isSpeakingRef.current &&
      previousAnimationStillRunning &&
      timeSinceLast < 1000;

    if (isRapidDuplicate || ((isDuplicateByTime || isDuplicateByToken) && !isNewUtterance)) {
      console.log('Skipping duplicate animation', {
        reason: isRapidDuplicate ? 'rapid-duplicate' : (isDuplicateByTime ? 'time-based' : 'token-based'),
        timeSinceLast,
        currentToken: currentAnimationHandoffTokenRef.current,
        newToken: handoffToken,
        isSpeaking: isSpeakingRef.current,
        previousAnimationStillRunning,
        isNewUtterance,
        isSameBlendData,
        blendDataLength: blendData?.length,
        lastBlendDataLength: lastBlendDataRef.current?.length
      });
      return;
    }

    if (verboseAvatarDebug) {
      console.log('animateCallSpeak triggered', {
        hasBlendData: !!animateCallSpeak.blendData,
        handoffToken,
        isMobileCall,
        isWebrtcCall,
        hasMixer: !!mixerRef.current,
        hasBodyMorph: !!morphTargetDictionaryBody,
        hasTeethMorph: !!morphTargetDictionaryLowerTeeth,
        timeSinceLastAnimation: now - lastAnimationTimeRef.current,
        currentAnimationToken: currentAnimationHandoffTokenRef.current
      });
    }

    // Only body morph dictionary is required - teeth is optional for speech animation
    if (!mixerRef.current || !morphTargetDictionaryBody) {
      console.warn('Cannot animate - missing mixer or body morph dictionary');
      return;
    }

    // Clean up previous animation if still running
    if (currentAnimationCleanupRef.current) {
      console.log('Cleaning up previous animation before starting new one');
      currentAnimationCleanupRef.current();
      currentAnimationCleanupRef.current = null;
    }

    // CRITICAL: Increment animation ID to invalidate any old animation loops still running
    // This ensures old animations don't call cleanup/completion when they finish
    currentAnimationIdRef.current++;
    const thisAnimationId = currentAnimationIdRef.current;
    console.log('Starting new animation with ID:', thisAnimationId);

    // SYNC FIX v2: Reset external audio sync state for new animation
    // This prevents stale sync data from previous animation causing initial fast playback
    externalAudioTimeRef.current = {
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      lastSyncTime: 0, // Reset to force waiting for fresh sync data
      animationStartOffset: 0
    };

    // Mark the time of this animation start and track handoffToken and blendData
    lastAnimationTimeRef.current = now;
    lastBlendDataRef.current = blendData; // Store for duplicate detection
    if (handoffToken) {
      currentAnimationHandoffTokenRef.current = handoffToken;
    }

    const processAnimation = async () => {
      try {
        // Check if we can start speaking animation (should always be able to - highest priority)
        const canStart = animationStateManagerRef.current.canStart(AnimationType.SPEAKING, true); // Force start for speaking
        if (!canStart && !animationStateManagerRef.current.isProtected()) {
          console.warn('Cannot start speaking animation - this should not happen');
          return;
        }

        // CRITICAL: IMMEDIATE SPEAKING TAKEOVER
        // This cancels ALL RAF loops synchronously to prevent any jitter
        animationController.immediateSpeakingTakeover();

        // Set speaking flag IMMEDIATELY via ref (before state update) to prevent idle animations
        isSpeakingRef.current = true;
        // Mood preview hold zeros jawOpen every frame. Release it so visemes
        // can actually open / stretch / funnel instead of looking like a pout.
        facePreviewHoldRef.current = false;
        muteIdleMorphActionsForGesture();
        setIsVoiceCallAnimationInProgress(true);

        // Start speaking animation with PROTECTION (highest priority, cannot be interrupted)
        animationStateManagerRef.current.startAnimation(AnimationType.SPEAKING, true, true); // Force start, protected

        if (isVoiceLikeMode) {
          console.log('🔄 Voice/live speech takeover via unified non-speech controller');
          voiceNonSpeechAnimationRef.current?.setMode('speaking');
        } else {
          // CRITICAL: Stop listening/thinking animation loops before speaking,
          // but preserve the current pose so speech can begin from wherever the
          // head already is instead of snapping back to center.
          if (listeningAnimationRef.current?.cancelAndHoldPose) {
            console.log('🔄 Holding listening pose before speaking');
            listeningAnimationRef.current.cancelAndHoldPose();
          } else if (listeningAnimationRef.current?.forceImmediateReset) {
            console.log('🔄 Force IMMEDIATE reset of listening animation before speaking');
            listeningAnimationRef.current.forceImmediateReset();
          } else if (listeningAnimationRef.current) {
            console.log('🔄 Force stopping listening animation before speaking');
            listeningAnimationRef.current.stop();
            listeningAnimationRef.current.cleanup();
          }
          if (thinkingAnimationRef.current?.cancelAndHoldPose) {
            console.log('🔄 Holding thinking pose before speaking');
            thinkingAnimationRef.current.cancelAndHoldPose();
          } else if (thinkingAnimationRef.current?.forceImmediateReset) {
            console.log('🔄 Force IMMEDIATE reset of thinking animation before speaking');
            thinkingAnimationRef.current.forceImmediateReset();
          } else if (thinkingAnimationRef.current) {
            console.log('🔄 Force stopping thinking animation before speaking');
            thinkingAnimationRef.current.stop();
            thinkingAnimationRef.current.cleanup();
          }
        }
        // Also update the state to ensure useEffect cleanup runs
        setIsListening(false);
        setIsThinking(false);

        const rawBlendData = animateCallSpeak.blendData;
        const blendData = overlayBreathingOnBlendData(
          Array.isArray(rawBlendData)
            ? rawBlendData
            : (rawBlendData?.body ?? [])
        );
        console.log('Processing voice call animation with', blendData.length, 'frames');

        // Stop current animations AND uncache old clips before starting new one.
        // CRITICAL: Just stopping actions is not enough — stopped/finished actions remain in
        // the mixer's cache and their track bindings interfere with new actions targeting the
        // same morph target indices. This causes the "second animation stops working" bug.
        //
        // EXCEPT the cached natural-blink clip (6 Aug 2026, diagnostics REPORT.md §c-1):
        // the blink setTimeout chain in setupNaturalBlinking() holds
        // naturalBlinkClipRef/naturalBlinkActionRef across this sweep. Uncaching the
        // blink clip here freed its property bindings while the timer kept the stale
        // AnimationAction; the next blink's stop()/reset() then wrote `_cacheIndex` on
        // a freed binding, corrupting the mixer and freezing the avatar mid-reply
        // (the intermittent `TypeError: … '_cacheIndex' …` seen on every engine on
        // nuamedia — longest replies, most blinks in the window). Blink only drives
        // eyeBlinkLeft/Right, which no viseme track touches, so keeping it cached is
        // both safe and the intended behaviour (blinking continues through speech).
        if (mixerRef.current) {
          const actions = (mixerRef.current as any)._actions;
          if (actions && actions.length > 0) {
            // Collect clips to uncache (can't modify _actions while iterating)
            const clipsToUncache: any[] = [];
            actions.forEach((action: any) => {
              if (action._clip.name.startsWith('mood_')) return;
              if (naturalBlinkClipRef.current && action._clip === naturalBlinkClipRef.current) return;
              action.stop();
              clipsToUncache.push(action._clip);
            });
            // Uncache clips AFTER stopping — removes actions + track bindings from mixer
            clipsToUncache.forEach((clip: any) => {
              // Defence in depth: if the blink clip ever ends up in a sweep (e.g. a
              // future cleanup path forgets the exclusion), drop the cached refs so
              // performBlink() rebuilds a fresh clip+action instead of replaying a
              // stale action against freed bindings.
              if (clip === naturalBlinkClipRef.current) {
                naturalBlinkActionRef.current = null;
                naturalBlinkClipRef.current = null;
              }
              mixerRef.current?.uncacheClip(clip);
            });
            console.log(`[M4] Uncached ${clipsToUncache.length} old clips before new speaking animation (blink clip preserved)`);
          }
        }

        // Create and play animations with AUDIO-SYNC instead of frame-based timing
        // This prevents drift and stops when audio stops
        const mixer = mixerRef.current;

        // Guard: Ensure morphTargetDictionaries are loaded before attempting animation
        if (!morphTargetDictionaryBody && !morphTargetDictionaryLowerTeeth) {
          console.warn('⚠️ Cannot animate - no morph dictionaries loaded yet');
          return;
        }

        if (mixer) {
          // Body + optional teeth animation mode
          // Create clips - body is required, teeth is optional (skip for performance)
          const bodyClipForDuration = createAnimation(blendData, morphTargetDictionaryBody, bodyMeshName);
          const teethClipForDuration = (!skipTeethAnimation && morphTargetDictionaryLowerTeeth)
            ? createAnimation(blendData, morphTargetDictionaryLowerTeeth, teethMeshName)
            : null;

          // Proceed if body clip exists (this has the mouth morphs)
          if (bodyClipForDuration) {

            // Start audio sync tracking
            audioSyncRef.current = {
              isPlaying: true,
              startTime: Date.now(),
              pausedAt: 0,
              currentAudioTime: 0
            };

            // THREE.JS ANIMATION - BODY animation (with optional teeth)
            try {
              // REUSE the clips already created above (don't recreate - this caused null crashes)
              const bodyClip = bodyClipForDuration;
              const teethClip = teethClipForDuration;

              // BODY animation - Use action.play() which respects keyframe timing from viseme data
              // This ensures pauses in speech are properly animated (mouth closes during pauses)
              // The mixer.update(delta) is called in useFrame, advancing the animation correctly
              if (bodyClip && bodyClip.tracks.length > 0) {
                // Find ALL meshes with morph targets (body, teeth, eyes, etc.)
                // This ensures teeth and other primitives animate together with the body
                const morphMeshes: any[] = [];
                gltf.scene.traverse((node: any) => {
                  // Collect ALL meshes with morph targets
                  if (node.isMesh && node.morphTargetDictionary && node.morphTargetInfluences) {
                    morphMeshes.push(node);
                  }
                });

                const isGenericBodyMorphKey = (morphKey: string): boolean =>
                  /^body_morph_\d+$/i.test(morphKey) || /^target_\d+$/i.test(morphKey);

                const isSpeechMorphMesh = (mesh: any): boolean => {
                  const meshName = String(mesh.name || '').toLowerCase();
                  const morphKeys = Object.keys(mesh.morphTargetDictionary || {});
                  const hasNamedFacialTargets = morphKeys.some((morphKey) => {
                    const key = morphKey.toLowerCase();
                    return key.includes('jaw') ||
                      key.includes('mouth') ||
                      key.includes('tongue') ||
                      key.includes('cheek') ||
                      key.includes('brow') ||
                      key.includes('eye') ||
                      key.includes('nose');
                  });
                  const hasOnlyGenericBodyMorphs = morphKeys.length > 0 && morphKeys.every(isGenericBodyMorphKey);
                  const isPrimarySpeechMesh = mesh.name === bodyMeshName;
                  const isHeadLikeMesh = meshName.includes('head') || meshName.includes('face');
                  const isTeethLikeMesh = meshName.includes('teeth') || meshName.includes('tongue') || meshName.includes('mouth');

                  if (isPrimarySpeechMesh || isHeadLikeMesh || isTeethLikeMesh || hasNamedFacialTargets) {
                    return true;
                  }

                  // Avoid animating clothing/body-shape meshes that only expose generic body morph sliders.
                  if (hasOnlyGenericBodyMorphs) {
                    return false;
                  }

                  return false;
                };

                const speechMorphMeshes = morphMeshes.filter(isSpeechMorphMesh);

                console.log(
                  `[Avatar] Found ${morphMeshes.length} meshes with morph targets for animation (${speechMorphMeshes.length} speech meshes)`
                );

                if (speechMorphMeshes.length > 0 && morphTargetDictionaryBody) {

                  // PHONEME_SCALING - v1.9.10 values aligned with speechUtils.ts and converter.ts
                  // Reduced jaw/mouth values to fix teeth exposure issue
                  const PHONEME_SCALING: Record<string, number> = {
                    jawOpen: 0.70, jawForward: 0.90, jawLeft: 1.05, jawRight: 1.05,
                    mouthFunnel: 1.25, mouthPucker: 1.25, mouthOpen: 1.15, mouthClose: 0.65,
                    mouthUpperUp_L: 0.95, mouthUpperUp_R: 0.95,
                    mouthLowerDown_L: 0.45, mouthLowerDown_R: 0.45,
                    mouthSmile_L: 1.1, mouthSmile_R: 1.1,
                    mouthFrown_L: 1.05, mouthFrown_R: 1.05,
                    mouthRollLower: 0.75, mouthRollUpper: 0.75,
                  };

                  const getMorphTargetIndex = (key: string): number | undefined => {
                    return resolveMorphTargetIndex(morphTargetDictionaryBody, key);
                  };

                  // Helper: Linear interpolation
                  const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

                  // SILENCE DETECTION: Morph targets that should return to neutral during pauses
                  const MOUTH_MORPHS = new Set([
                    'jawOpen', 'jawForward', 'jawLeft', 'jawRight', 'jaw_L', 'jaw_R',
                    'mouthOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker',
                    'mouthUpperUp_L', 'mouthUpperUp_R', 'mouthLowerDown_L', 'mouthLowerDown_R',
                    'mouthSmile_L', 'mouthSmile_R', 'mouthFrown_L', 'mouthFrown_R',
                    'mouthStretch_L', 'mouthStretch_R', 'mouthDimple_L', 'mouthDimple_R',
                    'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower', 'mouthShrugUpper',
                    'mouthPress_L', 'mouthPress_R', 'mouthLeft', 'mouthRight',
                    'tongueOut', 'cheekPuff', 'cheekSquint_L', 'cheekSquint_R'
                  ]);
                  // Time gap threshold for detecting silence (in seconds)
                  // If the gap between frames is > 100ms, it's likely a pause
                  const SILENCE_GAP_THRESHOLD = 0.1; // 100ms
                  // How fast to return to neutral during silence (higher = faster reset)
                  const SILENCE_DECAY_RATE = 0.25;

                  // Phase 4: Jitter Reduction Constants (IMPROVED)
                  // Higher smoothing factor = more responsive animation (more weight on new value)
                  const TEMPORAL_SMOOTHING_FACTOR = 0.75; // 25% previous + 75% new (more responsive)
                  const MAX_DELTA_TIME = 0.025; // Cap at ~40fps to prevent large jumps (was 0.033)
                  const MIN_FRAME_INTERVAL = 16; // Minimum 16ms between frames (~60fps)
                  const SILENCE_FULL_RESET_THRESHOLD = 0.15; // 150ms = full reset of mouth morphs (was 0.2)

                  // Animate using requestAnimationFrame with INTERPOLATION.
                  //
                  // CATCH-UP FIX (Apr 2026): If the audio for this utterance has
                  // already started playing on the user's speakers BEFORE the
                  // visemes arrived at the browser (which happens routinely in
                  // WebRTC live mode — CamlinVoice synthesizes audio + visemes,
                  // pushes audio to Asterisk RTP first, and the visemes hop
                  // through the runtime websocket adding ~200-1500ms latency),
                  // the local clock would start at 0 while audio is already at
                  // e.g. 1.2s. The first `visual.audio.position` event then
                  // makes `getAnimationTime` jump from 0 to ~1.05s (after the
                  // 150ms WebRTC compensation), which the user sees as the
                  // animation "fast-forwarding to catch up to the audio".
                  //
                  // To eliminate the catch-up, anchor the local clock to the
                  // audio's existing position when we have a fresh external
                  // sync. From the very first frame, animation starts at the
                  // same position the audio is actually at — no jump.
                  let animStartTime = performance.now();
                  const initialExtAudio = externalAudioTimeRef.current;
                  const initialSyncFreshness = initialExtAudio.lastSyncTime > 0
                    ? performance.now() - initialExtAudio.lastSyncTime
                    : Infinity;
                  if (initialSyncFreshness < 500 && initialExtAudio.currentTime > 0) {
                    // Apply the same compensation that the renderer uses on
                    // every subsequent frame so we line up cleanly when the
                    // next external sync arrives.
                    const initialOffsetSeconds = Math.max(
                      0,
                      initialExtAudio.currentTime + (initialSyncFreshness / 1000) - 0
                    );
                    animStartTime = performance.now() - (initialOffsetSeconds * 1000);
                    console.log(
                      `[Avatar] Anchoring animation start to existing audio position ` +
                      `${initialOffsetSeconds.toFixed(2)}s (audio was ${initialExtAudio.currentTime.toFixed(2)}s ` +
                      `${initialSyncFreshness.toFixed(0)}ms ago) — no catch-up jump`
                    );
                  }
                  const animDuration = bodyClip.duration * 1000; // Convert to ms
                  let animFrameId: number | null = null;
                  let prevMorphValues: Record<number, number> = {}; // For temporal smoothing
                  let lastFrameTime = performance.now(); // For delta time clamping
                  let lastFrameIdx = 0; // Track last frame for faster lookup and completion detection
                  const totalFrames = blendData.length;
                  let missingJawOpenMorphLogged = false;

                  // Phase 1.3: Get animation time - uses audio time if available, falls back to performance.now()
                  // SYNC FIX v3: Detect audio pauses and don't extrapolate during them
                  let useExternalSyncLogCount = 0; // Log first few external syncs
                  let lastReportedAudioTime = 0; // Track last audio position for pause detection
                  let lastReportedAudioSyncTime = 0; // When we last saw the audio position change
                  let isAudioPaused = false; // Detect if audio is in a pause (comma, period, etc.)

                  const getAnimationTime = (): number => {
                    const extAudio = externalAudioTimeRef.current;
                    const now = performance.now();

                    // If we have recent external audio sync (within 500ms), use it
                    if (extAudio.lastSyncTime > 0) {
                      const timeSinceSync = now - extAudio.lastSyncTime;
                      if (timeSinceSync < 500) {
                        // SYNC FIX v3: Detect if audio position has changed (not in a pause)
                        // If audio position is same as before, we're in a punctuation pause
                        const audioPositionChanged = Math.abs(extAudio.currentTime - lastReportedAudioTime) > 0.01;

                        if (audioPositionChanged) {
                          // Audio is advancing normally - update tracking
                          lastReportedAudioTime = extAudio.currentTime;
                          lastReportedAudioSyncTime = extAudio.lastSyncTime;
                          isAudioPaused = false;
                        } else {
                          // Audio position hasn't changed - likely in a punctuation pause
                          // Only mark as paused if we've seen 2+ updates with same position
                          if (extAudio.lastSyncTime > lastReportedAudioSyncTime + 150) {
                            isAudioPaused = true;
                          }
                        }

                        // SYNC FIX v3: NO extrapolation during pauses, smooth extrapolation
                        // between sync ticks otherwise.
                        let estimatedCurrentTime: number;
                        if (isAudioPaused) {
                          // During pause - hold at current audio position, no extrapolation
                          estimatedCurrentTime = extAudio.currentTime;
                        } else {
                          // SYNC FIX v4 (Apr 23 2026): extrapolate UP TO ~250ms.
                          //
                          // Background: now that the synthetic clock is fully
                          // disabled in the CamlinVoice-driven path (Visual IVR
                          // + browser WebRTC live), external sync is the ONLY
                          // timing source. CamlinVoice publishes audio.position
                          // every 200ms. With the previous 50ms extrapolation
                          // cap, the renderer would advance smoothly for 50ms
                          // then FREEZE for 150ms waiting for the next sync
                          // tick — a stutter every 200ms that Keith described
                          // as "jerky, feels like two animations".
                          //
                          // Capping at 250ms means the renderer keeps
                          // advancing smoothly across the gap between sync
                          // ticks. CamlinVoice's clock is wall-clock-accurate
                          // and the audio plays at wall-clock rate, so
                          // extrapolating with `performance.now()` deltas
                          // tracks audio perfectly across one tick. If the
                          // sync stops entirely (audio finished, network
                          // hiccup) we cap before we'd visibly drift past
                          // the end of the next expected tick.
                          //
                          // Safe upper bound = sync interval (200ms) + 50ms
                          // headroom for jitter on the runtime websocket
                          // forwarding hop. If this ever gets > 250ms stale
                          // we fall through to local clock (line below).
                          const maxExtrapolation = 0.25;
                          const extrapolation = Math.min(timeSinceSync / 1000, maxExtrapolation);
                          estimatedCurrentTime = extAudio.currentTime + extrapolation;
                        }

                        // Log first 3 uses of external sync
                        if (useExternalSyncLogCount < 3) {
                          console.log(`🔄 [Avatar] EXTERNAL sync: ${estimatedCurrentTime.toFixed(2)}s (paused=${isAudioPaused}, lastSync ${timeSinceSync.toFixed(0)}ms ago)`);
                          useExternalSyncLogCount++;
                        }
                        return estimatedCurrentTime * 1000; // Return in ms
                      }
                    }

                    // Fallback to local time - log if external sync was expected but stale
                    const localTime = now - animStartTime;
                    if (useExternalSyncLogCount === 0 && extAudio.lastSyncTime > 0) {
                      console.log(`⚠️ [Avatar] External sync STALE (${(now - extAudio.lastSyncTime).toFixed(0)}ms old), using local: ${(localTime / 1000).toFixed(2)}s`);
                      useExternalSyncLogCount = 99; // Prevent repeated warnings
                    }
                    return localTime;
                  };

                  // Capture this animation's ID so the RAF loop stops if a newer animation starts
                  const thisAnimId = currentAnimationIdRef.current;
                  const animateFrame = () => {
                    // Check if speaking was cancelled OR a newer animation has started
                    // Without the ID check, old RAF loops keep running alongside new ones,
                    // both writing to the same morph targets and causing stiff/reduced movement
                    if (!isSpeakingRef.current || currentAnimationIdRef.current !== thisAnimId) {
                      if (currentAnimationIdRef.current !== thisAnimId) {
                        console.log(`[M4] RAF loop ${thisAnimId} stopping (superseded by ${currentAnimationIdRef.current})`);
                      }
                      return;
                    }

                    const now = performance.now();

                    // Phase 4: Frame rate limiting - skip if less than MIN_FRAME_INTERVAL since last frame
                    const timeSinceLastFrame = now - lastFrameTime;
                    if (timeSinceLastFrame < MIN_FRAME_INTERVAL) {
                      animFrameId = requestAnimationFrame(animateFrame);
                      return;
                    }

                    // Phase 4: Delta time clamping to prevent large jumps
                    const clampedDelta = Math.min(timeSinceLastFrame / 1000, MAX_DELTA_TIME);
                    lastFrameTime = now;

                    // Phase 1.3: Use audio-synced time when available
                    const elapsed = getAnimationTime();
                    const progress = Math.min(elapsed / animDuration, 1.0);
                    const currentTimeSeconds = elapsed / 1000;

                    // Find the two frames we're between for interpolation
                    // OPTIMIZATION: Start search from last known frame instead of 0
                    // This is O(1) for sequential playback instead of O(n)
                    let prevFrameIdx = lastFrameIdx;
                    let nextFrameIdx = lastFrameIdx;

                    // Search forward from last frame
                    for (let i = lastFrameIdx; i < blendData.length - 1; i++) {
                      if (blendData[i].time !== undefined && blendData[i].time <= currentTimeSeconds) {
                        prevFrameIdx = i;
                        nextFrameIdx = Math.min(i + 1, blendData.length - 1);
                      } else {
                        break;
                      }
                    }

                    // Update last frame index for next iteration
                    lastFrameIdx = prevFrameIdx;

                    const prevFrame = blendData[prevFrameIdx];
                    const nextFrame = blendData[nextFrameIdx];

                    if (prevFrame && prevFrame.blendshapes && speechMorphMeshes.length > 0) {
                      // Calculate interpolation factor between frames
                      const prevTime = prevFrame.time || 0;
                      const nextTime = nextFrame?.time || prevTime;
                      const frameDuration = nextTime - prevTime;
                      const interpFactor = frameDuration > 0
                        ? Math.min(1, (currentTimeSeconds - prevTime) / frameDuration)
                        : 0;

                      // IMPROVED SILENCE DETECTION: Detect when we should be in a pause
                      // Method 1: Large gap between consecutive frames (punctuation pauses in Azure TTS)
                      const isLargeFrameGap = frameDuration > SILENCE_GAP_THRESHOLD;

                      // Method 2: Animation time is ahead of where we should be
                      // This catches cases where frames are dense but we're running ahead
                      const timeSincePrevFrame = currentTimeSeconds - prevTime;
                      const timeUntilNextFrame = nextTime - currentTimeSeconds;

                      // We're in a silence gap if:
                      // - Frame gap is large (> 100ms) AND we're past the midpoint, OR
                      // - We're more than 50ms past the previous frame and next frame is > 100ms away
                      const isPastMidpoint = interpFactor > 0.5;
                      const isInSilenceGap = (isLargeFrameGap && isPastMidpoint) ||
                        (timeSincePrevFrame > 0.05 && timeUntilNextFrame > SILENCE_GAP_THRESHOLD);

                      // Calculate silence decay intensity based on how far into the gap we are
                      let silenceDecayIntensity = 0;
                      if (isInSilenceGap) {
                        if (isLargeFrameGap) {
                          // For large gaps, decay based on progress through the gap
                          const gapProgress = Math.min(1, timeSincePrevFrame / frameDuration);
                          // Start decay after 30% through the gap
                          if (gapProgress > 0.3) {
                            silenceDecayIntensity = Math.min(1, (gapProgress - 0.3) / 0.4); // Faster ramp to full decay
                          }
                        } else {
                          // For small gaps where we're just ahead, use fixed intensity
                          silenceDecayIntensity = 0.5;
                        }
                      }

                      // Apply each blendshape with interpolation, scaling, and smoothing
                      // CRITICAL FIX: Iterate through ALL blendshapes from BOTH frames
                      // Previously only iterated prevFrame, missing blendshapes that appear in nextFrame
                      const allBlendshapeKeys = new Set([
                        ...Object.keys(prevFrame.blendshapes),
                        ...(nextFrame?.blendshapes ? Object.keys(nextFrame.blendshapes) : [])
                      ]);

                      allBlendshapeKeys.forEach((key) => {
                        const prevValue = prevFrame.blendshapes[key];
                        const morphTargets = getMorphTargets(key);
                        const morphName = morphTargets[0] || key;
                        const isMouthMorph = morphTargets.some((candidate) => MOUTH_MORPHS.has(candidate));
                        // Use helper function that supports both named and numeric morph targets
                        const idx = getMorphTargetIndex(key);

                        if (idx !== undefined) {
                          // Get both frame values, defaulting to 0 if not present
                          const prevVal = prevValue !== undefined ? Number(prevValue) : 0;
                          const nextValue = nextFrame?.blendshapes?.[key] ?? prevVal;

                          // LINEAR INTERPOLATION between frames (like Three.js InterpolateLinear)
                          let interpolatedValue = lerp(
                            prevVal,
                            Number(nextValue) || 0,
                            interpFactor
                          );

                          // Slice 9 (May 12 2026): capture pre-decay value for attribution
                          const preDecayValue = interpolatedValue;

                          // SILENCE HANDLING: During gaps, decay mouth morphs to neutral
                          // This prevents the avatar from holding mouth open during pauses (commas, periods, etc.)
                          if (silenceDecayIntensity > 0 && isMouthMorph) {
                            // Decay towards neutral (0) - mouth should close during pauses
                            const effectiveDecay = silenceDecayIntensity * SILENCE_DECAY_RATE * 2; // Faster decay
                            interpolatedValue = lerp(interpolatedValue, 0, effectiveDecay);
                          }

                          // Slice 9: capture post-decay, pre-scale value
                          const postDecayValue = interpolatedValue;

                          // Apply PHONEME_SCALING for natural movement
                          const scale = PHONEME_SCALING[morphName] || 1.0;
                          interpolatedValue *= scale;

                          // Phase 4: IMPROVED TEMPORAL SMOOTHING
                          // Use 35% previous + 65% new for smoother animation
                          const previousValue = prevMorphValues[idx] ?? interpolatedValue;
                          const smoothedValue = lerp(previousValue, interpolatedValue, TEMPORAL_SMOOTHING_FACTOR);

                          // Clamp to valid range [0, 1]
                          const finalValue = Math.min(Math.max(smoothedValue, 0), 1);

                          // Apply to ALL meshes with morph targets (body, teeth, eyes, etc.)
                          // IMPORTANT: Each mesh has its own morphTargetDictionary and some
                          // UE5 exports keep unnamed `body_morph_N` keys, so resolve per mesh.
                          speechMorphMeshes.forEach(mesh => {
                            if (mesh.morphTargetInfluences && mesh.morphTargetDictionary) {
                              let meshIdx = resolveMorphTargetIndex(mesh.morphTargetDictionary, key);
                              // Final fallback for numeric-only dictionaries (Shenya-style)
                              if (meshIdx === undefined && idx < mesh.morphTargetInfluences.length) {
                                const hasNumericKeys = Object.keys(mesh.morphTargetDictionary).some(k => /^\d+$/.test(k));
                                if (hasNumericKeys) meshIdx = idx;
                              }

                              if (meshIdx !== undefined && meshIdx < mesh.morphTargetInfluences.length) {
                                mesh.morphTargetInfluences[meshIdx] = finalValue;
                              }
                            }
                          });
                          // Stuck-jaw warn: kept as a one-line trip-wire if Slice
                          // 10's speech-restore ever stops working (regressions
                          // would re-surface the symptom Keith hit on May 11/12).
                          // Fires only on the failure signature (raw source wants
                          // jaw open but final value is near zero), and only once
                          // per Avatar instance to avoid spamming the console.
                          if (key === 'jawOpen' && !stuckJawWarnedRef.current
                              && finalValue < 0.05 && Number(nextValue) > 0.30) {
                            console.warn('[Avatar] jawOpen stuck-low: nextValue='
                              + Number(nextValue).toFixed(2) + ' finalValue='
                              + finalValue.toFixed(2) + ' (Slice 10 speech-restore may be regressed)');
                            stuckJawWarnedRef.current = true;
                          }
                          prevMorphValues[idx] = finalValue;
                        } else if (key === 'jawOpen' && !missingJawOpenMorphLogged) {
                          console.warn('[Avatar] jawOpen morph target missing on active model');
                          missingJawOpenMorphLogged = true;
                        }
                      });

                      // ADDITIONAL: During extended silence, proactively reset ALL mouth morphs
                      // This handles cases where certain morphs weren't in the blendshape data
                      if (silenceDecayIntensity > 0.7 && timeSincePrevFrame > SILENCE_FULL_RESET_THRESHOLD) {
                        MOUTH_MORPHS.forEach(silenceMorphName => {
                          // Apply decay to ALL meshes with morph targets - lookup by NAME in each mesh
                          speechMorphMeshes.forEach(mesh => {
                            if (mesh.morphTargetInfluences && mesh.morphTargetDictionary) {
                              const meshIdx = resolveMorphTargetIndex(mesh.morphTargetDictionary, silenceMorphName);
                              if (meshIdx !== undefined && meshIdx < mesh.morphTargetInfluences.length) {
                                const currentVal = mesh.morphTargetInfluences[meshIdx];
                                if (currentVal > 0.01) {
                                  // Decay to neutral faster during extended silence
                                  mesh.morphTargetInfluences[meshIdx] = lerp(currentVal, 0, SILENCE_DECAY_RATE * 1.5);
                                }
                              }
                            }
                          });
                        });
                      }
                    }

                    // Continue animation while:
                    // 1. Not all frames processed (lastFrameIdx < totalFrames - 1) OR progress not complete
                    // 2. Speaking ref is still true (not cancelled by bargein)
                    // FIX: Use frame index completion check to ensure last word is always animated
                    const hasMoreFrames = lastFrameIdx < totalFrames - 1;
                    const animationNotComplete = progress < 1.0 || hasMoreFrames;

                    if (animationNotComplete && isSpeakingRef.current) {
                      animFrameId = requestAnimationFrame(animateFrame);
                      // Track RAF ID with controller
                      if (animFrameId) {
                        animationController.trackRafId(animFrameId);
                      }
                    }
                  };

                  animFrameId = requestAnimationFrame(animateFrame);
                  // Track initial RAF ID with controller
                  if (animFrameId) {
                    animationController.trackRafId(animFrameId);
                  }

                  // Store cleanup for this animation
                  currentAnimationCleanupRef.current = () => {
                    if (animFrameId) {
                      animationController.untrackRafId(animFrameId);
                      cancelAnimationFrame(animFrameId);
                    }
                    prevMorphValues = {}; // Clear smoothing state
                  };
                } else {
                  console.warn('⚠️ Could not find HG_Body mesh for direct morph animation');
                }
              } else {
                console.warn('⚠️ No bodyClip or empty tracks - skipping body animation');
              }

              // TEETH animation (tongue only)
              if (teethClip && teethClip.tracks.length > 0) {
                const actionTeeth = mixer.clipAction(teethClip);
                actionTeeth.setLoop(THREE.LoopOnce, 1);
                actionTeeth.clampWhenFinished = true;
                actionTeeth.play();
              }

            } catch (err: any) {
              console.error('❌ ERROR in Three.js animation:', err?.message || err);
              console.error('❌ Stack:', err?.stack);
            }

            // Track that animation is playing (for cleanup)
            audioSyncRef.current.isPlaying = true;

            // Calculate animation duration from blend data using server timestamps
            let duration = 0;
            if (blendData.length > 0) {
              const lastFrame = blendData[blendData.length - 1];
              duration = lastFrame.time ? lastFrame.time * 1000 : (blendData.length / 60) * 1000;
            }

            // Set up finish handler - use timeout-based cleanup instead of mixer finished event
            // The mixer's 'finished' event fires for ANY animation, not just speaking, causing premature cleanup
            let cleanupTimeout: NodeJS.Timeout | null = null;
            let checkInterval: NodeJS.Timeout | null = null;
            let hasCleanedUp = false;

            const cleanup = () => {
              if (hasCleanedUp) {
                return;
              }
              hasCleanedUp = true;

              // Set flag to prevent re-triggering during cleanup
              isStoppingAnimationRef.current = true;

              // Note: Direct morph animation cleanup is handled by currentAnimationCleanupRef

              if (cleanupTimeout) {
                clearTimeout(cleanupTimeout);
                cleanupTimeout = null;
              }
              if (checkInterval) {
                clearInterval(checkInterval);
                checkInterval = null;
              }
              isSpeakingRef.current = false; // Clear ref immediately
              setIsVoiceCallAnimationInProgress(false);
              currentAnimationHandoffTokenRef.current = null;
              currentAnimationCleanupRef.current = null;
              lastBlendDataRef.current = null; // Clear last blendData
              audioSyncRef.current.isPlaying = false;

              // Return the mouth to baseline. The speech RAF writes morphs
              // directly (no mixer tracks) and the F4/M4 speech-restore loop
              // copies them from the teeth donor submesh back to the body
              // every frame — without this reset a final vowel frame leaves
              // the jaw hanging open through listening/thinking (Aug 16 2026
              // "mouth opens up comically" defect). Mood/ambient overlays
              // re-apply their own values on the next tick.
              const zeroedMorphs = zeroSpeechOwnedMorphs(gltf?.scene as any);
              if (zeroedMorphs > 0) {
                console.log(`[Avatar] Speech cleanup reset ${zeroedMorphs} leftover speech morph influences`);
              }

              // End speaking state in unified controller
              animationController.endSpeaking();

              // Clear protection and stop speaking animation state
              // Only clear protection if audio has actually ended (not just paused)
              const audioState = getAudioPlaybackState();
              if (audioState.isEnded || !isAudioActuallyPlaying()) {
                animationStateManagerRef.current.clearProtection();
                animationStateManagerRef.current.stopAnimation(AnimationType.SPEAKING, true);
              }
              if (isVoiceLikeMode) {
                voiceNonSpeechAnimationRef.current?.setMode('idle');
              }

              // Reset flag after cleanup completes
              setTimeout(() => {
                isStoppingAnimationRef.current = false;
              }, 100);
            };

            // Use timeout-based cleanup with actual animation duration
            // teethClipForDuration may be null when skipTeethAnimation=true, so use bodyClipForDuration
            const clipForDuration = bodyClipForDuration || teethClipForDuration;
            const maxClipDuration = clipForDuration ? clipForDuration.duration * 1000 : duration;
            const actualDuration = Math.max(duration, maxClipDuration);

            console.log('Setting up animation cleanup', {
              calculatedDuration: duration,
              maxClipDuration: maxClipDuration,
              actualDuration: actualDuration,
              clipDuration: clipForDuration?.duration || 0
            });

            // Primary cleanup: use timeout based on actual duration
            // But also check audio - if audio is still playing, extend the timeout
            // CRITICAL: Capture animation ID to check if this animation is still current when timeout fires
            const timeoutAnimationId = thisAnimationId;
            cleanupTimeout = setTimeout(() => {
              // CRITICAL: Check if a new animation has started - if so, skip cleanup
              if (currentAnimationIdRef.current !== timeoutAnimationId) {
                console.log('Cleanup timeout fired but animation ID changed - skipping (old:', timeoutAnimationId, 'current:', currentAnimationIdRef.current, ')');
                return;
              }

              const audioState = getAudioPlaybackState();
              const audioStillPlaying = isAudioActuallyPlaying();

              // If audio is still playing, extend the timeout rather than cleaning up
              if (audioStillPlaying && !audioState.isEnded) {
                const audioRemaining = audioState.duration - audioState.currentTime;
                if (audioRemaining > 0) {
                  console.log('Animation timeout reached but audio still playing - extending timeout', {
                    audioRemaining,
                    audioCurrentTime: audioState.currentTime,
                    audioDuration: audioState.duration
                  });
                  // Extend timeout to match audio duration
                  cleanupTimeout = setTimeout(() => {
                    // Check animation ID again for extended timeout
                    if (currentAnimationIdRef.current !== timeoutAnimationId) {
                      console.log('Extended cleanup timeout fired but animation ID changed - skipping');
                      return;
                    }
                    console.log('Extended timeout complete - cleaning up after audio finished', {
                      calculatedDuration: duration,
                      actualDuration: actualDuration,
                      clipDuration: clipForDuration?.duration || 0
                    });
                    cleanup();
                    setAnimateCallSpeak(false);
                    playIdleAnimation();
                  }, audioRemaining + 500); // Add 500ms buffer
                  return;
                }
              }

              console.log('Voice call animation cleanup timeout - animations should be complete', {
                calculatedDuration: duration,
                actualDuration: actualDuration,
                teethClipDuration: teethClipForDuration?.duration,
                audioStillPlaying,
                audioEnded: audioState.isEnded
              });
              cleanup();
              setAnimateCallSpeak(false);
              playIdleAnimation();
            }, actualDuration + 1000); // Add 1 second buffer to ensure completion

            // Secondary check: monitor elapsed time and audio state
            // For direct morph animation, we use time-based cleanup
            const animationStartTime = Date.now();
            const intervalAnimationId = thisAnimationId;
            checkInterval = setInterval(() => {
              // CRITICAL: Check if a new animation has started - if so, stop this interval
              if (currentAnimationIdRef.current !== intervalAnimationId) {
                console.log('Check interval fired but animation ID changed - stopping interval (old:', intervalAnimationId, 'current:', currentAnimationIdRef.current, ')');
                clearInterval(checkInterval!);
                checkInterval = null;
                return;
              }

              const elapsed = Date.now() - animationStartTime;
              const pastDuration = elapsed >= actualDuration * 0.98; // 98% of duration

              // CRITICAL: Also check if audio is still playing - don't cleanup if audio is active
              const audioState = getAudioPlaybackState();
              const audioStillPlaying = isAudioActuallyPlaying();

              // Only cleanup if:
              // 1. We're past the animation duration
              // 2. Audio has finished (or is not playing)
              if (pastDuration && (!audioStillPlaying || audioState.isEnded)) {
                console.log('Direct morph animation complete, cleaning up', {
                  elapsed,
                  actualDuration,
                  audioStillPlaying,
                  audioEnded: audioState.isEnded,
                  animationId: intervalAnimationId
                });
                clearInterval(checkInterval!);
                checkInterval = null;
                if (cleanupTimeout) {
                  clearTimeout(cleanupTimeout);
                  cleanupTimeout = null;
                }
                cleanup();
                setAnimateCallSpeak(false);
                playIdleAnimation();
              } else if (pastDuration && audioStillPlaying) {
                // Log when animation is done but audio is still playing (shouldn't cleanup yet)
                console.log('Animation finished but audio still playing - waiting for audio to finish', {
                  elapsed,
                  actualDuration,
                  audioStillPlaying,
                  audioCurrentTime: audioState.currentTime,
                  audioDuration: audioState.duration
                });
              }
            }, 200); // Check every 200ms (less aggressive)

            // Store cleanup function for potential early termination
            currentAnimationCleanupRef.current = cleanup;

            if (verboseAvatarDebug) {
              console.log('Voice call animations started (raw blend data from server)');
            }

            // Return cleanup function for useEffect
            return cleanup;
          } else {
            console.error('❌ Failed to create teethClip! blendData.length:', blendData.length, 'morphTargetDictionaryLowerTeeth keys:', morphTargetDictionaryLowerTeeth ? Object.keys(morphTargetDictionaryLowerTeeth).length : 0);
          }
        } else {
          console.error('❌ No mixer available for animation!');
        }

      } catch (error) {
        console.error('Error in animateCallSpeak:', error);
        isSpeakingRef.current = false; // Clear ref on error
        setIsVoiceCallAnimationInProgress(false);
        currentAnimationHandoffTokenRef.current = null;
        currentAnimationCleanupRef.current = null;
        setAnimateCallSpeak(false);
        voiceNonSpeechAnimationRef.current?.setMode('idle');

        // Clear protection and stop speaking animation state on error
        animationStateManagerRef.current.clearProtection();
        animationStateManagerRef.current.stopAnimation(AnimationType.SPEAKING, true);
      }
    };

    processAnimation();
  }, [animateCallSpeak, isMobileCall, isWebrtcCall, isInVoiceCall, morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth, playIdleAnimation, setAnimateCallSpeak, stopAllAnimations]);

  // Listen for avatar stop event from parent page (bargein handling)
  useEffect(() => {
    const handleStopAvatar = (event: any) => {
      const detail = event.detail;
      console.log('🛑 Avatar stop event received:', detail);

      // Check if speaking animation is protected
      const isProtected = animationStateManagerRef.current.isProtected();
      const currentState = animationStateManagerRef.current.getCurrentState();

      // Only stop if not protected OR if audio has actually stopped
      const audioState = getAudioPlaybackState();
      const shouldStop = !isProtected || audioState.isEnded || !isAudioActuallyPlaying();

      if (!shouldStop) {
        console.log('Avatar stop event ignored - speaking animation is protected and audio is still playing');
        return;
      }

      // Cancel ALL RAF loops immediately via unified controller
      animationController.cancelAllRafIds();
      animationController.endSpeaking();

      // Clean up current animation if running
      if (currentAnimationCleanupRef.current) {
        console.log('Cleaning up current animation due to stop event');
        currentAnimationCleanupRef.current();
      }

      // Stop all animations immediately
      if (mixerRef.current) {
        console.log('Stopping avatar animations due to:', detail.stopReason);
        stopAllAnimations();
        isSpeakingRef.current = false; // Clear ref immediately
        setIsVoiceCallAnimationInProgress(false);
        currentAnimationHandoffTokenRef.current = null;
        setAnimateCallSpeak(false);
        voiceNonSpeechAnimationRef.current?.setMode('idle');

        // Barge-in cancels the viseme RAF mid-frame; without an explicit
        // reset the mouth freezes at the last viseme (jaw open mid-vowel)
        // and the speech-restore donor copy keeps it there while the user
        // talks and the bot thinks. See utils/speechMorphReset.ts.
        const zeroedMorphs = zeroSpeechOwnedMorphs(gltf?.scene as any);
        if (zeroedMorphs > 0) {
          console.log(`[Avatar] Barge-in stop reset ${zeroedMorphs} leftover speech morph influences`);
        }

        // Stop audio sync
        audioSyncRef.current.isPlaying = false;

        // Clear protection and stop speaking animation state
        animationStateManagerRef.current.clearProtection();
        animationStateManagerRef.current.stopAnimation(AnimationType.SPEAKING, true);

        // Return to idle after brief pause
        setTimeout(() => {
          if (!isSpeakingRef.current) {
            playIdleAnimation();
          }
        }, 300);
      }
    };

    window.addEventListener('avatar:stop', handleStopAvatar);
    console.log('Avatar stop event listener registered');

    return () => {
      window.removeEventListener('avatar:stop', handleStopAvatar);
    };
  }, [stopAllAnimations, playIdleAnimation]); // Removed isVoiceCallAnimationInProgress from deps to prevent re-registration

  // Listen for viseme animation event from parent page (Visual IVR)
  useEffect(() => {
    const handleAnimateAvatar = (event: any) => {
      const detail = event.detail;
      console.log('🎭 Avatar animate event received with viseme data');

      try {
        // Parse viseme data if it's a string
        const visemeData = typeof detail.visemeData === 'string'
          ? JSON.parse(detail.visemeData)
          : detail.visemeData;

        if (!visemeData || visemeData.length === 0) {
          console.log('No viseme data to animate');
          return;
        }

        console.log('Viseme data frames:', visemeData.length, 'Total duration:', detail.totalDuration, 'ms');

        // Convert visemeData format to blendData format expected by createAnimation
        // Input format from VisualChannel: { timeMs: number, blendShapes: { shapeId: value } }
        // Output format for createAnimation: { time: float (seconds), blendshapes: { shapeId: value } }
        // IMPORTANT: createAnimation expects 'time' in seconds, not 'frame' numbers!
        const blendData = visemeData.map((frame: { timeMs: number; blendShapes: Record<string, number> }) => ({
          time: frame.timeMs / 1000, // Convert ms to seconds (e.g., 16 -> 0.016)
          blendshapes: frame.blendShapes || {}
        }));

        // Trigger the animation using the existing animateCallSpeak mechanism
        // This will use the same animation pipeline as voice calls
        if (setAnimateCallSpeak && blendData.length > 0) {
          setAnimateCallSpeak({
            blendData: blendData,
            handoffToken: detail.messageId || `visual-${Date.now()}` // Use messageId for duplicate detection
          });
          console.log('✅ Animation triggered via setAnimateCallSpeak');
        }

      } catch (error) {
        console.error('Error processing viseme animation:', error);
      }
    };

    window.addEventListener('avatar:animate', handleAnimateAvatar);
    console.log('Avatar animate event listener registered');

    return () => {
      window.removeEventListener('avatar:animate', handleAnimateAvatar);
    };
  }, [setAnimateCallSpeak]);

  // Phase 1.2: Listen for audio time sync events from parent page (Visual IVR)
  // This synchronizes animation timing with external audio playback
  useEffect(() => {
    let syncEventCount = 0;

    const handleAudioTimeSync = (event: any) => {
      const detail = event.detail;
      if (!detail) return;

      const { currentTime, duration, isPlaying } = detail;
      const now = performance.now();

      syncEventCount++;

      // Log first 3 sync events for debugging
    if (verboseAvatarDebug && syncEventCount <= 3) {
      console.log(`🎵 [Avatar] Audio sync event #${syncEventCount}: time=${currentTime?.toFixed(2)}s, duration=${duration?.toFixed(2)}s, playing=${isPlaying}`);
    }

      // Update external audio time tracking
      // SYNC FIX: If we're receiving position events, audio IS playing (assume true if undefined)
      // Reset animationStartOffset (used as "logged" flag) when audio restarts from near beginning
      const shouldResetLogFlag = (currentTime ?? 0) < 0.3 && externalAudioTimeRef.current.currentTime > 0.5;
      externalAudioTimeRef.current = {
        currentTime: currentTime ?? 0,
        duration: duration ?? 0,
        isPlaying: isPlaying !== false, // Default to true if undefined
        lastSyncTime: now,
        animationStartOffset: shouldResetLogFlag ? 0 : externalAudioTimeRef.current.animationStartOffset
      };

    };

    window.addEventListener('avatar:audioTimeSync', handleAudioTimeSync);

    return () => {
      window.removeEventListener('avatar:audioTimeSync', handleAudioTimeSync);
    };
  }, []);

  // Listen for avatar editor config updates from parent page
  useEffect(() => {
    const handleConfigUpdate = (event: CustomEvent) => {
      const detail = event.detail;
      console.log('🎨 Avatar config update received:', detail);

      try {
        const { section, config } = detail;

        // Handle appearance updates (colors, textures)
        if (section === 'appearance' || config?.appearance) {
          const appearance = config?.appearance || config;
          console.log('Appearance update:', appearance);
          // TODO: Apply color tinting to materials when material refs are available
          // For now, dispatch an event that App.tsx can handle to trigger UI updates
          window.dispatchEvent(new CustomEvent('avatar:config:applied', {
            detail: { section: 'appearance', applied: true }
          }));
        }

        // Handle animation config updates -- store in ref for animation loops to read
        if (section === 'animations' || config?.animations) {
          const animations = config?.animations || config;
          console.log('Animation config update:', animations);

          // Merge into the BASE (unscaled) config, then recompute the
          // effective config through the active personality scales.
          const prev = baseAnimationConfigRef.current;
          if (animations.blink) prev.blink = { ...prev.blink, ...animations.blink };
          if (animations.breathing) prev.breathing = { ...prev.breathing, ...animations.breathing };
          if (animations.idle) prev.idle = { ...prev.idle, ...animations.idle };
          if (animations.listening) prev.listening = { ...prev.listening, ...animations.listening };
          if (animations.thinking) prev.thinking = { ...prev.thinking, ...animations.thinking };
          baseAnimationConfigRef.current = { ...prev };
          animationConfigRef.current = applyPersonalityToAnimationConfig(
            baseAnimationConfigRef.current,
            personalityKnobsRef.current,
          );

          // Restart blinking with new config if blink settings changed
          if (animations.blink && morphTargetDictionaryBody) {
            setupNaturalBlinking();
          }

          // Restart idle animations if breathing/idle settings changed
          if ((animations.breathing || animations.idle) && morphTargetDictionaryBody && !isSpeakingRef.current) {
            playIdleAnimation();
          }

          window.dispatchEvent(new CustomEvent('avatar:config:applied', {
            detail: { section: 'animations', applied: true }
          }));
        }

        // Handle sentiment config updates -- configureSentiment stores the
        // full runtime config (enable/producer gates, sensitivity-derived
        // thresholds, mood persistence, expressiveness cap) consumed by
        // handleSentiment, the keyword analyzer, and the moodAnimator.
        if (section === 'sentiment' || config?.sentiment) {
          const sentimentCfg = config?.sentiment || config;
          console.log('Sentiment config update:', sentimentCfg);
          import('../../utils/sentimentAnalyzer').then(({ configureSentiment }) => {
            configureSentiment(sentimentCfg);
          });
          window.dispatchEvent(new CustomEvent('avatar:config:applied', {
            detail: { section: 'sentiment', applied: true }
          }));
        }

        // Handle visual config updates (camera, lighting)
        if (section === 'visual' || config?.visual) {
          const visual = config?.visual || config;
          console.log('Visual config update:', visual);
          window.dispatchEvent(new CustomEvent('avatar:config:applied', {
            detail: { section: 'visual', applied: true }
          }));
        }

        // Notify parent that config was applied
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'avatar.config.applied', payload: detail }, '*');
        }
      } catch (error) {
        console.error('Error processing config update:', error);
      }
    };

    let moodPreviewRevertTimer: ReturnType<typeof setTimeout> | null = null;

    const stopSpeakingForPreview = () => {
      isSpeakingRef.current = false;
      setAnimateCallSpeak(false);
      if (currentAnimationCleanupRef.current) {
        currentAnimationCleanupRef.current();
        currentAnimationCleanupRef.current = null;
      }
    };

    const zeroSpeechMorphs = () => {
      if (!gltf?.scene) return;
      gltf.scene.traverse((node: any) => {
        const influences = node?.morphTargetInfluences;
        const dict = node?.morphTargetDictionary;
        if (!influences || !dict) return;
        for (const morphName of SPEECH_OWNED_MORPH_NAMES) {
          const idx = resolveMorphTargetIndex(dict, morphName);
          if (idx !== undefined && idx < influences.length) {
            influences[idx] = 0;
          }
        }
      });
    };

    const handleMoodPreview = (event: CustomEvent) => {
      if (!event.detail) {
        console.warn('🎭 Avatar mood preview called with no detail');
        return;
      }
      const { mood, duration = 3000 } = event.detail;
      if (!mood) {
        console.warn('🎭 Avatar mood preview called with no mood');
        return;
      }
      console.log('🎭 Avatar mood preview requested:', mood, 'duration:', duration, {
        hasMixer: Boolean(mixerRef.current),
        morphKeys: morphTargetDictionaryBody ? Object.keys(morphTargetDictionaryBody).length : 0,
        bodyMeshName,
      });

      try {
        stopSpeakingForPreview();
        zeroSpeechMorphs();
        facePreviewHoldRef.current = true;
        muteIdleMorphActionsForGesture();
        if (moodPreviewRevertTimer) {
          clearTimeout(moodPreviewRevertTimer);
          moodPreviewRevertTimer = null;
        }
        const mixer = mixerRef.current;
        const morphDict = morphTargetDictionaryBody;

        if (mixer && morphDict) {
          const ref = { current: { getMixer: () => mixer, getMorphTargetDictionary: () => ({ body: morphDict, teeth: morphTargetDictionaryLowerTeeth }) } } as any;
          void applyMoodToAvatar(ref, mood, 'neutral', 800, morphDict, mixer, bodyMeshName);

          window.setTimeout(() => {
            if (!gltf?.scene) return;
            const dump: Record<string, Record<string, number>> = {};
            gltf.scene.traverse((node: any) => {
              const dict = node?.morphTargetDictionary;
              const inf = node?.morphTargetInfluences;
              if (!dict || !inf) return;
              const nz: Record<string, number> = {};
              for (const [name, idx] of Object.entries(dict) as Array<[string, number]>) {
                const v = Number(inf[idx]) || 0;
                if (Math.abs(v) > 0.015) nz[name] = Math.round(v * 1000) / 1000;
              }
              if (Object.keys(nz).length > 0) dump[String(node.name || '(unnamed)')] = nz;
            });
            const body = gltf.scene.getObjectByName(bodyMeshName || 'HG_Body') as THREE.Mesh | undefined;
            const bodyDict = body?.morphTargetDictionary;
            const bodyInf = body?.morphTargetInfluences;
            const bodyMeta = bodyDict && bodyInf ? {
              faceWidthIdx: bodyDict.faceWidth,
              basisIdx: bodyDict['Basis.001'],
              inf0: bodyInf[0],
              hasFaceWidth: Object.prototype.hasOwnProperty.call(bodyDict, 'faceWidth'),
              keyCount: Object.keys(bodyDict).length,
            } : null;
            const overlay = getActiveMoodOverlayBlendshapes();
            const smileResolved = bodyDict ? {
              L: Object.entries(bodyDict).find(([, idx]) => idx === resolveMorphTargetIndex(bodyDict, 'mouthSmile_L'))?.[0],
              R: Object.entries(bodyDict).find(([, idx]) => idx === resolveMorphTargetIndex(bodyDict, 'mouthSmile_R'))?.[0],
              Lval: bodyDict.mouthSmileLeft != null ? bodyInf?.[bodyDict.mouthSmileLeft] : undefined,
              Rval: bodyDict.mouthSmileRight != null ? bodyInf?.[bodyDict.mouthSmileRight] : undefined,
              Rold: bodyDict.mouthSmileRightold != null ? bodyInf?.[bodyDict.mouthSmileRightold] : undefined,
            } : null;
            console.log('🎭 enamel20 morph dump after mood preview:', mood, dump, { overlay, bodyMeta, smileResolved });
            (window as unknown as { __camlinMorphDump?: unknown }).__camlinMorphDump = { mood, dump, overlay, bodyMeta, smileResolved, pipeline: 'enamel20' };
          }, 950);

          moodPreviewRevertTimer = setTimeout(() => {
            void applyMoodToAvatar(ref, 'neutral', mood, 800, morphDict, mixer, bodyMeshName);
            facePreviewHoldRef.current = false;
            moodPreviewRevertTimer = null;
          }, duration);

          if (window.parent !== window) {
            window.parent.postMessage({ type: 'avatar.mood.changed', payload: { mood } }, '*');
          }
        } else {
          console.warn('🎭 Mood preview skipped — mixer or morph dict not ready');
          stopMoodOverlay();
        }
      } catch (error) {
        console.error('Error previewing mood:', error);
      }
    };

    const handleBlendshapeSet = (event: CustomEvent) => {
      const { blendshapes } = event.detail;
      console.log('🔧 Direct blendshape update:', Object.keys(blendshapes).length, 'shapes');

      try {
        if (!morphTargetDictionaryBody || !gltf?.scene) {
          console.warn('handleBlendshapeSet: morphTargetDictionaryBody or gltf.scene not ready');
          return;
        }

        // Find the body mesh and apply blendshapes directly to morphTargetInfluences
        gltf.scene.traverse((node: any) => {
          if (node instanceof THREE.SkinnedMesh && (node.name.includes('Body') || node.name.includes('HG_Body'))) {
            const influences = node.morphTargetInfluences;
            if (!influences) return;

            Object.entries(blendshapes).forEach(([key, value]) => {
              const idx = resolveMorphTargetIndex(morphTargetDictionaryBody, key);
              if (idx !== undefined && influences[idx] !== undefined) {
                influences[idx] = Number(value);
              }
            });
          }
        });
      } catch (error) {
        console.error('Error setting blendshapes:', error);
      }
    };

    let expressionPreviewOverlay: { stop: () => void } | null = null;
    let expressionRevertTimer: ReturnType<typeof setTimeout> | null = null;

    const handleExpressionPreview = (event: CustomEvent) => {
      const { blendshapes, duration = 60000 } = event.detail || {};
      if (!blendshapes || Object.keys(blendshapes).length === 0) return;

      const mixer = mixerRef.current;
      if (!mixer) return;

      try {
        if (expressionPreviewOverlay) {
          expressionPreviewOverlay.stop();
          expressionPreviewOverlay = null;
        }
        if (expressionRevertTimer) {
          clearTimeout(expressionRevertTimer);
          expressionRevertTimer = null;
        }

        stopSpeakingForPreview();
        zeroSpeechMorphs();
        facePreviewHoldRef.current = true;
        muteIdleMorphActionsForGesture();

        const overlay = applyBlendshapeOverlay(mixer, blendshapes, 350, bodyMeshName, 1);
        if (!overlay) return;
        expressionPreviewOverlay = overlay;

        expressionRevertTimer = setTimeout(() => {
          expressionPreviewOverlay?.stop();
          expressionPreviewOverlay = null;
          facePreviewHoldRef.current = false;
        }, duration);
      } catch (error) {
        console.error('Error previewing expression:', error);
      }
    };

    const handleReset = () => {
      console.log('🔄 Avatar reset to idle');
      getGestureController().stop();
      gestureMorphApplyRef.current = null;
      skeletalGestureRef.current = null;
      if (expressionPreviewOverlay) {
        expressionPreviewOverlay.stop();
        expressionPreviewOverlay = null;
      }
      if (expressionRevertTimer) {
        clearTimeout(expressionRevertTimer);
        expressionRevertTimer = null;
      }
      if (moodPreviewRevertTimer) {
        clearTimeout(moodPreviewRevertTimer);
        moodPreviewRevertTimer = null;
      }
      stopSpeakingForPreview();
      zeroSpeechMorphs();
      facePreviewHoldRef.current = false;
      stopMoodOverlay();
      restoreIdleMorphActionsAfterGesture();
      stopAllAnimations();
      playIdleAnimation();

      // Notify parent
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'avatar.mood.changed', payload: { mood: 'neutral' } }, '*');
      }
    };

    // Handle speak command from builder preview - trigger TTS playback
    const handleSpeak = async (event: CustomEvent) => {
      const { text, ssml, voice } = event.detail;
      console.log('🗣️ Avatar speak command received:', text, 'voice:', voice);

      if (!text) {
        console.warn('No text to speak');
        return;
      }

      try {
        const speechText = getSpeechText(text);
        // Drive the avatar's emoji/expression overlay from the ORIGINAL
        // text (with emoji intact), in parallel with shipping the
        // emoji-stripped `speechText` to TTS. This is the local-browser-
        // TTS sibling of the host-speak dispatch in `useLiveListenControls`
        // — without it, hosts that drive the avatar via the imperative
        // `avatarRef.current.speak(text)` path (Living Notes / nua-workflow
        // sim mode, builder preview) get a clean voice but no smile/laugh
        // animation when the assistant uses an emoji.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('avatar:chat:content', {
            detail: { text, source: 'bot', origin: 'avatar-speak' }
          }));
        }
        // Stop any current animations and prepare for speaking
        stopAllAnimations();
        isSpeakingRef.current = true;

        // Get TTS URL from the host app or use a same-origin fallback.
        // Local dev keeps the direct runtime-service port for convenience.
        // Production should stay on the current host domain rather than the retired camlin.ai domain.
        const isLocalDev = window.location.hostname === 'localhost';
        const defaultTtsUrl = isLocalDev ? 'http://localhost:5075' : window.location.origin;
        const ttsUrl = (window as any).__CAMLIN_TTS_URL__ || defaultTtsUrl;
        const talkEndpoint = `${ttsUrl}/talk`;

        console.log('🎵 Calling TTS endpoint:', talkEndpoint, 'with voice:', voice);

        // Import axios dynamically to avoid bundling issues
        const { default: axios } = await import('axios');
        // Pass voice parameter if provided (Azure neural voice name like 'en-AU-WilliamNeural')
        const ttsResponse = await axios.post(talkEndpoint, { text: speechText, voice: voice || undefined });

        if (ttsResponse.data?.blendData) {
          console.log('✅ TTS response received with viseme data');

          // Detect and play gesture from spoken text
          const spokenGesture = detectGestureFromText(text);
          if (spokenGesture) {
            window.dispatchEvent(new CustomEvent('avatar:gesture', {
              detail: { gesture: spokenGesture, text }
            }));
          }

          // Trigger speaking animation with viseme data
          setAnimateCallSpeak({ blendData: ttsResponse.data.blendData });

          // Set audio source to play
          if (ttsResponse.data.filename) {
            // Clean up filename - remove leading slash if present
            const cleanFilename = ttsResponse.data.filename.startsWith('/')
              ? ttsResponse.data.filename.substring(1)
              : ttsResponse.data.filename;
            const audioUrl = `${ttsUrl}/audio/${cleanFilename}`;
            console.log('🔊 Setting audio source:', audioUrl);
            setAudioSource(audioUrl);
          } else if (ttsResponse.data.audioData) {
            // Base64 audio data
            const audioUrl = `data:audio/wav;base64,${ttsResponse.data.audioData}`;
            setAudioSource(audioUrl);
          }

          // Notify parent that avatar is speaking
          if (window.parent !== window) {
            window.parent.postMessage({ type: 'avatar.speaking', payload: { text } }, '*');
          }
        } else {
          console.warn('No viseme data in TTS response, using basic animation');
          // Fallback: simple jaw animation
          const fallbackBlendData = {
            body: [
              { frame: 0, blendshapes: { jawOpen: 0 } },
              { frame: 15, blendshapes: { jawOpen: 0.3 } },
              { frame: 30, blendshapes: { jawOpen: 0.1 } },
              { frame: 45, blendshapes: { jawOpen: 0.25 } },
              { frame: 60, blendshapes: { jawOpen: 0 } }
            ],
            lowerTeeth: [{ frame: 0, blendshapes: {} }]
          };
          setAnimateCallSpeak({ blendData: fallbackBlendData });
        }
      } catch (error) {
        console.error('Error generating TTS:', error);
        isSpeakingRef.current = false;
        playIdleAnimation();

        // Notify parent of error
        if (window.parent !== window) {
          window.parent.postMessage({ type: 'avatar.speak.error', payload: { error: String(error) } }, '*');
        }
      }
    };

    // Handle face customization (bone scaling + shape keys)
    const handleFaceCustomize = (event: CustomEvent) => {
      const faceConfig = event.detail;
      console.log('🎨 Face customization received:', faceConfig);

      // Store in persistent ref for shape key application in useFrame
      Object.entries(faceConfig).forEach(([key, value]) => {
        if (typeof value === 'number') {
          faceCustomizationRef.current[key] = value;
        }
      });

      // Apply bone-based face customization (faceWidth, faceHeight, jawWidth, eyeSpacing)
      if (skeletonRef.current) {
        applyBoneFaceCustomization(skeletonRef.current, faceCustomizationRef.current);
      } else {
        console.warn('🦴 No skeleton available for bone-based face customization');
      }

      console.log('✅ Face customization applied');
    };

    // Auto-trigger mood from runtime sentiment events (visual.sentiment → avatar:sentiment).
    //
    // Previous behaviour used a closure-scoped `lastSentimentMood` that
    // was never reset, so once the avatar saw the FIRST non-neutral label
    // (usually `positive`) it latched there forever: subsequent labels
    // were deduped against the stored value and silently dropped. This
    // matched the user-reported symptom "the odd smile when sentiment is
    // shown at a basic level but no other sentiment really shows up".
    //
    // Fix: (a) promote to a ref so it survives re-renders but can be
    // reset, (b) allow re-trigger when the same label arrives with a
    // meaningfully higher confidence score (so sentiment strength
    // changes still animate), and (c) track the previous mood so the
    // moodAnimator can cross-fade correctly instead of always
    // transitioning from 'neutral'.
    const handleSentiment = (event: CustomEvent) => {
      const { label, score } = event.detail || {};
      if (!label) return;

      // Sentiment settings (Aug 17 2026): the Mood Reactions panel's enable
      // toggle + producer choice gate server-produced `visual.sentiment`
      // events here. Default (enabled, producer 'server') keeps the
      // pre-knob behaviour; 'client'/'off' or enabled=false means server
      // events must not move the face.
      if (!isServerSentimentConsumerActive()) return;

      // While the USER is talking, don't move the face. Server sentiment
      // fires per final utterance, and STT emits finals mid-turn while the
      // caller keeps speaking — applying a mood then pins an expression on
      // the avatar exactly when it should read attentive-neutral. The mood
      // simply drops; the next post-turn sentiment event animates normally.
      if (userTalkingRef.current) {
        console.log('🎭 Sentiment received while user talking — skipped (attentive-neutral)');
        return;
      }

      // Map runtime sentiment (positive/negative/neutral) to extended mood states
      const sentimentToMood: Record<string, string> = {
        positive: 'positive',
        negative: 'negative',
        neutral: 'neutral',
        frustrated: 'frustrated',
        confused: 'confused',
        concerned: 'concerned',
        empathetic: 'empathetic',
        celebration: 'celebration',
        concentration: 'concentration',
        happy: 'celebration',
        sad: 'empathetic',
        angry: 'frustrated',
        surprised: 'surprised',
        // Voice-prosody aliases (producer C)
        fear: 'concerned',
        disgust: 'negative',
      };
      const mood = sentimentToMood[label] || 'neutral';
      const previousMood = lastSentimentMoodRef.current;
      const confidence = typeof score === 'number' ? score : 0;
      // Confidence gates derive from the sensitivity slider (delta-mapped so
      // the default is bit-identical to the old hardcoded values):
      // - minConfidence (default 0): first-application gate. At low
      //   sensitivity only emphatic emotion moves the face.
      // - replayThreshold (default 0.6): dedupe unless confidence jumped
      //   noticeably — same label coming back stronger should get a fresh
      //   pulse rather than being silently dropped.
      const gates = getServerSentimentGates();
      if (mood !== 'neutral' && mood !== previousMood && confidence < gates.minConfidence) {
        return;
      }
      if (mood === previousMood && confidence < gates.replayThreshold) {
        return;
      }
      lastSentimentMoodRef.current = mood;

      const mixer = mixerRef.current;
      const morphDict = morphTargetDictionaryBody;
      if (!mixer || !morphDict) return;

      import('../../utils/moodAnimator').then(({ applyMoodToAvatar }) => {
        // Conversation-sentiment moods are a REACTION, not a resting face:
        // hold then fade back, instead of pinning until the next sentiment
        // event. Without the decay, one server "positive" result (which fires
        // after most cooperative user answers) held a big smile through
        // listening / thinking / idle for the rest of the turn — the Aug 16
        // 2026 Visual IVR "silly smile" defect. Editor previews keep their own
        // pinned path (handleMoodPreview) and are unaffected.
        //
        // Hold time (moodPersistence, default 8000ms) and transition speed
        // (transitionDuration, default 800ms) come from the Mood Reactions
        // settings via configureSentiment (Aug 17 2026).
        const sentimentRuntime = getSentimentRuntimeConfig();
        applyMoodToAvatar(
          { current: {} } as any, mood as any, previousMood as any,
          sentimentRuntime.transitionDuration, morphDict, mixer, bodyMeshName,
          mood === 'neutral' ? undefined : {
            decayAfterMs: sentimentRuntime.moodPersistence,
            decayDurationMs: 1500,
            onDecayed: () => {
              // The visible face is back at baseline; let the same label
              // re-trigger a fresh pulse instead of being deduped forever.
              lastSentimentMoodRef.current = 'neutral';
            },
          }
        );
      });
    };

    // Accept mood overrides from runtime or editor (avatar:moods:configure events)
    const handleMoodsConfigure = (event: CustomEvent) => {
      const overrides = event.detail?.overrides;
      if (overrides && typeof overrides === 'object') {
        setMoodOverrides(overrides);
      }
    };

    // Personality (Aug 14 2026): resolve the sparse settings `personality`
    // record into concrete runtime knobs and apply them. Dispatched by
    // replayAvatarRuntimeConfig (settings chain) and by the builder's
    // Animation Tester. `detail.personality: null` resets to no personality.
    const handlePersonalityConfigure = (event: CustomEvent) => {
      const detail = event.detail || {};
      if (detail.avatarInstanceId && detail.avatarInstanceId !== avatarInstanceId) {
        return;
      }
      const personality = detail.personality ?? detail.config?.personality ?? null;
      const knobs = resolvePersonalityKnobs(personality);
      // replayAvatarRuntimeConfig dispatches this event on EVERY replay
      // (null when no personality is configured). Skip the reset work when
      // there is nothing to reset, so personality-free sessions don't
      // restart blinking/idle on each config replay.
      if (!knobs && !personalityKnobsRef.current) return;
      // Idempotency (Aug 16 2026): config replays arrive repeatedly during a
      // call (visual.linked, mid-call republish, multiple dispatchers with
      // separate dedup keys). Re-applying UNCHANGED knobs restarted the
      // blink/idle loops and stomped the listening animation — Keith's
      // "listening and other animations don't fire correctly" Visual IVR
      // defect. Only re-apply when the resolved knobs actually changed.
      if (personalityKnobsEqual(knobs, personalityKnobsRef.current)) return;
      personalityKnobsRef.current = knobs;

      // Mood-overlay scaling: personality composes with the per-model
      // scale inside moodAnimator (personality × model), never replaces it.
      setPersonalityMoodScales(
        knobs?.expression.intensityScale ?? 1,
        knobs?.expression.perMood ?? {},
      );
      setPersonalityRestingFace(knobs?.expression.restingFace);

      // Idle / blink / listening: recompute the effective animation config
      // from the unscaled base, then restart the loops that cache values.
      animationConfigRef.current = applyPersonalityToAnimationConfig(
        baseAnimationConfigRef.current,
        knobs,
      );
      console.log(
        '🎭 personality applied:',
        knobs ? (knobs.presetId ?? 'custom') : 'none',
        knobs
          ? {
              expression: knobs.expression.intensityScale,
              perMood: knobs.expression.perMood,
              gesture: knobs.gesture,
              animations: animationConfigRef.current,
            }
          : '(reset to base config)',
      );
      if (morphTargetDictionaryBody) {
        setupNaturalBlinking();
        if (!isSpeakingRef.current) {
          playIdleAnimation();
        }
      }
      window.dispatchEvent(new CustomEvent('avatar:config:applied', {
        detail: { section: 'personality', applied: true, presetId: knobs?.presetId ?? null }
      }));
    };

    // Play gesture animations from runtime or test UI (avatar:gesture events)
    const gestureController = getGestureController();
    const RUNTIME_GESTURE_MAP: Record<string, GestureType> = {
      'curious': 'shrug',
      'nodding': 'nod',
      'concerned': 'shake',
      'thinking': 'lean-back',
      'happy': 'nod',
      'waving': 'wave',
    };
    // Slice 8 (May 12 2026): per-gesture global defaults + per-model
    // overrides via ModelConfig.gestureOverrides.
    //
    // - `wave`: globally OFF. Cara was the last opt-in; her wave tears
    //   clothes (Keith, Aug 13 2026 enamel18), so she is now opted out too.
    // - `nod` / `shake` / `lean-forward` / `lean-back`: globally ON, but
    //   models whose hair / clothes / body are bound to a non-shared
    //   skeleton (Arc, Joel, Jason, Kiera, Naomi) opt OUT because
    //   rotating the head bone leaves their hair / clothes / body
    //   behind. Those models set
    //   `gestureOverrides: { 'lean-forward': false, ... }`.
    // - `shrug`: globally ON (no reported bugs); overrides allowed.
    //
    // The right long-term fix is a universal rig solution that walks the
    // duplicated bones in every skeleton (study Cara vs Naomi to find
    // the binding difference). Until then, this policy prevents users
    // from seeing broken animations.
    const GLOBAL_GESTURE_DEFAULTS: Record<GestureType, boolean> = {
      'wave': false,
      'nod': true,
      'shake': true,
      'lean-forward': true,
      'lean-back': true,
      'shrug': true,
    };
    const handleGesture = (event: CustomEvent) => {
      const { gesture, text } = event.detail || {};
      if (!gltf?.scene || !morphTargetDictionaryBody) return;

      let resolvedGesture: GestureType | null = null;
      let explicitlyRequested = false;

      if (['nod', 'shake', 'shrug', 'lean-forward', 'lean-back', 'wave'].includes(gesture)) {
        resolvedGesture = gesture as GestureType;
        explicitlyRequested = true;
      } else if (RUNTIME_GESTURE_MAP[gesture]) {
        resolvedGesture = RUNTIME_GESTURE_MAP[gesture];
        explicitlyRequested = true;
      }

      if (!resolvedGesture && text) {
        resolvedGesture = detectGestureFromText(text);
      }

      if (!resolvedGesture) return;

      // Personality gesture policy (Aug 14 2026). Applied BEFORE the
      // per-model gating below — personality decides WHETHER a gesture
      // plays; the model config decides HOW it can play on this rig.
      const personalityGesture = personalityKnobsRef.current?.gesture ?? null;
      if (personalityGesture) {
        if (personalityGesture.allowed && !personalityGesture.allowed.includes(resolvedGesture)) {
          console.log(
            `🎭 personality gesture policy: '${resolvedGesture}' not in allowed list [${personalityGesture.allowed.join(', ')}] — skipping`,
          );
          return;
        }
        // Frequency gate: only damps AUTO-triggered gestures (text
        // detection). Explicit requests and favoured gestures always play;
        // scales > 1 clamp to 1 (we never invent extra triggers).
        if (!explicitlyRequested && !personalityGesture.favoured.includes(resolvedGesture)) {
          const playProbability = Math.min(1, personalityGesture.idleFrequencyScale);
          if (playProbability < 1 && Math.random() > playProbability) {
            console.log(
              `🎭 personality gesture policy: damped auto gesture '${resolvedGesture}' (frequency ${playProbability})`,
            );
            return;
          }
        }
      }

      // Per-model gesture policy. `gestureOverrides[gesture]` semantics:
      //   • `true`  — force on (skeletal + face)
      //   • `false` — face-only on this model (skip skeletal), e.g. Arc
      //               shrug/shake where bone motion tears embedded clothes
      //   • unset   — fall back to GLOBAL_GESTURE_DEFAULTS (wave defaults
      //               off unless a model opts in with `true`)
      const activeModelConfig = avatar_url ? getModelByUrl(avatar_url) : null;
      const override = activeModelConfig?.gestureOverrides?.[resolvedGesture];
      const globalDefault = GLOBAL_GESTURE_DEFAULTS[resolvedGesture] ?? true;
      let playFace = true;
      let playSkeletal = true;
      if (override === true) {
        playFace = true;
        playSkeletal = true;
      } else if (override === false) {
        playFace = true;
        playSkeletal = false;
      } else if (!globalDefault) {
        playFace = false;
        playSkeletal = false;
      }
      if (!playFace) {
        console.log(
          `🚫 Gesture "${resolvedGesture}" disabled on this model (${activeModelConfig?.name ?? 'unknown'}); skipping`,
        );
        return;
      }
      const shrugMorphAllowlist =
        resolvedGesture === 'shrug'
          ? activeModelConfig?.gestureShrugMorphAllowlist
          : undefined;
      const embeddedOutfitShrug =
        (shrugMorphAllowlist?.length ?? 0) > 0;
      if (!playSkeletal) {
        console.debug(
          embeddedOutfitShrug
            ? `🦴 Gesture 'shrug' — brow-only morph (embedded outfit, no torso sync) on ${activeModelConfig?.name ?? 'unknown'}`
            : `🦴 Gesture '${resolvedGesture}' — face-only morph playback on ${activeModelConfig?.name ?? 'unknown'}`,
        );
      }

      const LOWER_FACE_GESTURE_MORPHS = new Set([
        'mouthPucker', 'mouthFrownLeft', 'mouthFrownRight', 'mouthLeft', 'mouthRight',
        'mouthPressLeft', 'mouthPressRight', 'mouthShrugLower', 'mouthShrugUpper',
        'mouthSmileLeft', 'mouthSmileRight', 'mouthDimpleLeft', 'mouthDimpleRight',
        'jawOpen', 'jawForward', 'jawLeft', 'jawRight',
      ]);

      // MUST target the same mesh as morphTargetDictionaryBody / idle clips.
      // Traversal order used to pick the first mesh with >10 morphs (often
      // torso), so gesture morphs were written to an invisible submesh.
      let targetMesh: THREE.SkinnedMesh | null = null;
      if (bodyMeshName) {
        gltf.scene.traverse((node: any) => {
          if (
            !targetMesh &&
            node.name === bodyMeshName &&
            node.morphTargetInfluences &&
            node.morphTargetInfluences.length > 10
          ) {
            targetMesh = node as THREE.SkinnedMesh;
          }
        });
      }
      if (!targetMesh) {
        targetMesh =
          (m4MorphMeshesRef.current?.[0] as THREE.SkinnedMesh | undefined) ?? null;
      }
      if (!targetMesh) {
        let best: THREE.SkinnedMesh | null = null;
        let bestCount = 0;
        gltf.scene.traverse((node: any) => {
          if (
            (node.isSkinnedMesh || node.isMesh) &&
            node.morphTargetDictionary &&
            node.morphTargetInfluences
          ) {
            const count = node.morphTargetInfluences.length;
            if (count > bestCount) {
              bestCount = count;
              best = node as THREE.SkinnedMesh;
            }
          }
        });
        targetMesh = best;
      }
      if (!targetMesh) return;

      const mesh = targetMesh as THREE.SkinnedMesh;
      const morphDict =
        mesh.morphTargetDictionary ?? morphTargetDictionaryBody;
      const lowerFaceMorphScale =
        activeModelConfig?.gestureLowerFaceMorphScaleByGesture?.[resolvedGesture]
        ?? activeModelConfig?.gestureLowerFaceMorphScale
        ?? 1;
      const baseHeadRotation = headRef.current
        ? headRef.current.rotation.clone()
        : new THREE.Euler();
      const baseHeadPosition = headRef.current
        ? headRef.current.position.clone()
        : new THREE.Vector3();

      // Skeletal (bone) component — wave, shrug, lean-forward, lean-back
      // animate the body, not just the face. We find ALL bones in the scene
      // tree whose name matches each role's candidate list (not just the
      // first), so models that authored hair/clothes as separate skinned
      // meshes — each carrying their own copy of the rig — still follow the
      // gesture. Resolved bone names use a candidate list per role so the
      // same gesture data works on Mixamo, mixamorig:-prefixed, and UE5
      // MetaHuman rigs.
      if (playSkeletal && isSkeletalGesture(resolvedGesture)) {
        const skeletalKf = GESTURE_BONE_KEYFRAMES[resolvedGesture];
        if (skeletalKf) {
          try {
            // Build the set of every bone name in the scene tree once.
            // The head/face mesh's skeleton.bones may only contain head /
            // neck / spine bones, while arm and shoulder bones live in
            // separate skinned-mesh skeletons (body, clothes, hair).
            const allBoneNames = new Set<string>();
            const allBonesByName = new Map<string, THREE.Bone[]>();
            // Case-insensitive index so resolveBoneNameForRole can fall
            // back to fuzzy-matching (e.g. `Spine_03` matches a candidate
            // `spine_03`). We map lowered-name → an actual existing bone
            // name so the canonical lookup table still keys on the
            // real-world casing.
            const lowerToActual = new Map<string, string>();
            gltf.scene.traverse((node: any) => {
              if (!node?.isBone) return;
              const bone = node as THREE.Bone;
              allBoneNames.add(bone.name);
              if (!lowerToActual.has(bone.name.toLowerCase())) {
                lowerToActual.set(bone.name.toLowerCase(), bone.name);
              }
              const arr = allBonesByName.get(bone.name);
              if (arr) arr.push(bone);
              else allBonesByName.set(bone.name, [bone]);
            });
            const sceneHasBone = (n: string) => allBoneNames.has(n);
            const getActualBoneName = (lower: string) =>
              lowerToActual.get(lower) ?? null;

            const byRole: Array<{
              role: BoneRole;
              bones: THREE.Bone[];
              baseQuats: THREE.Quaternion[];
              worldBaseQuats: THREE.Quaternion[];
              frames: Array<{ x: number; y: number; z: number }>;
            }> = [];
            const skippedRoles: BoneRole[] = [];

            const baseSkeletalIntensity = activeModelConfig?.gestureSkeletalIntensity ?? 1;
            const perGestureIntensity =
              activeModelConfig?.gestureSkeletalIntensityByGesture?.[resolvedGesture] ?? 1;
            // Personality amplitude COMPOSES with per-model gating (e.g.
            // Cara's 0.32 shrug × reserved 0.7 = 0.224) — never replaces it.
            const personalityAmplitude = personalityGesture?.amplitudeScale ?? 1;
            const skeletalIntensity = Math.max(
              0,
              Math.min(1.5, baseSkeletalIntensity * perGestureIntensity * personalityAmplitude),
            );
            const suppressRoles = new Set([
              ...(activeModelConfig?.gestureSuppressBoneRoles ?? []),
              ...(activeModelConfig?.gestureSuppressBoneRolesByGesture?.[resolvedGesture] ?? []),
            ]);
            for (const [role, eulerFrames] of Object.entries(skeletalKf.bones)) {
              if (!eulerFrames || eulerFrames.length === 0) continue;
              if (suppressRoles.has(role as BoneRole)) continue;
              const scaledFrames =
                skeletalIntensity === 1
                  ? eulerFrames
                  : eulerFrames.map((f) => ({
                      x: f.x * skeletalIntensity,
                      y: f.y * skeletalIntensity,
                      z: f.z * skeletalIntensity,
                    }));
              const resolvedName = resolveBoneNameForRole(
                role as BoneRole,
                sceneHasBone,
                getActualBoneName,
              );
              if (!resolvedName) {
                skippedRoles.push(role as BoneRole);
                continue;
              }
              // Slice 16 (May 16 2026): THREE.js's GLTFLoader appends
              // `_1`, `_2`, …, `_N` to duplicate bone names when a GLB has
              // multiple armatures (one per skin). For Naomi/Kiera (8 skins,
              // 8 armatures), the bones are `spine_02`, `spine_02_1`, …
              // `spine_02_7`; Joel/Arc have 5. The resolver returns the
              // canonical name (`spine_02`), but `allBonesByName.get('spine_02')`
              // only finds the first instance — the other 7 are invisible
              // to the gesture system. That's why nod/lean only moved the
              // head mesh and left hair/clothes/torso behind on multi-skel
              // rigs (Slice 12's `sharedBase` covered the case where the
              // ONE matched bone was at idle pose but didn't help when
              // siblings were missing entirely).
              //
              // Fix: also collect bones whose name is `<resolvedName>_<digits>`.
              // All N instances share identical world transforms at rest
              // (verified via /tmp/avatar-blake/three-rig-study.mjs against
              // f4-naomi, f4-kiera, m4-joel, arc), so applying the same
              // world-space rotation to all N keeps them in lockstep —
              // body, hair, clothes, all moving together like Cara
              // (which has 1 armature shared across all 15 meshes).
              const matched: THREE.Bone[] = [];
              const matchedNames: string[] = [];
              const primaryBones = allBonesByName.get(resolvedName);
              if (primaryBones && primaryBones.length > 0) {
                matched.push(...primaryBones);
                matchedNames.push(resolvedName);
              }
              const siblingPattern = new RegExp(
                '^' +
                  resolvedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
                  '_\\d+$',
              );
              allBonesByName.forEach((bones, name) => {
                if (siblingPattern.test(name)) {
                  matched.push(...bones);
                  matchedNames.push(name);
                }
              });
              if (matched.length === 0) {
                skippedRoles.push(role as BoneRole);
                continue;
              }
              // Slice 12 (May 15 2026): when a model has duplicate skeletons,
              // share ONE base quaternion across all duplicate bones.
              // bones[0] is the canonical-named bone (the one the mixer is
              // updating); copying its quaternion to siblings keeps them
              // in lockstep for the bone-local-axes gesture path. The
              // world-axes path uses `worldBaseQuats` per-instance instead.
              const sharedBase = matched[0].quaternion.clone();
              // Slice 13 (May 15 2026): also snapshot each bone's WORLD
              // quaternion at gesture start. The world-space-axes path
              // in useFrame uses these to target the desired world quat
              // directly (worldBase * worldOffset → back-derive local),
              // which is robust to the idle clip animating one
              // skeleton's parent chain but not the others.
              const worldBaseQuats: THREE.Quaternion[] = matched.map((b) => {
                b.updateWorldMatrix(true, false);
                const wq = new THREE.Quaternion();
                b.getWorldQuaternion(wq);
                return wq;
              });
              byRole.push({
                role: role as BoneRole,
                bones: matched,
                baseQuats: matched.map(() => sharedBase),
                worldBaseQuats,
                frames: scaledFrames,
              });
            }

            if (skippedRoles.length > 0) {
              // Surface the FULL bone name set when a role is skipped so
              // operators can see exactly what naming convention the rig
              // uses (and we can extend BONE_CANDIDATES accordingly). The
              // cap was 30 in v2.7.x, which truncated before reaching
              // arm/shoulder names on the Xandra-rigged f4-* models —
              // the very models the operator was reporting as broken.
              // 200 covers every model in the current library; if a
              // future model exceeds it we'll still see the head of the
              // list which is the diagnostically useful part.
              const allNames = Array.from(allBoneNames);
              const armLikeNames = allNames.filter((n) =>
                /(shoulder|arm|hand|clavicle|elbow|wrist|forearm)/i.test(n)
              );
              const sampleNames = allNames.slice(0, 200);
              console.debug(
                `🦴 Gesture '${resolvedGesture}' — skipped roles (no matching bone on this rig): ${skippedRoles.join(', ')}; arm-like bones (any case): [${armLikeNames.join(', ')}]; full bones (first 200): ${sampleNames.join(', ')}${allBoneNames.size > 200 ? ` …(+${allBoneNames.size - 200})` : ''}`
              );
            }

            if (byRole.length > 0) {
              const clipDuration = skeletalKf.times[skeletalKf.times.length - 1];
              const durationMs = clipDuration * 1000;
              const totalBones = byRole.reduce((n, r) => n + r.bones.length, 0);
              const axisMode = skeletalKf.worldSpaceAxes ? 'world-axes' : 'bone-local';
              console.debug(
                `🦴 Gesture '${resolvedGesture}' — animating ${byRole.length} role(s) across ${totalBones} bone instance(s) for ${durationMs}ms (${axisMode})`
              );
              skeletalGestureRef.current = {
                gestureType: resolvedGesture,
                startTimeMs: performance.now(),
                durationMs,
                fadeInMs: 250,
                fadeOutMs: 600,
                times: skeletalKf.times,
                worldSpaceAxes: skeletalKf.worldSpaceAxes === true,
                byRole,
              };
              // Skinning-integrity guardrail: snapshot the avatar's skinned
              // mesh bounds now (rest) and again near the gesture hold, then
              // emit a `🦴 … integrity` log + `avatar:gesture:integrity`
              // event reporting whether the meshes stayed stitched together
              // (rotation-invariant seam-separation + spike detection). This
              // is what catches the "head detaches / body comes apart on
              // lean/shrug" class of bind-pose regression that a matched
              // bone count alone never surfaces. Best-effort, never throws.
              if (gltf?.scene) {
                probeGestureIntegrity(
                  gltf.scene,
                  resolvedGesture,
                  Math.min(durationMs * 0.6, durationMs - 100),
                  activeModelConfig?.name,
                );
              }
            } else {
              const rolesInGesture = Object.keys(skeletalKf.bones);
              const allRolesSuppressed =
                rolesInGesture.length > 0 &&
                rolesInGesture.every((role) =>
                  suppressRoles.has(role as BoneRole),
                );
              if (!allRolesSuppressed) {
                console.warn(
                  `🦴 Gesture '${resolvedGesture}' — no bones matched on this rig; falling back to face-only`,
                );
              }
            }
          } catch (e) {
            console.warn(`🦴 Procedural skeletal gesture '${resolvedGesture}' failed, falling back to face-only:`, e);
          }
        }
      }

      const faceIntensity = Math.max(
        0,
        Math.min(
          1.5,
          (activeModelConfig?.gestureFaceIntensityByGesture?.[resolvedGesture] ?? 1)
            * (personalityGesture?.amplitudeScale ?? 1),
        ),
      );
      const disableHeadEuler =
        activeModelConfig?.gestureDisableHeadEuler?.[resolvedGesture] === true;
      const headEulerScale =
        activeModelConfig?.gestureHeadEulerScale?.[resolvedGesture] ?? 1;

      const shrugMorphBoost =
        embeddedOutfitShrug
          ? (activeModelConfig?.gestureShrugMorphBoost ?? 1.75)
          : 1;

      const applyGestureMorphFrame = (frame: GestureFrame) => {
          if (!mesh.morphTargetInfluences) return;
          let appliedMorphCount = 0;
          for (const [name, rawValue] of Object.entries(frame.blendshapes)) {
            if (
              embeddedOutfitShrug &&
              shrugMorphAllowlist &&
              !shrugMorphAllowlist.includes(name)
            ) {
              continue;
            }
            let value = rawValue;
            if (
              lowerFaceMorphScale !== 1 &&
              LOWER_FACE_GESTURE_MORPHS.has(name)
            ) {
              value *= lowerFaceMorphScale;
            }
            if (embeddedOutfitShrug && shrugMorphBoost !== 1) {
              value = Math.min(1, value * shrugMorphBoost);
            }
            const idx = resolveMorphTargetIndex(morphDict, name);
            if (idx !== undefined && mesh.morphTargetInfluences[idx] !== undefined) {
              mesh.morphTargetInfluences[idx] = value;
              if (value > 0.02) appliedMorphCount += 1;
            }
          }
          if (appliedMorphCount === 0 && getGestureController().isGesturePlaying()) {
            console.warn(
              `🦴 Gesture '${resolvedGesture}' — face morphs did not bind on mesh ${mesh.name} (${Object.keys(morphDict).length} keys in dict)`,
            );
          }
          // Keep duplicate M4 morph submeshes in sync during gestures (idle path).
          // Embedded-outfit shrugs skip sync so brow morphs do not shear torso/jacket.
          const m4Meshes = m4MorphMeshesRef.current;
          if (
            !embeddedOutfitShrug &&
            m4Meshes &&
            m4Meshes.length > 1 &&
            mesh === m4Meshes[0]
          ) {
            const primaryInf = mesh.morphTargetInfluences;
            for (let m = 1; m < m4Meshes.length; m++) {
              const otherInf = m4Meshes[m].morphTargetInfluences;
              if (otherInf && otherInf.length === primaryInf.length) {
                for (let i = 0; i < primaryInf.length; i++) {
                  otherInf[i] = primaryInf[i];
                }
              }
            }
          }

          // headRotation is also applied via the skeletal byRole path
          // (nod/shake declare `head` and `neck` roles on
          // GESTURE_BONE_KEYFRAMES with worldSpaceAxes:true). When that
          // path is active, the per-frame quaternion update in
          // useFrame WINS over this Euler write because it runs after
          // rAF in the same frame; but the Euler write here would
          // produce a 1-frame fight for any gesture whose blend-data
          // JSON ALSO sets headRotation (e.g. blendDataGestureNod.json
          // ships ±0.08rad nod offsets). To keep this branch
          // unambiguous and easy to reason about, we skip the Euler
          // head writes whenever a skeletal gesture is active for the
          // SAME gesture type — the skeletal path is the authority.
          const skgActive = skeletalGestureRef.current;
          const skipHeadEulerWrite =
            skgActive !== null &&
            skgActive.gestureType === resolvedGesture &&
            skgActive.byRole.some((r) => {
              // Nod/shake: skeletal head+neck are authoritative.
              if (r.role === 'head' || r.role === 'neck') return true;
              // Lean: torso is skeletal; face JSON still applies headRotation
              // offsets that fight the spine chain → floating-head artefact.
              if (r.role === 'spine' || r.role === 'chest') return true;
              return false;
            });

          if (
            frame.headRotation &&
            headRef.current &&
            !skipHeadEulerWrite &&
            !disableHeadEuler
          ) {
            const s = headEulerScale;
            headRef.current.rotation.x =
              baseHeadRotation.x + frame.headRotation.x * s;
            headRef.current.rotation.y =
              baseHeadRotation.y + frame.headRotation.y * s;
            headRef.current.rotation.z =
              baseHeadRotation.z + frame.headRotation.z * s;
          }
          if (
            frame.headPosition &&
            headRef.current &&
            !skipHeadEulerWrite &&
            !disableHeadEuler
          ) {
            const s = headEulerScale;
            headRef.current.position.x =
              baseHeadPosition.x + frame.headPosition.x * s;
            headRef.current.position.y =
              baseHeadPosition.y + frame.headPosition.y * s;
            headRef.current.position.z =
              baseHeadPosition.z + frame.headPosition.z * s;
          }
      };

      gestureMorphApplyRef.current = applyGestureMorphFrame;
      muteIdleMorphActionsForGesture();

      // Always play the blendshape (face) component
      gestureController.play(
        { type: resolvedGesture, intensity: faceIntensity },
        applyGestureMorphFrame,
        () => {
          restoreIdleMorphActionsAfterGesture();
          if (gestureMorphApplyRef.current === applyGestureMorphFrame) {
            gestureMorphApplyRef.current = null;
          }
        },
      );
    };

    // React to emojis and text patterns in chat content with facial expressions
    const handleChatContent = (event: CustomEvent) => {
      const text = event.detail?.text || event.detail?.message || '';
      if (!text || !gltf?.scene) return;

      import('../../utils/emojiAnimationMapper').then(({ detectEmojiAnimation, playEmojiAnimation }) => {
        const anim = detectEmojiAnimation(text);
        if (!anim) return;

        let targetMesh: THREE.SkinnedMesh | null = null;
        gltf.scene.traverse((node: any) => {
          if (!targetMesh && (node.isSkinnedMesh || node.isMesh) &&
              node.morphTargetDictionary && node.morphTargetInfluences && node.morphTargetInfluences.length > 10) {
            targetMesh = node;
          }
        });
        if (targetMesh && morphTargetDictionaryBody) {
          playEmojiAnimation(anim, targetMesh, morphTargetDictionaryBody);
        }
      });
    };

    window.addEventListener('avatar:config:update', handleConfigUpdate as EventListener);
    window.addEventListener('avatar:mood:preview', handleMoodPreview as EventListener);
    window.addEventListener('avatar:blendshape:set', handleBlendshapeSet as EventListener);
    window.addEventListener('avatar:expression:preview', handleExpressionPreview as EventListener);
    window.addEventListener('avatar:reset', handleReset);
    window.addEventListener('avatar:speak', handleSpeak as unknown as EventListener);
    window.addEventListener('avatar:face:customize', handleFaceCustomize as EventListener);
    window.addEventListener('avatar:sentiment', handleSentiment as EventListener);
    window.addEventListener('avatar:moods:configure', handleMoodsConfigure as EventListener);
    window.addEventListener('avatar:personality:configure', handlePersonalityConfigure as EventListener);
    window.addEventListener('avatar:gesture', handleGesture as EventListener);
    window.addEventListener('avatar:chat:content', handleChatContent as EventListener);

    return () => {
      window.removeEventListener('avatar:config:update', handleConfigUpdate as EventListener);
      window.removeEventListener('avatar:mood:preview', handleMoodPreview as EventListener);
      window.removeEventListener('avatar:blendshape:set', handleBlendshapeSet as EventListener);
      window.removeEventListener('avatar:expression:preview', handleExpressionPreview as EventListener);
      window.removeEventListener('avatar:reset', handleReset);
      if (expressionPreviewOverlay) { expressionPreviewOverlay.stop(); }
      if (expressionRevertTimer) { clearTimeout(expressionRevertTimer); }
      if (moodPreviewRevertTimer) { clearTimeout(moodPreviewRevertTimer); }
      window.removeEventListener('avatar:speak', handleSpeak as unknown as EventListener);
      window.removeEventListener('avatar:face:customize', handleFaceCustomize as EventListener);
      window.removeEventListener('avatar:sentiment', handleSentiment as EventListener);
      window.removeEventListener('avatar:moods:configure', handleMoodsConfigure as EventListener);
      window.removeEventListener('avatar:personality:configure', handlePersonalityConfigure as EventListener);
      window.removeEventListener('avatar:gesture', handleGesture as EventListener);
      window.removeEventListener('avatar:chat:content', handleChatContent as EventListener);
      gestureController.stop();
    };
  }, [gltf, morphTargetDictionaryBody, morphTargetDictionaryLowerTeeth, stopAllAnimations, playIdleAnimation, setAnimateCallSpeak, setAudioSource, bodyMeshName, muteIdleMorphActionsForGesture, restoreIdleMorphActionsAfterGesture]);

  return (
    <>
      {/* @ts-ignore */}
      <primitive object={gltf.scene} dispose={null} position={position} />
    </>
  );
});

export default Avatar;
